/**
 * @file blog-extra-articles.ts
 * @summary Données statiques
 *
 * Source de vérité TS pour des contenus statiques (articles, communes…). Importé depuis pages et composants.
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
// Auto-managed registry of "10 articles SEO denses" — markdown-driven pages.
// Each markdown body lives in src/content/blog/{slug}.md and is imported as raw text.



import type { BlogCluster } from "@/data/blog-articles";
import type { BlogImage } from "@/lib/blog-images";

export interface ExtraArticle {
  slug: string;
  title: string;
  description: string;
  h1: string;
  publishedAt: string;
  readingMinutes: number;
  cluster: BlogCluster;
  ctaVariant: "default" | "douleur" | "reprise" | "competition" | "debutant";
  needsHealthDisclaimer: boolean;
  category: string;
  breadcrumbLabel: string;
  excerpt: string;
  author: { name: string; role: string };
  reviewer?: { name: string; role: string };
  /** Optional per-article image set (overrides shared pool selection). */
  images?: BlogImage[];
}

export const EXTRA_ARTICLES: ExtraArticle[] = [
  {
    slug: "reprendre-le-sport-apres-40-ans-est-lyonnais",
    title: "Reprendre le sport après 40 ans dans l'Est lyonnais",
    description: "Reprendre le sport après 40 ans à Saint-Priest, Mions, Genas, Chaponnay : la méthode honnête, sans douleur ni rechute. Box CrossFit Chistera.",
    h1: "Reprendre le sport après 40 ans dans l'Est lyonnais : par où commencer vraiment",
    publishedAt: "2026-05-20", readingMinutes: 9,
    cluster: "reprise", ctaVariant: "reprise", needsHealthDisclaimer: true,
    category: "Reprise · 40+ ans", breadcrumbLabel: "Reprendre après 40 ans",
    excerpt: "La méthode honnête sans douleur ni rechute pour les cadres actifs de l'Est lyonnais.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" },
  },
  {
    slug: "salle-de-sport-avec-coach-est-lyonnais",
    title: "Salle de sport avec coach dans l'Est lyonnais",
    description: "Salle de sport avec coach à Saint-Priest, Mions, Genas, Chaponnay : ce que coach diplômé change concrètement vs salle libre-service. Box CrossFit Chistera.",
    h1: "Salle de sport avec coach dans l'Est lyonnais : ce que ça change vraiment",
    publishedAt: "2026-05-22", readingMinutes: 8,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Méthode · Coaching", breadcrumbLabel: "Salle avec coach",
    excerpt: "Pourquoi un vrai encadrement change tout : technique, progression, rétention.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-vs-salle-de-musculation",
    title: "CrossFit vs salle de musculation",
    description: "CrossFit ou salle de musculation classique : forces, faiblesses, profil idéal. Comparatif honnête par CrossFit Chistera, box affiliée Saint-Pierre-de-Chandieu.",
    h1: "CrossFit vs salle de musculation : 7 différences pour vraiment progresser",
    publishedAt: "2026-05-24", readingMinutes: 9,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Méthode · Comparatif", breadcrumbLabel: "CrossFit vs musculation",
    excerpt: "7 différences concrètes pour choisir selon votre profil et vos objectifs.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, spécialiste haltérophilie" },
  },
  {
    slug: "debuter-en-crossfit-premiere-seance",
    title: "Débuter en CrossFit : ce qu'on ne dit jamais",
    description: "Première séance de CrossFit à Saint-Pierre-de-Chandieu : à quoi s'attendre, tenue, niveau requis, peurs classiques. Honest guide par CrossFit Chistera.",
    h1: "Débuter en CrossFit : ce que personne ne vous dit avant la première séance",
    publishedAt: "2026-05-27", readingMinutes: 8,
    cluster: "decouverte", ctaVariant: "debutant", needsHealthDisclaimer: false,
    category: "Découverte · Débutant", breadcrumbLabel: "Première séance",
    excerpt: "Tenue, niveau, déroulé et peurs classiques avant le premier WOD.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Perrine", role: "BPJEPS AGFF, CrossFit Level 1" },
  },
  {
    slug: "preparation-hyrox-lyon-est-programme-12-semaines",
    title: "Préparation Hyrox dans l'Est lyonnais",
    description: "Préparer un Hyrox à Lyon Est en 12 semaines : structure programme, mouvements clés, charges, erreurs. Box CrossFit Chistera, à 15 min d'Eurexpo.",
    h1: "Préparation Hyrox dans l'Est lyonnais : programme 12 semaines qui fonctionne",
    publishedAt: "2026-05-30", readingMinutes: 10,
    cluster: "competition", ctaVariant: "competition", needsHealthDisclaimer: false,
    category: "Compétition · Hyrox", breadcrumbLabel: "Préparation Hyrox 12 semaines",
    excerpt: "Plan 12 semaines, mouvements clés, erreurs à éviter pour un premier Hyrox.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, spécialiste cardio/endurance" },
  },
  {
    slug: "sport-en-famille-saint-pierre-de-chandieu",
    title: "Sport en famille à Saint-Pierre-de-Chandieu",
    description: "Sport en famille à Saint-Pierre-de-Chandieu, Mions, Genas : CrossFit Kids, cours parents-enfants, Family Contest. Box Chistera.",
    h1: "Sport en famille à Saint-Pierre-de-Chandieu : trouver l'équilibre parents-enfants",
    publishedAt: "2026-06-03", readingMinutes: 8,
    cluster: "profils", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Profils · Famille", breadcrumbLabel: "Sport en famille",
    excerpt: "CrossFit Kids, cours parents-enfants : comment caler le sport en famille.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Perrine", role: "BPJEPS AGFF, coach Kids" },
  },
  {
    slug: "salle-de-sport-associative-ou-box-crossfit",
    title: "Salle de sport associative ou box CrossFit",
    description: "Salle de sport associative ou box CrossFit affiliée : tarifs, coaching, méthode, communauté. Comparatif honnête dans l'Est lyonnais par CrossFit Chistera.",
    h1: "Salle de sport associative ou box CrossFit affiliée : que choisir vraiment",
    publishedAt: "2026-06-05", readingMinutes: 8,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Méthode · Comparatif", breadcrumbLabel: "Associatif vs box",
    excerpt: "Tarifs, coaching, méthode, communauté : comparatif honnête dans l'Est lyonnais.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, coach gérant" },
  },
  {
    slug: "box-crossfit-proche-mions-saint-priest-genas",
    title: "Box CrossFit proche de Mions, Saint-Priest, Genas",
    description: "Box CrossFit affiliée près de Mions, Saint-Priest, Genas, Chaponnay : panorama des options dans l'Est lyonnais. Distances, tarifs, spécialités. Box Chistera.",
    h1: "Box CrossFit proche de Mions, Saint-Priest, Genas : panorama de l'Est lyonnais",
    publishedAt: "2026-06-10", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Près de chez vous", breadcrumbLabel: "Box Est lyonnais",
    excerpt: "Distances, tarifs, spécialités : panorama des box affiliées de l'Est lyonnais.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, coach gérant" },
  },
  {
    slug: "open-crossfit-2027-preparer-27-1-27-2-27-3",
    title: "Open CrossFit 2027 : préparer 27.1, 27.2 et 27.3 sans erreur",
    description: "Open CrossFit 2027 : format, inscription, préparation 27.1, 27.2, 27.3. Le guide complet par CrossFit Chistera, box affiliée à Saint-Pierre-de-Chandieu.",
    h1: "Open CrossFit 2027 : tout comprendre et bien préparer 27.1, 27.2, 27.3",
    publishedAt: "2027-01-20", readingMinutes: 11,
    cluster: "competition", ctaVariant: "competition", needsHealthDisclaimer: false,
    category: "Compétition · Open", breadcrumbLabel: "Open CrossFit 2027",
    excerpt: "Format, inscription, préparation séance par séance pour ne pas griller l'Open.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, 8 ans de compétition CrossFit" },
  },
  {
    slug: "musculation-douce-apres-40-ans",
    title: "Musculation douce après 40 ans : la fausse bonne idée",
    description: "Musculation douce après 40 ans : pourquoi ça ne renforce pas vraiment, et ce qui marche pour reprendre sans se blesser. CrossFit Chistera.",
    h1: "Musculation douce après 40 ans : la fausse bonne idée et ce qui marche",
    publishedAt: "2026-06-12", readingMinutes: 9,
    cluster: "reprise", ctaVariant: "reprise", needsHealthDisclaimer: true,
    category: "Reprise · 40+ ans", breadcrumbLabel: "Musculation douce 40+",
    excerpt: "Pourquoi la « muscu douce » ne renforce pas vraiment, et ce qui marche après 40 ans.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Orni", role: "Double BPJEPS APT/AF, formation préparation mentale" },
  },
  {
    slug: "tolerance-inconfort-crossfit-wod-mental",
    title: "Tolérance à l'inconfort en CrossFit : guide mental",
    description: "Pourquoi certaines personnes progressent plus vite dans les WODs ? Méthodes concrètes pour développer votre tolérance à l'inconfort en CrossFit : respiration, gestion mentale, pacing, préparation mentale.",
    h1: "Comment développer une vraie tolérance à l'inconfort en CrossFit : le facteur mental qui change tout dans les WODs",
    publishedAt: "2026-05-13", readingMinutes: 10,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Mental · Performance", breadcrumbLabel: "Tolérance à l'inconfort",
    excerpt: "Le facteur mental qui change tout dans les WODs : respiration, pacing, dissociation mentale et exposition intelligente.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Orni", role: "Double BPJEPS APT/AF, formation préparation mentale" },
  },
  {
    slug: "alternatives-exercices-crossfit-pathologies-blessures",
    title: "CrossFit et blessures : alternatives par pathologie",
    description: "Douleurs de dos, genoux, épaules, tendinites ou reprise après blessure : adaptez les exercices de CrossFit intelligemment pour continuer à progresser en toute sécurité.",
    h1: "Les alternatives aux exercices de CrossFit selon les blessures, douleurs et limitations",
    publishedAt: "2026-05-13", readingMinutes: 12,
    cluster: "reprise", ctaVariant: "douleur", needsHealthDisclaimer: true,
    category: "Santé · Adaptations", breadcrumbLabel: "Alternatives & pathologies",
    excerpt: "Mouvement par mouvement, les vraies alternatives pour pratiquer le CrossFit avec mal de dos, genoux, épaules, arthrose ou en reprise.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" },
  },
  {
    slug: "crossfit-definition-faq-complete",
    title: "Qu'est-ce que le CrossFit ? Guide complet, FAQ",
    description: "Découvrez ce qu'est réellement le CrossFit, comment se déroule une séance, les résultats, les prix, les bienfaits, les adaptations selon votre niveau et les réponses aux questions fréquentes.",
    h1: "Le CrossFit expliqué simplement : définition, bienfaits, prix, résultats, sécurité et questions fréquentes",
    publishedAt: "2026-05-13", readingMinutes: 11,
    cluster: "methode", ctaVariant: "debutant", needsHealthDisclaimer: false,
    category: "Méthode · Guide complet", breadcrumbLabel: "CrossFit : guide complet",
    excerpt: "Définition, déroulé d'une séance, bienfaits, prix, résultats, sécurité : tout pour comprendre vraiment le CrossFit.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-science-preparation-physique-sante-performance",
    title: "CrossFit et science : préparation physique",
    description: "Que dit la science sur le CrossFit ? Revue critique des études sur la préparation physique fonctionnelle, l'intensité, la santé, les blessures, le sport après 35 ans et l'encadrement.",
    h1: "CrossFit et science : que disent vraiment les études sur la préparation physique fonctionnelle ?",
    publishedAt: "2026-05-13", readingMinutes: 14,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Méthode · Science", breadcrumbLabel: "CrossFit & science",
    excerpt: "Revue critique des études sur le HIFT, la santé, les blessures et l'encadrement : ce que la science valide vraiment dans la méthode CrossFit.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" },
  },
  {
    slug: "debuter-crossfit-sans-etre-sportif",
    title: "Débuter le CrossFit sans être sportif — Chistera",
    description: "Peut-on commencer le CrossFit sans expérience sportive ? Méthode honnête pour démarrer dans l'Est lyonnais avec CrossFit Chistera.",
    h1: "Débuter le CrossFit sans être sportif : ce qu'il faut vraiment savoir avant de s'inscrire",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "decouverte", ctaVariant: "debutant", needsHealthDisclaimer: false,
    category: "Découverte · Débutant", breadcrumbLabel: "Débuter sans être sportif",
    excerpt: "Pas besoin d'être sportif pour démarrer : la méthode honnête pour les vrais débutants.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Perrine", role: "BPJEPS AGFF, CrossFit Level 1" },
  },
  {
    slug: "crossfit-perte-de-poids",
    title: "CrossFit et perte de poids : ce qui marche vraiment",
    description: "Le CrossFit fait-il vraiment perdre du poids ? Ce qui fonctionne réellement pour maigrir durablement, par les coachs CrossFit Chistera.",
    h1: "CrossFit et perte de poids : ce qui marche vraiment, ce qui ne marche pas",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Méthode · Composition corporelle", breadcrumbLabel: "CrossFit & perte de poids",
    excerpt: "Recomposition corporelle, métabolisme, alimentation : ce qui produit des résultats durables.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" },
  },
  {
    slug: "chaussure-crossfit-guide",
    title: "Chaussures CrossFit : guide complet 2026",
    description: "Nike Metcon, Reebok Nano, TYR CXT, RAD One : guide complet 2026 pour choisir vos chaussures de CrossFit selon votre profil.",
    h1: "Quelles chaussures choisir pour le CrossFit ? Le guide complet 2026",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "outil", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Outil · Équipement", breadcrumbLabel: "Chaussures CrossFit 2026",
    excerpt: "Stabilité, drop, polyvalence : tous les critères pour choisir la bonne paire en 2026.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparer-premier-hyrox-lyon",
    title: "Préparer son premier Hyrox dans la région lyonnaise",
    description: "Format Hyrox, erreurs à éviter, pacing : guide complet pour préparer votre premier Hyrox dans l'Est lyonnais avec CrossFit Chistera.",
    h1: "Préparer son premier Hyrox dans la région lyonnaise : ce qu'il faut savoir",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "competition", ctaVariant: "competition", needsHealthDisclaimer: false,
    category: "Compétition · Hyrox", breadcrumbLabel: "Premier Hyrox",
    excerpt: "Format, ateliers, erreurs classiques de pacing : tout pour réussir un premier Hyrox.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, spécialiste cardio/endurance" },
  },
  {
    slug: "drop-in-crossfit-lyon-est",
    title: "Drop-in CrossFit dans l'Est lyonnais",
    description: "Drop-in CrossFit dans l'Est lyonnais : tarifs, réservation, déroulé. Box CrossFit Chistera à 12 min de l'aéroport Lyon-Saint-Exupéry.",
    h1: "Drop-in CrossFit dans l'Est lyonnais : où s'entraîner pendant un déplacement",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Près de chez vous · Drop-in", breadcrumbLabel: "Drop-in Lyon Est",
    excerpt: "Drop-in à 12 min de l'aéroport et 15 min d'Eurexpo : modalités, tarifs, réservation.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, coach gérant" },
  },
  {
    slug: "crossfit-saint-priest",
    title: "CrossFit Saint-Priest | Salle de sport",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Saint-Priest ? Découvrez CrossFit Chistera : coaching, Hyrox, sport santé, remise en forme et ambiance familiale.",
    h1: "Faire du sport à Saint-Priest : pourquoi de plus en plus de sportifs rejoignent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Saint-Priest", breadcrumbLabel: "CrossFit Saint-Priest",
    excerpt: "À 9–11 min de Saint-Priest : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, coach gérant" },
  },
  {
    slug: "crossfit-mions-est-lyonnais",
    title: "Salle de CrossFit proche Mions",
    description: "Box CrossFit affiliée à 10 min de Mions : encadrement diplômé, communauté, programmation pensée. CrossFit Chistera, Saint-Pierre-de-Chandieu.",
    h1: "Salle de CrossFit proche de Mions : sport, communauté et progression",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Près de chez vous · Mions", breadcrumbLabel: "CrossFit Mions",
    excerpt: "À 10 min du centre de Mions : box à taille humaine, coachs diplômés, mixité réelle des profils.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, coach gérant" },
  },
  {
    slug: "crossfit-lyon-est-preparation-physique",
    title: "CrossFit Lyon Est : préparation physique et sport santé",
    description: "Préparation physique générale pour coureurs, cyclistes, sport co' et sport santé dans l'Est lyonnais. Box CrossFit Chistera affiliée.",
    h1: "CrossFit dans l'Est lyonnais : préparation physique et sport santé",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Méthode · Préparation physique", breadcrumbLabel: "Préparation physique Lyon Est",
    excerpt: "Pourquoi le CrossFit complète intelligemment course, vélo, sports co' et sport santé.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" },
  },
  {
    slug: "crossfit-apres-35-ans-renforcement",
    title: "Pourquoi le CrossFit est idéal après 35 ans",
    description: "Sarcopénie, sédentarité, récupération : pourquoi le CrossFit bien encadré devient un outil santé majeur après 35 ans. CrossFit Chistera.",
    h1: "Pourquoi le CrossFit est idéal après 35 ans",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "profils", ctaVariant: "reprise", needsHealthDisclaimer: true,
    category: "Profils · 35+ ans", breadcrumbLabel: "CrossFit après 35 ans",
    excerpt: "Force, mobilité, métabolisme : pourquoi cette méthode devient prioritaire après 35 ans.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" },
  },
  {
    slug: "crossfit-blessures-adaptations-intelligentes",
    title: "CrossFit et blessures : adapter intelligemment les exercices",
    description: "Mal de dos, douleurs de genoux, épaules fragiles, reprise après blessure : comment adapter le CrossFit intelligemment. CrossFit Chistera, Est lyonnais.",
    h1: "CrossFit et blessures : comment adapter intelligemment les exercices selon vos douleurs",
    publishedAt: "2026-05-14", readingMinutes: 13,
    cluster: "reprise", ctaVariant: "douleur", needsHealthDisclaimer: true,
    category: "Santé · Adaptations", breadcrumbLabel: "Blessures & adaptations intelligentes",
    excerpt: "Le scaling expliqué concrètement : dos, genoux, épaules, hanches, anciennes blessures.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" },
  },
  {
    slug: "chaussures-crossfit-2026-guide-choisir",
    title: "Chaussures CrossFit en 2026",
    description: "Quelle chaussure pour le CrossFit en 2026 : critères techniques, top 6 modèles testés, prix réels, erreurs à éviter. Guide CrossFit Chistera, box affiliée Lyon Est.",
    h1: "Chaussures CrossFit en 2026 : le guide pour vraiment bien choisir",
    publishedAt: "2026-05-15", readingMinutes: 11,
    cluster: "outil", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Équipement · Chaussures", breadcrumbLabel: "Chaussures CrossFit 2026",
    excerpt: "Drop, semelle, renfort latéral : critères techniques et top 6 modèles 2026.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1, spécialiste haltérophilie" },
  },
  {
    slug: "accessoires-crossfit-indispensables",
    title: "Accessoires CrossFit : ce qu'il faut vraiment",
    description: "Quels accessoires CrossFit acheter en priorité (et lesquels éviter) : magnésie, ceinture, chaussettes, sangles, genouillères. Guide CrossFit Chistera, Lyon Est.",
    h1: "Accessoires CrossFit indispensables : ce qu'il faut vraiment, ce qui ne sert à rien",
    publishedAt: "2026-05-29", readingMinutes: 9,
    cluster: "outil", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Équipement · Accessoires", breadcrumbLabel: "Accessoires CrossFit",
    excerpt: "Le minimum vital, l'utile à 6 mois, le matériel compétiteur, le marketing inutile.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparer-premiere-competition-crossfit-hyrox",
    title: "Préparer sa 1ère compétition CrossFit ou Hyrox",
    description: "Préparer sa première compétition CrossFit ou Hyrox : 12 semaines de prépa, choix de l'épreuve, erreurs classiques. Guide CrossFit Chistera, box affiliée Lyon Est.",
    h1: "Préparer sa première compétition CrossFit ou Hyrox sans se planter",
    publishedAt: "2026-06-19", readingMinutes: 11,
    cluster: "competition", ctaVariant: "competition", needsHealthDisclaimer: false,
    category: "Compétition · Première", breadcrumbLabel: "Première compétition",
    excerpt: "Choix du format, plan 12 semaines, erreurs classiques de pacing et nutrition.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1, 8 ans de compétition" },
  },
  {
    slug: "combien-coute-le-crossfit-france-2026",
    title: "Combien coûte le CrossFit en France en 2026",
    description: "Combien coûte le CrossFit en France en 2026 : tarifs détaillés box affiliée, accessoires, alimentation, coaching individuel. Guide CrossFit Chistera, box Lyon Est.",
    h1: "Combien coûte le CrossFit en France en 2026 ? Les vrais prix sans baratin",
    publishedAt: "2026-05-22", readingMinutes: 8,
    cluster: "outil", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Pratique · Tarifs", breadcrumbLabel: "Prix CrossFit 2026",
    excerpt: "Abonnement, frais d'inscription, équipement, coaching : le budget annuel réaliste.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "combien-de-seances-crossfit-par-semaine",
    title: "Combien de séances de CrossFit par semaine selon vos objectifs",
    description: "Combien de séances de CrossFit par semaine ? 2, 3, 4 ou 5 séances : la fréquence optimale selon votre objectif et votre profil. Guide CrossFit Chistera, Lyon Est.",
    h1: "Combien de séances de CrossFit par semaine selon vos objectifs",
    publishedAt: "2026-05-25", readingMinutes: 7,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Pratique · Fréquence", breadcrumbLabel: "Séances par semaine",
    excerpt: "Récupération, progression, surentraînement : la fréquence idéale selon 5 profils.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Orni", role: "Double BPJEPS APT/AF, formation préparation mentale" },
  },
  {
    slug: "duree-seance-crossfit-comment-organisee",
    title: "Combien de temps dure une séance de CrossFit",
    description: "Durée d'une séance de CrossFit : 60 minutes. Décomposition par bloc : échauffement, technique, WOD, récupération. Guide CrossFit Chistera, Saint-Pierre-de-Chandieu.",
    h1: "Combien de temps dure une séance de CrossFit et comment elle s'organise",
    publishedAt: "2026-06-26", readingMinutes: 7,
    cluster: "decouverte", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Découverte · Déroulé", breadcrumbLabel: "Durée d'une séance",
    excerpt: "60 minutes décomposées : échauffement, skill, WOD, retour au calme.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Perrine", role: "BPJEPS AGFF, CrossFit Level 1" },
  },
  {
    slug: "cross-training-vs-crossfit-differences",
    title: "Cross training vs CrossFit",
    description: "Cross training et CrossFit : confusion fréquente, mêmes mouvements mais différences importantes en méthode, coaching, communauté. Guide CrossFit Chistera, Lyon Est.",
    h1: "Cross training ou CrossFit : les différences réelles qu'il faut comprendre",
    publishedAt: "2026-06-05", readingMinutes: 8,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Méthode · Comparatif", breadcrumbLabel: "Cross training vs CrossFit",
    excerpt: "Affiliation, méthodologie, coaching, communauté : ce qui sépare réellement les deux.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" },
  },
  {
    slug: "wod-amrap-emom-lexique-crossfit",
    title: "WOD, AMRAP, EMOM : le lexique CrossFit à connaître",
    description: "Lexique CrossFit complet : WOD, AMRAP, EMOM, RX, scaled, PR, benchmark. Définitions claires et exemples concrets. Guide CrossFit Chistera, box affiliée Lyon Est.",
    h1: "WOD, AMRAP, EMOM : le lexique CrossFit complet pour ne plus rien rater",
    publishedAt: "2026-07-10", readingMinutes: 8,
    cluster: "outil", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Découverte · Lexique", breadcrumbLabel: "Lexique WOD AMRAP EMOM",
    excerpt: "Tous les termes CrossFit expliqués clairement avec exemples concrets.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1" },
  },
  {
    slug: "crossfit-est-lyonnais",
    title: "CrossFit dans l'Est lyonnais",
    description: "Hub local CrossFit Est lyonnais : Saint-Priest, Mions, Chaponnay, Corbas, Heyrieux, Genas, Bron, Vénissieux, nord-Isère. Activités, profils, tarifs, FAQ.",
    h1: "CrossFit dans l'Est lyonnais : la box, les activités, les villes desservies",
    publishedAt: "2026-05-14", readingMinutes: 16,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Hub local · Est lyonnais", breadcrumbLabel: "CrossFit Est lyonnais",
    excerpt: "Page hub : activités, villes desservies, profils, tarifs, équipe et FAQ pour l'Est lyonnais.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" },
  },
  {
    slug: "repertoire-est-lyonnais",
    title: "Sport & CrossFit Est lyonnais : le guide complet — Chistera",
    description: "Guide complet du CrossFit, Hyrox, hybrid training et sport santé dans l'Est lyonnais. Villes desservies, disciplines, conseils pratiques par CrossFit Chistera.",
    h1: "Le répertoire du sport dans l'Est lyonnais : CrossFit, Hyrox, hybrid training et sport santé",
    publishedAt: "2026-05-14", readingMinutes: 18,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Hub local · Est lyonnais", breadcrumbLabel: "Répertoire Est lyonnais",
    excerpt: "Hub complet : disciplines, villes, profils et ressources de l'Est lyonnais.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-est-lyonnais-guide",
    title: "CrossFit Est lyonnais : box, méthode, prix — Chistera",
    description: "Pratiquer le CrossFit dans l'Est lyonnais : méthode, profils accueillis, coaching, tarifs. Présentation de l'approche CrossFit Chistera à Saint-Pierre-de-Chandieu.",
    h1: "Le CrossFit dans l'Est lyonnais : comprendre la méthode et bien choisir sa box",
    publishedAt: "2026-05-14", readingMinutes: 12,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Lieu · CrossFit Est lyonnais", breadcrumbLabel: "CrossFit Est lyonnais",
    excerpt: "Box, communes desservies, profils, méthode CrossFit dans l'Est lyonnais.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-est-lyonnais",
    title: "Salle de sport Est lyonnais : guide complet — Chistera",
    description: "Bien choisir sa salle de sport dans l'Est lyonnais : critères, types de salles, coaching, tarifs. Comparatif des approches et conseils pratiques par CrossFit Chistera.",
    h1: "Choisir sa salle de sport dans l'Est lyonnais : critères, types et conseils",
    publishedAt: "2026-05-14", readingMinutes: 11,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Lieu · Salle de sport", breadcrumbLabel: "Salle de sport Est lyonnais",
    excerpt: "Choisir sa salle de sport dans l'Est lyonnais : critères, types, profils.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-est-lyonnais",
    title: "Salle de fitness Est lyonnais",
    description: "Salle de fitness dans l'Est lyonnais : différences avec une box CrossFit, types d'abonnements, conseils pour bien démarrer. Guide CrossFit Chistera.",
    h1: "Salle de fitness dans l'Est lyonnais : comprendre les différences avant de s'inscrire",
    publishedAt: "2026-05-14", readingMinutes: 11,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Lieu · Fitness", breadcrumbLabel: "Salle de fitness",
    excerpt: "Salle de fitness vs box CrossFit dans l'Est lyonnais : ce que cela change.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-est-lyonnais",
    title: "CrossFit débutant Est lyonnais : par où commencer — Chistera",
    description: "Commencer le CrossFit dans l'Est lyonnais quand on est débutant : déroulé d'une première séance, adaptations, conseils pratiques. Guide CrossFit Chistera.",
    h1: "Commencer le CrossFit quand on n'a jamais pratiqué : guide pour les débutants de l'Est lyonnais",
    publishedAt: "2026-05-14", readingMinutes: 11,
    cluster: "decouverte", ctaVariant: "debutant", needsHealthDisclaimer: false,
    category: "Débutant · Est lyonnais", breadcrumbLabel: "CrossFit débutant",
    excerpt: "Débuter le CrossFit dans l'Est lyonnais : déroulé, niveau, déroulement.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-est-lyonnais",
    title: "CrossFit enfants Est lyonnais : programme adapté — Chistera",
    description: "CrossFit pour enfants et adolescents dans l'Est lyonnais : philosophie, sécurité, groupes d'âge, ce que les parents doivent savoir. Approche CrossFit Chistera.",
    h1: "Le CrossFit pour enfants et adolescents dans l'Est lyonnais : ce qu'il faut savoir",
    publishedAt: "2026-05-14", readingMinutes: 12,
    cluster: "profils", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Profils · Enfants", breadcrumbLabel: "CrossFit enfants",
    excerpt: "CrossFit Kids dans l'Est lyonnais : encadrement, âge, bénéfices.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-est-lyonnais",
    title: "CrossFit senior Est lyonnais : pratique adaptée — Chistera",
    description: "CrossFit pour seniors dans l'Est lyonnais : adaptations, bénéfices santé, sécurité, témoignages. L'approche CrossFit Chistera pour les plus de 60 ans.",
    h1: "Le CrossFit après 60 ans dans l'Est lyonnais : pourquoi et comment",
    publishedAt: "2026-05-14", readingMinutes: 11,
    cluster: "profils", ctaVariant: "reprise", needsHealthDisclaimer: true,
    category: "Profils · Seniors", breadcrumbLabel: "CrossFit senior",
    excerpt: "CrossFit après 60 ans dans l'Est lyonnais : adaptation, santé, autonomie.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-est-lyonnais",
    title: "Sport santé Est lyonnais : APA encadrée — Chistera",
    description: "Sport santé dans l'Est lyonnais : activité physique adaptée, lien avec les professionnels de santé, programmes pour pathologies chroniques. Approche CrossFit Chistera.",
    h1: "Le sport santé dans l'Est lyonnais : une activité physique adaptée, encadrée et durable",
    publishedAt: "2026-05-14", readingMinutes: 12,
    cluster: "reprise", ctaVariant: "reprise", needsHealthDisclaimer: true,
    category: "Sport santé · Est lyonnais", breadcrumbLabel: "Sport santé",
    excerpt: "Sport santé dans l'Est lyonnais : reprendre sans douleur ni rechute.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-est-lyonnais",
    title: "Callisthénie Est lyonnais : poids du corps — Chistera",
    description: "La callisthénie dans l'Est lyonnais : principes, progressions, complémentarité avec le CrossFit. Guide pratique CrossFit Chistera à Saint-Pierre-de-Chandieu.",
    h1: "La callisthénie dans l'Est lyonnais : comprendre, démarrer, progresser",
    publishedAt: "2026-05-14", readingMinutes: 11,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Méthode · Callisthénie", breadcrumbLabel: "Callisthénie",
    excerpt: "Callisthénie dans l'Est lyonnais : poids du corps, gymnastique, progression.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-est-lyonnais",
    title: "Hybrid training Est lyonnais",
    description: "L'hybrid training dans l'Est lyonnais : combiner force et endurance, organisation de la semaine, programme type. Approche pratique par CrossFit Chistera.",
    h1: "Hybrid training dans l'Est lyonnais : combiner force et endurance, intelligemment",
    publishedAt: "2026-05-14", readingMinutes: 12,
    cluster: "methode", ctaVariant: "competition", needsHealthDisclaimer: false,
    category: "Méthode · Hybrid Training", breadcrumbLabel: "Hybrid Training",
    excerpt: "Hybrid training dans l'Est lyonnais : force + endurance pour Hyrox & co.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-est-lyonnais",
    title: "Pilates Est lyonnais : méthode et complémentarité — Chistera",
    description: "Le pilates dans l'Est lyonnais : principes, bénéfices, complémentarité avec le CrossFit et la préparation physique. Approche CrossFit Chistera.",
    h1: "Le pilates dans l'Est lyonnais : ce que la méthode apporte vraiment",
    publishedAt: "2026-05-14", readingMinutes: 12,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Méthode · Pilates", breadcrumbLabel: "Pilates",
    excerpt: "Pilates dans l'Est lyonnais : posture, gainage, complément du CrossFit.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-est-lyonnais",
    title: "Crosstraining Est lyonnais",
    description: "Crosstraining dans l'Est lyonnais : différence avec le CrossFit, méthode fonctionnelle, bénéfices. Guide pratique par CrossFit Chistera.",
    h1: "Le crosstraining dans l'Est lyonnais : comprendre, pratiquer, progresser",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Méthode · Crosstraining", breadcrumbLabel: "Crosstraining",
    excerpt: "Crosstraining dans l'Est lyonnais : différences avec le CrossFit affilié.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-est-lyonnais",
    title: "ATHX Est lyonnais : conditioning structuré — Chistera",
    description: "L'ATHX dans l'Est lyonnais : format de cours, public, complémentarité avec le CrossFit. Présentation par CrossFit Chistera à Saint-Pierre-de-Chandieu.",
    h1: "ATHX dans l'Est lyonnais : un format de conditioning accessible et structuré",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "competition", ctaVariant: "competition", needsHealthDisclaimer: false,
    category: "Compétition · ATHX", breadcrumbLabel: "ATHX",
    excerpt: "ATHX dans l'Est lyonnais : format, ateliers, préparation.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-est-lyonnais",
    title: "Hyrox Est lyonnais : préparation et coaching — Chistera",
    description: "Préparer une course Hyrox dans l'Est lyonnais : format, stations, programme d'entraînement, coaching dédié. Guide complet par CrossFit Chistera.",
    h1: "Hyrox dans l'Est lyonnais : comprendre le format, préparer la course",
    publishedAt: "2026-05-14", readingMinutes: 11,
    cluster: "competition", ctaVariant: "competition", needsHealthDisclaimer: false,
    category: "Compétition · Hyrox", breadcrumbLabel: "Hyrox",
    excerpt: "Hyrox dans l'Est lyonnais : format, préparation et erreurs classiques.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-est-lyonnais",
    title: "Préparation physique Est lyonnais",
    description: "Préparation physique dans l'Est lyonnais pour sportifs amateurs et confirmés : méthode, sports concernés, approche individualisée. Guide CrossFit Chistera.",
    h1: "La préparation physique dans l'Est lyonnais : développer la performance, prévenir les blessures",
    publishedAt: "2026-05-14", readingMinutes: 12,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Méthode · Préparation physique", breadcrumbLabel: "Préparation physique",
    excerpt: "Préparation physique dans l'Est lyonnais : coureurs, cyclistes, sport co.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-est-lyonnais",
    title: "Remise en forme Est lyonnais : reprendre le sport — Chistera",
    description: "Se remettre en forme dans l'Est lyonnais après une longue pause : méthode progressive, choix d'activité, conseils pratiques. Guide CrossFit Chistera.",
    h1: "Remise en forme dans l'Est lyonnais : reprendre le sport sans se décourager",
    publishedAt: "2026-05-14", readingMinutes: 11,
    cluster: "reprise", ctaVariant: "reprise", needsHealthDisclaimer: true,
    category: "Remise en forme · Est lyonnais", breadcrumbLabel: "Remise en forme",
    excerpt: "Remise en forme durable dans l'Est lyonnais : la méthode CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-est-lyonnais",
    title: "Perte de poids Est lyonnais : sport et méthode — Chistera",
    description: "Perte de poids durable dans l'Est lyonnais : rôle réel du sport, lien avec la nutrition, méthode progressive. Guide honnête par CrossFit Chistera.",
    h1: "Perte de poids dans l'Est lyonnais : ce que le sport peut vraiment et ce qu'il ne peut pas",
    publishedAt: "2026-05-14", readingMinutes: 11,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Perte de poids · Est lyonnais", breadcrumbLabel: "Perte de poids",
    excerpt: "Perte de poids dans l'Est lyonnais : recomposition corporelle, alimentation.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-lyon",
    title: "CrossFit Lyon | Salle de sport Est lyonnais",
    description: "Vous cherchez une salle de CrossFit à Lyon ou dans l'Est lyonnais ? Découvrez CrossFit Chistera à Saint-Pierre-de-Chandieu : coaching, fitness, sport santé, Hyrox et ambiance familiale.",
    h1: "Faire du sport à Lyon : pourquoi de plus en plus de Lyonnais viennent s'entraîner chez CrossFit Chistera dans l'Est lyonnais",
    publishedAt: "2026-05-14", readingMinutes: 12,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit Lyon · Est lyonnais", breadcrumbLabel: "CrossFit Lyon",
    excerpt: "Salle de CrossFit accessible depuis Lyon : coaching, Hybrid Training, Hyrox, sport santé et ambiance familiale à Saint-Pierre-de-Chandieu.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-est-lyonnais-pourquoi-chistera",
    title: "CrossFit Est lyonnais | Salle de sport et fitness",
    description: "Découvrez CrossFit Chistera dans l'Est lyonnais : CrossFit, Hyrox, sport santé, fitness, remise en forme et coaching personnalisé à Saint-Pierre-de-Chandieu.",
    h1: "CrossFit dans l'Est lyonnais : pourquoi CrossFit Chistera attire des sportifs de tout le secteur lyonnais",
    publishedAt: "2026-05-14", readingMinutes: 11,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Est lyonnais", breadcrumbLabel: "CrossFit Est lyonnais",
    excerpt: "Box CrossFit centrale dans l'Est lyonnais : communes desservies, formats, sport santé, ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-corbas",
    title: "CrossFit Corbas : box voisine à 5-10 min — Chistera",
    description: "Pratiquer le CrossFit à Corbas : panorama local, choisir sa box, alternative voisine à Saint-Pierre-de-Chandieu. Guide CrossFit Chistera.",
    h1: "Le CrossFit à Corbas : panorama local et option de proximité immédiate",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Corbas", breadcrumbLabel: "crossfit-corbas",
    excerpt: "Pratiquer le CrossFit à Corbas : panorama local, choisir sa box, alternative voisine à Saint-Pierre-de-Chandieu. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-mions",
    title: "CrossFit Mions | Salle de sport et fitness proche",
    description: "Vous cherchez une salle de sport ou de CrossFit proche de Mions ? Découvrez CrossFit Chistera à Saint-Pierre-de-Chandieu : CrossFit, Hyrox, fitness et sport santé.",
    h1: "Faire du sport à Mions : pourquoi de plus en plus d'habitants choisissent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Mions", breadcrumbLabel: "CrossFit Mions",
    excerpt: "À 6–8 min de Mions : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale à Saint-Pierre-de-Chandieu.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-chaponnay",
    title: "CrossFit Chaponnay : box voisine à 5 min — Chistera",
    description: "Pratiquer le CrossFit à Chaponnay : box Chistera à Saint-Pierre-de-Chandieu, coaching personnalisé, ambiance familiale. Guide pratique.",
    h1: "Le CrossFit pour les Chaponnois : une box affiliée à cinq minutes",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · CrossFit", breadcrumbLabel: "CrossFit Chaponnay",
    excerpt: "Pratiquer le CrossFit à Chaponnay : box Chistera à Saint-Pierre-de-Chandieu, coaching personnalisé, ambiance familiale. Guide pratique.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-toussieu",
    title: "CrossFit Toussieu | Salle de sport, fitness et Hyrox proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Toussieu ? Découvrez CrossFit Chistera : coaching, sport santé, Hyrox, remise en forme et ambiance familiale.",
    h1: "Faire du sport à Toussieu : pourquoi de plus en plus d'habitants rejoignent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Toussieu", breadcrumbLabel: "CrossFit Toussieu",
    excerpt: "À 4–6 min de Toussieu : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale à Saint-Pierre-de-Chandieu.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-saint-bonnet-de-mure",
    title: "CrossFit Saint-Bonnet-de-Mure : box à 8 min — Chistera",
    description: "Pratiquer le CrossFit à Saint-Bonnet-de-Mure : box voisine immédiate à Saint-Pierre-de-Chandieu. Coaching personnalisé, ambiance familiale.",
    h1: "Le CrossFit pour les habitants de Saint-Bonnet-de-Mure : une box affiliée à proximité immédiate",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · CrossFit", breadcrumbLabel: "CrossFit Saint-Bonnet-de-Mure",
    excerpt: "Pratiquer le CrossFit à Saint-Bonnet-de-Mure : box voisine immédiate à Saint-Pierre-de-Chandieu. Coaching personnalisé, ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-saint-laurent-de-mure",
    title: "CrossFit Saint-Laurent-de-Mure : box à 10 min — Chistera",
    description: "Pratiquer le CrossFit à Saint-Laurent-de-Mure : box voisine à Saint-Pierre-de-Chandieu, coaching personnalisé. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour les Saint-Laurentins : box affiliée en proximité",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · CrossFit", breadcrumbLabel: "CrossFit Saint-Laurent-de-Mure",
    excerpt: "Pratiquer le CrossFit à Saint-Laurent-de-Mure : box voisine à Saint-Pierre-de-Chandieu, coaching personnalisé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-venissieux",
    title: "CrossFit Vénissieux : panorama et options — Chistera",
    description: "Pratiquer le CrossFit à Vénissieux : panorama des box locales et alternatives en proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit à Vénissieux : panorama d'une ville à forte offre sportive",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Vénissieux", breadcrumbLabel: "CrossFit Vénissieux",
    excerpt: "Pratiquer le CrossFit à Vénissieux : panorama des box locales et alternatives en proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-heyrieux",
    title: "CrossFit Heyrieux : box voisine à 10 min — Chistera",
    description: "Pratiquer le CrossFit à Heyrieux : box voisine à Saint-Pierre-de-Chandieu. Coaching personnalisé, ambiance familiale. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour les habitants de Heyrieux : box affiliée à proximité",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Heyrieux", breadcrumbLabel: "CrossFit Heyrieux",
    excerpt: "Pratiquer le CrossFit à Heyrieux : box voisine à Saint-Pierre-de-Chandieu. Coaching personnalisé, ambiance familiale. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-genas",
    title: "CrossFit Genas | Salle de sport et fitness proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Genas ? Découvrez CrossFit Chistera : sport santé, Hyrox, CrossFit et coaching personnalisé dans l'Est lyonnais.",
    h1: "Faire du sport à Genas : pourquoi de plus en plus d'habitants viennent chez CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Genas", breadcrumbLabel: "CrossFit Genas",
    excerpt: "À 12–14 min de Genas : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-bron",
    title: "CrossFit Bron | Salle de sport et fitness proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Bron ? Découvrez CrossFit Chistera : sport santé, Hyrox, CrossFit et coaching personnalisé dans l'Est lyonnais.",
    h1: "Faire du sport à Bron : pourquoi de plus en plus d'habitants choisissent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Bron", breadcrumbLabel: "CrossFit Bron",
    excerpt: "À 16–18 min de Bron : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-chassieu",
    title: "CrossFit Chassieu | Salle de sport et fitness proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Chassieu ? Découvrez CrossFit Chistera : sport santé, Hyrox, CrossFit et coaching personnalisé dans l'Est lyonnais.",
    h1: "Faire du sport à Chassieu : pourquoi de plus en plus d'habitants choisissent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Chassieu", breadcrumbLabel: "CrossFit Chassieu",
    excerpt: "À 14–16 min de Chassieu : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-saint-quentin-fallavier",
    title: "CrossFit Saint-Quentin-Fallavier : options — Chistera",
    description: "Pratiquer le CrossFit à Saint-Quentin-Fallavier : panorama local, box voisine à Saint-Pierre-de-Chandieu. Guide CrossFit Chistera.",
    h1: "Le CrossFit à Saint-Quentin-Fallavier : panorama et options",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Saint-Quentin-Fallavier", breadcrumbLabel: "CrossFit Saint-Quentin-Fallavier",
    excerpt: "Pratiquer le CrossFit à Saint-Quentin-Fallavier : panorama local, box voisine à Saint-Pierre-de-Chandieu. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-villefontaine",
    title: "CrossFit Villefontaine | Salle de sport, fitness et Hyrox",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Villefontaine ? Découvrez CrossFit Chistera : coaching, sport santé, Hyrox et remise en forme.",
    h1: "Faire du sport à Villefontaine : pourquoi certains sportifs préfèrent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Villefontaine", breadcrumbLabel: "CrossFit Villefontaine",
    excerpt: "À 18–20 min de Villefontaine : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-vienne",
    title: "CrossFit Vienne | Salle de sport et préparation physique",
    description: "Vous cherchez une salle de CrossFit ou de préparation physique proche de Vienne ? Découvrez CrossFit Chistera : coaching, Hyrox, sport santé, remise en forme et fitness.",
    h1: "Faire du sport à Vienne : pourquoi certains sportifs préfèrent désormais s'entraîner chez CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Vienne", breadcrumbLabel: "CrossFit Vienne",
    excerpt: "À 20–22 min de Vienne : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-meyzieu",
    title: "CrossFit Meyzieu | Salle de sport et fitness proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Meyzieu ? Découvrez CrossFit Chistera : sport santé, Hyrox, remise en forme et coaching personnalisé dans l'Est lyonnais.",
    h1: "Faire du sport à Meyzieu : pourquoi certains sportifs quittent les salles classiques pour CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Meyzieu", breadcrumbLabel: "CrossFit Meyzieu",
    excerpt: "À 17–19 min de Meyzieu : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-villeurbanne",
    title: "CrossFit Villeurbanne | Salle de sport et fitness proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Villeurbanne ? Découvrez CrossFit Chistera : sport santé, Hyrox, remise en forme et coaching personnalisé.",
    h1: "Faire du sport à Villeurbanne : pourquoi certains sportifs recherchent aujourd'hui une alternative plus humaine chez CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Villeurbanne", breadcrumbLabel: "CrossFit Villeurbanne",
    excerpt: "À 22–25 min de Villeurbanne : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-decines-charpieu",
    title: "CrossFit Décines-Charpieu | Salle de sport et fitness proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Décines-Charpieu ? Découvrez CrossFit Chistera : sport santé, Hyrox, remise en forme et coaching personnalisé.",
    h1: "Faire du sport à Décines-Charpieu : pourquoi de plus en plus de sportifs rejoignent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Décines-Charpieu", breadcrumbLabel: "CrossFit Décines-Charpieu",
    excerpt: "À 18–20 min de Décines-Charpieu : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-lyon-8",
    title: "CrossFit Lyon 8 | Salle de sport et fitness proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Lyon 8 ? Découvrez CrossFit Chistera : sport santé, Hyrox, remise en forme et coaching personnalisé.",
    h1: "Faire du sport à Lyon 8 : pourquoi certains sportifs préfèrent désormais s'entraîner chez CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Lyon 8", breadcrumbLabel: "CrossFit Lyon 8",
    excerpt: "À 18–20 min de Lyon 8 : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-vaulx-en-velin",
    title: "CrossFit Vaulx-en-Velin | Salle de sport et fitness proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Vaulx-en-Velin ? Découvrez CrossFit Chistera : sport santé, Hyrox, remise en forme et coaching personnalisé.",
    h1: "Faire du sport à Vaulx-en-Velin : pourquoi certains sportifs recherchent aujourd'hui une salle plus humaine comme CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Vaulx-en-Velin", breadcrumbLabel: "CrossFit Vaulx-en-Velin",
    excerpt: "À 20–22 min de Vaulx-en-Velin : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-oytier-saint-oblas",
    title: "CrossFit Oytier-Saint-Oblas | Salle de sport et fitness proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche d'Oytier-Saint-Oblas ? Découvrez CrossFit Chistera : sport santé, Hyrox, remise en forme et coaching personnalisé.",
    h1: "Faire du sport à Oytier-Saint-Oblas : pourquoi de plus en plus d'habitants viennent chez CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Oytier-Saint-Oblas", breadcrumbLabel: "CrossFit Oytier-Saint-Oblas",
    excerpt: "À 16–18 min d'Oytier-Saint-Oblas : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-pont-eveque",
    title: "CrossFit Pont-Évêque | Salle de sport et fitness proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Pont-Évêque ? Découvrez CrossFit Chistera : sport santé, Hyrox, remise en forme et coaching personnalisé.",
    h1: "Faire du sport à Pont-Évêque : pourquoi de plus en plus de sportifs rejoignent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Pont-Évêque", breadcrumbLabel: "CrossFit Pont-Évêque",
    excerpt: "À 18–20 min de Pont-Évêque : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-bonnefamille",
    title: "CrossFit Bonnefamille | Salle de sport",
    description: "Vous cherchez une salle de sport ou de CrossFit proche de Bonnefamille ? Découvrez CrossFit Chistera : coaching, Hyrox, sport santé, remise en forme et ambiance familiale.",
    h1: "Faire du sport à Bonnefamille : pourquoi de plus en plus de sportifs choisissent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Bonnefamille", breadcrumbLabel: "CrossFit Bonnefamille",
    excerpt: "À 13–15 min de Bonnefamille : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-saint-pierre-de-chandieu",
    title: "CrossFit Saint-Pierre-de-Chandieu",
    description: "Le CrossFit à Saint-Pierre-de-Chandieu : Chistera, votre box affiliée locale. Coaching individualisé, ambiance familiale, parking gratuit.",
    h1: "Le CrossFit à Saint-Pierre-de-Chandieu : votre box locale CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 11,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · CrossFit", breadcrumbLabel: "CrossFit Saint-Pierre-de-Chandieu",
    excerpt: "Le CrossFit à Saint-Pierre-de-Chandieu : Chistera, votre box affiliée locale. Coaching individualisé, ambiance familiale, parking gratuit.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-marennes",
    title: "CrossFit Marennes : box à 8 min — Chistera",
    description: "Pratiquer le CrossFit quand on habite Marennes : box voisine à Saint-Pierre-de-Chandieu, coaching personnalisé. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour les habitants de Marennes : box voisine en proximité",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Marennes", breadcrumbLabel: "CrossFit Marennes",
    excerpt: "Pratiquer le CrossFit quand on habite Marennes : box voisine à Saint-Pierre-de-Chandieu, coaching personnalisé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-simandres",
    title: "CrossFit Simandres | Salle de sport, fitness et Hyrox proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Simandres ? Découvrez CrossFit Chistera : coaching, sport santé, Hyrox, remise en forme et ambiance familiale.",
    h1: "Faire du sport à Simandres : pourquoi de plus en plus d'habitants choisissent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Simandres", breadcrumbLabel: "CrossFit Simandres",
    excerpt: "À 10–12 min de Simandres : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-chuzelles",
    title: "CrossFit Chuzelles | Salle de sport, fitness et Hyrox proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Chuzelles ? Découvrez CrossFit Chistera : coaching, sport santé, Hyrox, remise en forme et ambiance familiale.",
    h1: "Faire du sport à Chuzelles : pourquoi de plus en plus d'habitants rejoignent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Chuzelles", breadcrumbLabel: "CrossFit Chuzelles",
    excerpt: "À 14–16 min de Chuzelles : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-diemoz",
    title: "CrossFit Diémoz | Salle de sport, fitness et Hyrox proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Diémoz ? Découvrez CrossFit Chistera : coaching, sport santé, Hyrox et remise en forme.",
    h1: "Faire du sport à Diémoz : pourquoi CrossFit Chistera attire de plus en plus de sportifs",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Diémoz", breadcrumbLabel: "CrossFit Diémoz",
    excerpt: "À 10–12 min de Diémoz : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-charantonnay",
    title: "CrossFit Charantonnay | Salle de sport et Hyrox proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Charantonnay ? Découvrez CrossFit Chistera : coaching, sport santé, remise en forme et Hyrox.",
    h1: "Faire du sport à Charantonnay : pourquoi CrossFit Chistera attire de plus en plus d'habitants",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Charantonnay", breadcrumbLabel: "CrossFit Charantonnay",
    excerpt: "À 12–14 min de Charantonnay : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-septeme",
    title: "CrossFit Septème | Salle de sport, fitness et Hyrox proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Septème ? Découvrez CrossFit Chistera : coaching, sport santé, Hyrox, remise en forme et ambiance familiale.",
    h1: "Faire du sport à Septème : pourquoi de plus en plus d'habitants choisissent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Septème", breadcrumbLabel: "CrossFit Septème",
    excerpt: "À 15–17 min de Septème : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-valencin",
    title: "CrossFit Valencin | Salle de sport, fitness et Hyrox proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Valencin ? Découvrez CrossFit Chistera : coaching, sport santé, Hyrox et remise en forme.",
    h1: "Faire du sport à Valencin : pourquoi CrossFit Chistera attire de plus en plus de sportifs",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Valencin", breadcrumbLabel: "CrossFit Valencin",
    excerpt: "À 6–8 min de Valencin : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-bourgoin-jallieu",
    title: "CrossFit Bourgoin-Jallieu | Salle de sport, fitness et Hyrox",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Bourgoin-Jallieu ? Découvrez CrossFit Chistera : coaching, sport santé, Hyrox et préparation physique.",
    h1: "Faire du sport à Bourgoin-Jallieu : pourquoi certains sportifs préfèrent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Bourgoin-Jallieu", breadcrumbLabel: "CrossFit Bourgoin-Jallieu",
    excerpt: "À 20–23 min de Bourgoin-Jallieu : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-grenay",
    title: "CrossFit Grenay | Salle de sport, fitness et Hyrox proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Grenay ? Découvrez CrossFit Chistera : coaching, Hyrox, sport santé, remise en forme et ambiance familiale.",
    h1: "Faire du sport à Grenay : pourquoi de plus en plus d'habitants rejoignent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Grenay", breadcrumbLabel: "CrossFit Grenay",
    excerpt: "À 7–9 min de Grenay : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-saint-just-chaleyssin",
    title: "CrossFit Saint-Just-Chaleyssin : box à 7 min — Chistera",
    description: "Pratiquer le CrossFit quand on habite Saint-Just-Chaleyssin : box voisine immédiate à Saint-Pierre-de-Chandieu. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour les habitants de Saint-Just-Chaleyssin : box voisine",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Saint-Just-Chaleyssin", breadcrumbLabel: "CrossFit Saint-Just-Chaleyssin",
    excerpt: "Pratiquer le CrossFit quand on habite Saint-Just-Chaleyssin : box voisine immédiate à Saint-Pierre-de-Chandieu. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-la-verpilliere",
    title: "CrossFit La Verpillière | Salle de sport, fitness et Hyrox",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de La Verpillière ? Découvrez CrossFit Chistera : coaching, Hyrox, sport santé et remise en forme.",
    h1: "Faire du sport à La Verpillière : pourquoi certains sportifs choisissent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · La Verpillière", breadcrumbLabel: "CrossFit La Verpillière",
    excerpt: "À 15–17 min de La Verpillière : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-lyon-3",
    title: "CrossFit Lyon 3 | Salle de sport et Hyrox proche",
    description: "Vous cherchez une salle de CrossFit ou de fitness proche de Lyon 3 ? Découvrez CrossFit Chistera : coaching, Hyrox, sport santé et remise en forme.",
    h1: "Faire du sport à Lyon 3 : pourquoi de plus en plus de sportifs rejoignent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Lyon 3", breadcrumbLabel: "CrossFit Lyon 3",
    excerpt: "À environ 18 min de Lyon 3 : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-oullins",
    title: "CrossFit Oullins | Salle de sport et fitness",
    description: "Salle de CrossFit proche d'Oullins : découvrez CrossFit Chistera, Hyrox, coaching personnalisé et sport santé.",
    h1: "Faire du sport à Oullins : pourquoi CrossFit Chistera attire de plus en plus de sportifs",
    publishedAt: "2026-05-14", readingMinutes: 7,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Oullins", breadcrumbLabel: "CrossFit Oullins",
    excerpt: "À environ 23 min d'Oullins : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-tignieu-jameyzieu",
    title: "CrossFit Tignieu-Jameyzieu | Salle de sport et Hyrox",
    description: "Salle de CrossFit proche de Tignieu-Jameyzieu : coaching, Hyrox, sport santé et remise en forme chez CrossFit Chistera.",
    h1: "Faire du sport à Tignieu-Jameyzieu : pourquoi CrossFit Chistera attire de plus en plus de sportifs",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Tignieu-Jameyzieu", breadcrumbLabel: "CrossFit Tignieu-Jameyzieu",
    excerpt: "À environ 22 min de Tignieu-Jameyzieu : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-pont-de-cheruy",
    title: "CrossFit Pont-de-Chéruy | Salle de sport et fitness",
    description: "Salle de sport proche de Pont-de-Chéruy : découvrez CrossFit Chistera, Hyrox, coaching personnalisé et sport santé.",
    h1: "Faire du sport à Pont-de-Chéruy : pourquoi CrossFit Chistera attire de plus en plus de sportifs",
    publishedAt: "2026-05-14", readingMinutes: 7,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Pont-de-Chéruy", breadcrumbLabel: "CrossFit Pont-de-Chéruy",
    excerpt: "À environ 24 min de Pont-de-Chéruy : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-lisle-dabeau",
    title: "CrossFit L'Isle-d'Abeau | Salle de sport et Hyrox",
    description: "Salle de CrossFit proche de L'Isle-d'Abeau : découvrez CrossFit Chistera, coaching, Hyrox et remise en forme.",
    h1: "Faire du sport à L'Isle-d'Abeau : pourquoi certains sportifs choisissent CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 7,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · L'Isle-d'Abeau", breadcrumbLabel: "CrossFit L'Isle-d'Abeau",
    excerpt: "À environ 22 min de L'Isle-d'Abeau : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-pusignan",
    title: "CrossFit Pusignan | Salle de sport et Hyrox proche",
    description: "Salle de CrossFit proche de Pusignan : coaching, Hyrox, remise en forme et sport santé chez CrossFit Chistera.",
    h1: "Faire du sport à Pusignan : pourquoi CrossFit Chistera attire de plus en plus de sportifs",
    publishedAt: "2026-05-14", readingMinutes: 7,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Pusignan", breadcrumbLabel: "CrossFit Pusignan",
    excerpt: "À environ 15 min de Pusignan : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-colombier-saugnieu",
    title: "CrossFit Colombier-Saugnieu | Salle de sport",
    description: "Salle de CrossFit proche de Colombier-Saugnieu : Hyrox, coaching, sport santé et préparation physique chez CrossFit Chistera.",
    h1: "Faire du sport à Colombier-Saugnieu : pourquoi CrossFit Chistera attire de plus en plus de pratiquants",
    publishedAt: "2026-05-14", readingMinutes: 7,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Colombier-Saugnieu", breadcrumbLabel: "CrossFit Colombier-Saugnieu",
    excerpt: "À environ 16 min de Colombier-Saugnieu : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-givors",
    title: "CrossFit Givors | Salle de sport et Hyrox",
    description: "Salle de sport proche de Givors : découvrez CrossFit Chistera, Hyrox, coaching et remise en forme.",
    h1: "Faire du sport à Givors : pourquoi CrossFit Chistera attire de plus en plus de sportifs",
    publishedAt: "2026-05-14", readingMinutes: 7,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Givors", breadcrumbLabel: "CrossFit Givors",
    excerpt: "À environ 24 min de Givors : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-saint-genis-laval",
    title: "CrossFit Saint-Genis-Laval | Salle de sport",
    description: "Salle de CrossFit proche de Saint-Genis-Laval : découvrez CrossFit Chistera, coaching, Hyrox et sport santé.",
    h1: "Faire du sport à Saint-Genis-Laval : pourquoi CrossFit Chistera attire de plus en plus de sportifs",
    publishedAt: "2026-05-14", readingMinutes: 7,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Saint-Genis-Laval", breadcrumbLabel: "CrossFit Saint-Genis-Laval",
    excerpt: "À environ 24 min de Saint-Genis-Laval : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-jonage",
    title: "CrossFit Jonage | Salle de sport et Hyrox",
    description: "Salle de CrossFit proche de Jonage : découvrez CrossFit Chistera, Hyrox, coaching et remise en forme.",
    h1: "Faire du sport à Jonage : pourquoi CrossFit Chistera attire de plus en plus de sportifs",
    publishedAt: "2026-05-14", readingMinutes: 7,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Jonage", breadcrumbLabel: "CrossFit Jonage",
    excerpt: "À environ 18 min de Jonage : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-jons",
    title: "CrossFit Jons | Salle de sport et fitness",
    description: "Salle de CrossFit proche de Jons : découvrez CrossFit Chistera, coaching, Hyrox et sport santé.",
    h1: "Faire du sport à Jons : pourquoi CrossFit Chistera attire de plus en plus d'adhérents",
    publishedAt: "2026-05-14", readingMinutes: 7,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "CrossFit · Jons", breadcrumbLabel: "CrossFit Jons",
    excerpt: "À environ 17 min de Jons : CrossFit, Hyrox, Hybrid Training, sport santé, coaching et ambiance familiale.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-saint-priest",
    title: "Salle de sport Saint-Priest",
    description: "Salle de sport à Saint-Priest : types d'établissements, critères de choix, alternatives. Conseils pratiques par CrossFit Chistera.",
    h1: "Salle de sport à Saint-Priest : comprendre l'offre, faire le bon choix",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · Salle de sport", breadcrumbLabel: "Salle de sport Saint-Priest",
    excerpt: "Salle de sport à Saint-Priest : types d'établissements, critères de choix, alternatives. Conseils pratiques par CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-saint-priest",
    title: "Salle de fitness Saint-Priest : modèle, limites — Chistera",
    description: "Salle de fitness à Saint-Priest : avantages, limites du modèle abonnement, profils adaptés. Guide pratique CrossFit Chistera.",
    h1: "Salle de fitness à Saint-Priest : pour qui ce modèle convient vraiment",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · Salle de fitness", breadcrumbLabel: "Salle de fitness Saint-Priest",
    excerpt: "Salle de fitness à Saint-Priest : avantages, limites du modèle abonnement, profils adaptés. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-saint-priest",
    title: "CrossFit débutant Saint-Priest",
    description: "Démarrer le CrossFit à Saint-Priest sans bagage sportif : déroulé d'une première séance, adaptations, conseils pratiques. Guide CrossFit Chistera.",
    h1: "Commencer le CrossFit à Saint-Priest quand on n'a jamais pratiqué",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Saint-Priest",
    excerpt: "Démarrer le CrossFit à Saint-Priest sans bagage sportif : déroulé d'une première séance, adaptations, conseils pratiques. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-saint-priest",
    title: "CrossFit enfants Saint-Priest : programme adapté — Chistera",
    description: "CrossFit pour enfants à Saint-Priest : philosophie, sécurité, déroulé. Programme Kids encadré à proximité par CrossFit Chistera.",
    h1: "CrossFit pour enfants à Saint-Priest : ce que les parents doivent savoir",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Saint-Priest",
    excerpt: "CrossFit pour enfants à Saint-Priest : philosophie, sécurité, déroulé. Programme Kids encadré à proximité par CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-saint-priest",
    title: "CrossFit senior Saint-Priest : pratique adaptée — Chistera",
    description: "CrossFit pour seniors à Saint-Priest : adaptations, bénéfices santé, sécurité. Approche scientifique et humaine par CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Saint-Priest : préserver activement sa santé",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · CrossFit senior", breadcrumbLabel: "CrossFit senior Saint-Priest",
    excerpt: "CrossFit pour seniors à Saint-Priest : adaptations, bénéfices santé, sécurité. Approche scientifique et humaine par CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-saint-priest",
    title: "Sport santé Saint-Priest : activité adaptée — Chistera",
    description: "Sport santé à Saint-Priest : APA, prescription médicale, accompagnement, structures locales. Guide pratique CrossFit Chistera.",
    h1: "Le sport santé à Saint-Priest : activité physique adaptée et accompagnement",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · Sport santé", breadcrumbLabel: "Sport santé Saint-Priest",
    excerpt: "Sport santé à Saint-Priest : APA, prescription médicale, accompagnement, structures locales. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-saint-priest",
    title: "Callisthénie Saint-Priest : pratique et progressions",
    description: "La callisthénie à Saint-Priest : parcs urbains, pratique en salle, progressions. Guide pratique par CrossFit Chistera.",
    h1: "La callisthénie à Saint-Priest : pratique urbaine, salle, progression",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · Callisthénie", breadcrumbLabel: "Callisthénie Saint-Priest",
    excerpt: "La callisthénie à Saint-Priest : parcs urbains, pratique en salle, progressions. Guide pratique par CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-saint-priest",
    title: "Hybrid training Saint-Priest : force et endurance — Chistera",
    description: "L'hybrid training à Saint-Priest : combiner force et endurance, structure de semaine, coaching. Guide pratique par CrossFit Chistera.",
    h1: "L'hybrid training à Saint-Priest : combiner force et endurance intelligemment",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · Hybrid training", breadcrumbLabel: "Hybrid training Saint-Priest",
    excerpt: "L'hybrid training à Saint-Priest : combiner force et endurance, structure de semaine, coaching. Guide pratique par CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-saint-priest",
    title: "Pilates Saint-Priest : méthode et bénéfices — Chistera",
    description: "Le pilates à Saint-Priest : principes, bénéfices, studios locaux, complémentarité avec le CrossFit. Guide pratique CrossFit Chistera.",
    h1: "Le pilates à Saint-Priest : panorama et place dans une pratique globale",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · Pilates", breadcrumbLabel: "Pilates Saint-Priest",
    excerpt: "Le pilates à Saint-Priest : principes, bénéfices, studios locaux, complémentarité avec le CrossFit. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-saint-priest",
    title: "Crosstraining Saint-Priest",
    description: "Crosstraining à Saint-Priest : différences avec le CrossFit officiel, comment choisir. Guide pratique par CrossFit Chistera.",
    h1: "Crosstraining à Saint-Priest : comprendre les différences, choisir le bon cadre",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · Crosstraining", breadcrumbLabel: "Crosstraining Saint-Priest",
    excerpt: "Crosstraining à Saint-Priest : différences avec le CrossFit officiel, comment choisir. Guide pratique par CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-saint-priest",
    title: "ATHX Saint-Priest : conditioning accessible — Chistera",
    description: "L'ATHX à Saint-Priest : format de cours, accessibilité, complémentarité avec le CrossFit. Présentation par CrossFit Chistera.",
    h1: "L'ATHX à Saint-Priest : un format de conditioning accessible et exigeant",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · ATHX", breadcrumbLabel: "ATHX Saint-Priest",
    excerpt: "L'ATHX à Saint-Priest : format de cours, accessibilité, complémentarité avec le CrossFit. Présentation par CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-saint-priest",
    title: "Hyrox Saint-Priest : préparation et coaching — Chistera",
    description: "Préparer Hyrox à Saint-Priest : format de course, programme d'entraînement, coaching dédié. Guide pratique par CrossFit Chistera.",
    h1: "Hyrox à Saint-Priest : comprendre le format, préparer la course",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · Hyrox", breadcrumbLabel: "Hyrox Saint-Priest",
    excerpt: "Préparer Hyrox à Saint-Priest : format de course, programme d'entraînement, coaching dédié. Guide pratique par CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-saint-priest",
    title: "Préparation physique Saint-Priest",
    description: "Préparation physique à Saint-Priest : sportifs amateurs, clubs locaux, performance et prévention blessures. Guide CrossFit Chistera.",
    h1: "La préparation physique à Saint-Priest : performance et prévention pour le sportif amateur",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · Préparation physique", breadcrumbLabel: "Préparation physique Saint-Priest",
    excerpt: "Préparation physique à Saint-Priest : sportifs amateurs, clubs locaux, performance et prévention blessures. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-saint-priest",
    title: "Remise en forme Saint-Priest",
    description: "Remise en forme à Saint-Priest après une longue pause : méthode progressive, choix d'activité, conseils pratiques. Guide CrossFit Chistera.",
    h1: "Remise en forme à Saint-Priest : reprendre le sport sans se décourager",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · Remise en forme", breadcrumbLabel: "Remise en forme Saint-Priest",
    excerpt: "Remise en forme à Saint-Priest après une longue pause : méthode progressive, choix d'activité, conseils pratiques. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-saint-priest",
    title: "Perte de poids Saint-Priest : sport et méthode — Chistera",
    description: "Perte de poids durable à Saint-Priest : rôle du sport, accompagnement nutritionnel, méthode. Guide honnête par CrossFit Chistera.",
    h1: "Perte de poids à Saint-Priest : ce que le sport apporte vraiment",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Priest · Perte de poids", breadcrumbLabel: "Perte de poids Saint-Priest",
    excerpt: "Perte de poids durable à Saint-Priest : rôle du sport, accompagnement nutritionnel, méthode. Guide honnête par CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-bonnefamille",
    title: "Salle de sport Bonnefamille",
    description: "Salle de sport quand on habite Bonnefamille : pas d'offre intra-muros, options dans les communes voisines. Guide pratique CrossFit Chistera.",
    h1: "Salle de sport pour les Bonnefamillois : où aller quand il n'y a pas d'offre locale",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · Salle de sport", breadcrumbLabel: "Salle de sport Bonnefamille",
    excerpt: "Salle de sport quand on habite Bonnefamille : pas d'offre intra-muros, options dans les communes voisines. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-bonnefamille",
    title: "Salle de fitness Bonnefamille",
    description: "Salle de fitness pour les Bonnefamillois : pas d'offre locale, alternatives dans les communes voisines. Guide CrossFit Chistera.",
    h1: "Salle de fitness quand on habite Bonnefamille : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · Salle de fitness", breadcrumbLabel: "Salle de fitness Bonnefamille",
    excerpt: "Salle de fitness pour les Bonnefamillois : pas d'offre locale, alternatives dans les communes voisines. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-bonnefamille",
    title: "CrossFit débutant Bonnefamille",
    description: "Commencer le CrossFit quand on habite Bonnefamille : box voisine, accueil débutants. Guide pratique CrossFit Chistera.",
    h1: "Démarrer le CrossFit quand on habite Bonnefamille : pour les vrais débutants",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Bonnefamille",
    excerpt: "Commencer le CrossFit quand on habite Bonnefamille : box voisine, accueil débutants. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-bonnefamille",
    title: "CrossFit enfants Bonnefamille : programme Kids — Chistera",
    description: "CrossFit pour enfants à Bonnefamille : programme adapté à 5 minutes, sécurité, bénéfices. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Bonnefamille : programme à proximité immédiate",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Bonnefamille",
    excerpt: "CrossFit pour enfants à Bonnefamille : programme adapté à 5 minutes, sécurité, bénéfices. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-bonnefamille",
    title: "CrossFit senior Bonnefamille : pratique adaptée — Chistera",
    description: "CrossFit pour seniors à Bonnefamille : adaptations, bénéfices santé, box voisine à 5 minutes. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Bonnefamille : préserver activement sa santé",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · CrossFit senior", breadcrumbLabel: "CrossFit senior Bonnefamille",
    excerpt: "CrossFit pour seniors à Bonnefamille : adaptations, bénéfices santé, box voisine à 5 minutes. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-bonnefamille",
    title: "Sport santé Bonnefamille : activité adaptée — Chistera",
    description: "Sport santé à Bonnefamille : APA, prescription, accompagnement. Box voisine à 5 minutes pour pratique encadrée. Guide CrossFit Chistera.",
    h1: "Le sport santé pour les habitants de Bonnefamille : pratique adaptée et accompagnement",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · Sport santé", breadcrumbLabel: "Sport santé Bonnefamille",
    excerpt: "Sport santé à Bonnefamille : APA, prescription, accompagnement. Box voisine à 5 minutes pour pratique encadrée. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-bonnefamille",
    title: "Callisthénie Bonnefamille : pratique au poids du corps",
    description: "La callisthénie à Bonnefamille : pratique en extérieur, salle voisine, progressions. Guide CrossFit Chistera.",
    h1: "La callisthénie pour les Bonnefamillois : pratique extérieure et progression encadrée",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · Callisthénie", breadcrumbLabel: "Callisthénie Bonnefamille",
    excerpt: "La callisthénie à Bonnefamille : pratique en extérieur, salle voisine, progressions. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-bonnefamille",
    title: "Hybrid training Bonnefamille : force et endurance — Chistera",
    description: "L'hybrid training à Bonnefamille : combiner force et endurance, terrain rural pour le cardio. Guide CrossFit Chistera.",
    h1: "L'hybrid training pour les Bonnefamillois : tirer parti du terrain rural",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · Hybrid training", breadcrumbLabel: "Hybrid training Bonnefamille",
    excerpt: "L'hybrid training à Bonnefamille : combiner force et endurance, terrain rural pour le cardio. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-bonnefamille",
    title: "Pilates Bonnefamille : pratique et complémentarité",
    description: "Le pilates à Bonnefamille : principes, bénéfices, options locales, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates pour les Bonnefamillois : renforcement profond, options locales",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · Pilates", breadcrumbLabel: "Pilates Bonnefamille",
    excerpt: "Le pilates à Bonnefamille : principes, bénéfices, options locales, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-bonnefamille",
    title: "Crosstraining Bonnefamille",
    description: "Crosstraining à Bonnefamille : différences avec CrossFit, choisir le bon cadre. Guide pratique par CrossFit Chistera.",
    h1: "Crosstraining pour les Bonnefamillois : comprendre les différences",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · Crosstraining", breadcrumbLabel: "Crosstraining Bonnefamille",
    excerpt: "Crosstraining à Bonnefamille : différences avec CrossFit, choisir le bon cadre. Guide pratique par CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-bonnefamille",
    title: "ATHX Bonnefamille : conditioning accessible — Chistera",
    description: "L'ATHX à Bonnefamille : format de cours, accessibilité, complémentarité CrossFit. Présentation par CrossFit Chistera.",
    h1: "L'ATHX pour les Bonnefamillois : conditioning accessible à proximité",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · ATHX", breadcrumbLabel: "ATHX Bonnefamille",
    excerpt: "L'ATHX à Bonnefamille : format de cours, accessibilité, complémentarité CrossFit. Présentation par CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-bonnefamille",
    title: "Hyrox Bonnefamille : préparation et coaching — Chistera",
    description: "Préparer Hyrox quand on habite Bonnefamille : format, programme, coaching voisin. Guide CrossFit Chistera.",
    h1: "Hyrox quand on habite Bonnefamille : préparer la course efficacement",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · Hyrox", breadcrumbLabel: "Hyrox Bonnefamille",
    excerpt: "Préparer Hyrox quand on habite Bonnefamille : format, programme, coaching voisin. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-bonnefamille",
    title: "Préparation physique Bonnefamille",
    description: "Préparation physique à Bonnefamille : sportifs amateurs, performance, prévention blessures. Guide CrossFit Chistera.",
    h1: "Préparation physique à Bonnefamille : performance et prévention pour le sportif amateur",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · Préparation physique", breadcrumbLabel: "Préparation physique Bonnefamille",
    excerpt: "Préparation physique à Bonnefamille : sportifs amateurs, performance, prévention blessures. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-bonnefamille",
    title: "Remise en forme Bonnefamille",
    description: "Remise en forme à Bonnefamille : méthode progressive, atouts du cadre rural. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Bonnefamillois : reprendre sans se décourager",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · Remise en forme", breadcrumbLabel: "Remise en forme Bonnefamille",
    excerpt: "Remise en forme à Bonnefamille : méthode progressive, atouts du cadre rural. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-bonnefamille",
    title: "Perte de poids Bonnefamille : sport et méthode — Chistera",
    description: "Perte de poids durable à Bonnefamille : rôle du sport, nutrition, méthode. Guide CrossFit Chistera.",
    h1: "Perte de poids à Bonnefamille : ce que le sport apporte vraiment",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Bonnefamille · Perte de poids", breadcrumbLabel: "Perte de poids Bonnefamille",
    excerpt: "Perte de poids durable à Bonnefamille : rôle du sport, nutrition, méthode. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-chaponnay",
    title: "Salle de sport Chaponnay : options et alternatives",
    description: "Salle de sport à Chaponnay : tissu local, alternatives voisines, critères de choix. Guide pratique CrossFit Chistera.",
    h1: "Salle de sport à Chaponnay : ce qu'on trouve sur place, ce qu'il faut chercher ailleurs",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · Salle de sport", breadcrumbLabel: "Salle de sport Chaponnay",
    excerpt: "Salle de sport à Chaponnay : tissu local, alternatives voisines, critères de choix. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-chaponnay",
    title: "Salle de fitness Chaponnay : modèle, alternatives — Chistera",
    description: "Salle de fitness pour les Chaponnois : modèle abonnement, alternatives en communes voisines. Guide pratique CrossFit Chistera.",
    h1: "Salle de fitness pour les Chaponnois : quand le modèle convient, quand changer",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · Salle de fitness", breadcrumbLabel: "Salle de fitness Chaponnay",
    excerpt: "Salle de fitness pour les Chaponnois : modèle abonnement, alternatives en communes voisines. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-chaponnay",
    title: "CrossFit débutant Chaponnay : démarrer à 5 min — Chistera",
    description: "Commencer le CrossFit à Chaponnay : déroulé d'une première séance, accueil débutants chez Chistera à proximité immédiate.",
    h1: "Démarrer le CrossFit pour les Chaponnois : guide pour les vrais débutants",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Chaponnay",
    excerpt: "Commencer le CrossFit à Chaponnay : déroulé d'une première séance, accueil débutants chez Chistera à proximité immédiate.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-chaponnay",
    title: "CrossFit enfants Chaponnay : programme Kids — Chistera",
    description: "CrossFit pour enfants à Chaponnay : programme adapté, sécurité, bénéfices. Box voisine à 5 minutes.",
    h1: "Le CrossFit pour enfants quand on habite Chaponnay : option de proximité",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Chaponnay",
    excerpt: "CrossFit pour enfants à Chaponnay : programme adapté, sécurité, bénéfices. Box voisine à 5 minutes.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-chaponnay",
    title: "CrossFit senior Chaponnay : pratique adaptée — Chistera",
    description: "CrossFit pour seniors à Chaponnay : adaptations, bénéfices santé, box voisine à 5 min. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Chaponnay : préserver activement sa santé",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · CrossFit senior", breadcrumbLabel: "CrossFit senior Chaponnay",
    excerpt: "CrossFit pour seniors à Chaponnay : adaptations, bénéfices santé, box voisine à 5 min. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-chaponnay",
    title: "Sport santé Chaponnay : APA et accompagnement — Chistera",
    description: "Sport santé à Chaponnay : APA, prescription, accompagnement local. Guide pratique CrossFit Chistera.",
    h1: "Le sport santé pour les Chaponnois : pratique adaptée et accompagnement",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · Sport santé", breadcrumbLabel: "Sport santé Chaponnay",
    excerpt: "Sport santé à Chaponnay : APA, prescription, accompagnement local. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-chaponnay",
    title: "Callisthénie Chaponnay : pratique au poids du corps",
    description: "La callisthénie à Chaponnay : pratique en extérieur, salle, progressions. Guide pratique CrossFit Chistera.",
    h1: "La callisthénie pour les Chaponnois : pratique extérieure et progression encadrée",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · Callisthénie", breadcrumbLabel: "Callisthénie Chaponnay",
    excerpt: "La callisthénie à Chaponnay : pratique en extérieur, salle, progressions. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-chaponnay",
    title: "Hybrid training Chaponnay : force et endurance — Chistera",
    description: "L'hybrid training à Chaponnay : combiner force et endurance, structure de semaine. Guide pratique CrossFit Chistera.",
    h1: "L'hybrid training pour les Chaponnois : équilibrer force et endurance intelligemment",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · Hybrid training", breadcrumbLabel: "Hybrid training Chaponnay",
    excerpt: "L'hybrid training à Chaponnay : combiner force et endurance, structure de semaine. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-chaponnay",
    title: "Pilates Chaponnay : pratique et complémentarité — Chistera",
    description: "Le pilates à Chaponnay : principes, bénéfices, options locales, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates pour les Chaponnois : renforcement profond, options locales",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · Pilates", breadcrumbLabel: "Pilates Chaponnay",
    excerpt: "Le pilates à Chaponnay : principes, bénéfices, options locales, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-chaponnay",
    title: "Crosstraining Chaponnay : CrossFit ou indépendant — Chistera",
    description: "Crosstraining à Chaponnay : différences avec CrossFit, choisir le bon cadre. Guide CrossFit Chistera.",
    h1: "Crosstraining pour les Chaponnois : comprendre les différences",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · Crosstraining", breadcrumbLabel: "Crosstraining Chaponnay",
    excerpt: "Crosstraining à Chaponnay : différences avec CrossFit, choisir le bon cadre. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-chaponnay",
    title: "ATHX Chaponnay : conditioning accessible — Chistera",
    description: "L'ATHX à Chaponnay : format de cours, accessibilité, complémentarité CrossFit. Présentation CrossFit Chistera.",
    h1: "L'ATHX pour les Chaponnois : conditioning accessible et exigeant",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · ATHX", breadcrumbLabel: "ATHX Chaponnay",
    excerpt: "L'ATHX à Chaponnay : format de cours, accessibilité, complémentarité CrossFit. Présentation CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-chaponnay",
    title: "Hyrox Chaponnay : préparation et coaching — Chistera",
    description: "Préparer Hyrox quand on habite Chaponnay : format, programme, coaching voisin. Guide CrossFit Chistera.",
    h1: "Hyrox quand on habite Chaponnay : préparer la course efficacement",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · Hyrox", breadcrumbLabel: "Hyrox Chaponnay",
    excerpt: "Préparer Hyrox quand on habite Chaponnay : format, programme, coaching voisin. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-chaponnay",
    title: "Préparation physique Chaponnay",
    description: "Préparation physique à Chaponnay : sportifs amateurs, clubs locaux, performance et prévention. Guide CrossFit Chistera.",
    h1: "Préparation physique à Chaponnay : performance et prévention pour le sportif amateur",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · Préparation physique", breadcrumbLabel: "Préparation physique Chaponnay",
    excerpt: "Préparation physique à Chaponnay : sportifs amateurs, clubs locaux, performance et prévention. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-chaponnay",
    title: "Remise en forme Chaponnay : reprendre durablement — Chistera",
    description: "Remise en forme à Chaponnay : méthode progressive, conseils pratiques. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Chaponnois : reprendre sans se décourager",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · Remise en forme", breadcrumbLabel: "Remise en forme Chaponnay",
    excerpt: "Remise en forme à Chaponnay : méthode progressive, conseils pratiques. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-chaponnay",
    title: "Perte de poids Chaponnay : sport et méthode — Chistera",
    description: "Perte de poids durable à Chaponnay : rôle du sport, nutrition, méthode. Guide CrossFit Chistera.",
    h1: "Perte de poids à Chaponnay : ce que le sport apporte vraiment",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chaponnay · Perte de poids", breadcrumbLabel: "Perte de poids Chaponnay",
    excerpt: "Perte de poids durable à Chaponnay : rôle du sport, nutrition, méthode. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-corbas",
    title: "Salle de sport Corbas : choisir entre les options — Chistera",
    description: "Salle de sport à Corbas : tissu local, critères de choix, alternatives en proximité immédiate. Guide pratique CrossFit Chistera.",
    h1: "Salle de sport à Corbas : faire le bon choix dans un marché plus dense",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · Salle de sport", breadcrumbLabel: "Salle de sport Corbas",
    excerpt: "Salle de sport à Corbas : tissu local, critères de choix, alternatives en proximité immédiate. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-corbas",
    title: "Salle de fitness Corbas : modèle, alternatives — Chistera",
    description: "Salle de fitness à Corbas : avantages, limites du modèle abonnement, profils adaptés, alternatives. Guide pratique CrossFit Chistera.",
    h1: "Salle de fitness à Corbas : pour qui ce modèle convient vraiment",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · Salle de fitness", breadcrumbLabel: "Salle de fitness Corbas",
    excerpt: "Salle de fitness à Corbas : avantages, limites du modèle abonnement, profils adaptés, alternatives. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-corbas",
    title: "CrossFit débutant Corbas : commencer simplement — Chistera",
    description: "Démarrer le CrossFit à Corbas sans bagage sportif : déroulé d'une première séance, accueil débutants. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Corbasiens : guide pour les vrais débutants",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Corbas",
    excerpt: "Démarrer le CrossFit à Corbas sans bagage sportif : déroulé d'une première séance, accueil débutants. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-corbas",
    title: "CrossFit enfants Corbas : programme Kids — Chistera",
    description: "CrossFit pour enfants à Corbas : philosophie, sécurité, bénéfices. Box voisine à 5-10 min. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Corbas : programme à proximité",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Corbas",
    excerpt: "CrossFit pour enfants à Corbas : philosophie, sécurité, bénéfices. Box voisine à 5-10 min. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-corbas",
    title: "CrossFit senior Corbas : pratique adaptée — Chistera",
    description: "CrossFit pour seniors à Corbas : adaptations, bénéfices santé, box voisine. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Corbas : préserver activement sa santé",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · CrossFit senior", breadcrumbLabel: "CrossFit senior Corbas",
    excerpt: "CrossFit pour seniors à Corbas : adaptations, bénéfices santé, box voisine. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-corbas",
    title: "Sport santé Corbas : APA et accompagnement — Chistera",
    description: "Sport santé à Corbas : activité physique adaptée, prescription, accompagnement. Guide pratique CrossFit Chistera.",
    h1: "Le sport santé pour les habitants de Corbas : pratique adaptée et accompagnement",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · Sport santé", breadcrumbLabel: "Sport santé Corbas",
    excerpt: "Sport santé à Corbas : activité physique adaptée, prescription, accompagnement. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-corbas",
    title: "Callisthénie Corbas : pratique au poids du corps — Chistera",
    description: "La callisthénie à Corbas : pratique en extérieur, salle, progressions. Guide pratique CrossFit Chistera.",
    h1: "La callisthénie à Corbas : pratique urbaine et progressive",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · Callisthénie", breadcrumbLabel: "Callisthénie Corbas",
    excerpt: "La callisthénie à Corbas : pratique en extérieur, salle, progressions. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-corbas",
    title: "Hybrid training Corbas : force et endurance — Chistera",
    description: "L'hybrid training à Corbas : combiner force et endurance, structure de semaine, coaching. Guide pratique CrossFit Chistera.",
    h1: "L'hybrid training à Corbas : combiner force et endurance intelligemment",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · Hybrid training", breadcrumbLabel: "Hybrid training Corbas",
    excerpt: "L'hybrid training à Corbas : combiner force et endurance, structure de semaine, coaching. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-corbas",
    title: "Pilates Corbas : pratique et complémentarité — Chistera",
    description: "Le pilates à Corbas : principes, bénéfices, options locales, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Corbas : panorama et place dans une pratique sportive globale",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · Pilates", breadcrumbLabel: "Pilates Corbas",
    excerpt: "Le pilates à Corbas : principes, bénéfices, options locales, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-corbas",
    title: "Crosstraining Corbas : CrossFit ou indépendant — Chistera",
    description: "Crosstraining à Corbas : différences avec CrossFit, choisir le bon cadre. Guide pratique CrossFit Chistera.",
    h1: "Crosstraining à Corbas : comprendre les différences, choisir le bon cadre",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · Crosstraining", breadcrumbLabel: "Crosstraining Corbas",
    excerpt: "Crosstraining à Corbas : différences avec CrossFit, choisir le bon cadre. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-corbas",
    title: "ATHX Corbas : conditioning accessible — Chistera",
    description: "L'ATHX à Corbas : format de cours, accessibilité, complémentarité CrossFit. Présentation CrossFit Chistera.",
    h1: "L'ATHX à Corbas : un format de conditioning accessible et exigeant",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · ATHX", breadcrumbLabel: "ATHX Corbas",
    excerpt: "L'ATHX à Corbas : format de cours, accessibilité, complémentarité CrossFit. Présentation CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-corbas",
    title: "Hyrox Corbas : préparation et coaching — Chistera",
    description: "Préparer Hyrox quand on habite Corbas : format, programme, coaching à proximité. Guide CrossFit Chistera.",
    h1: "Hyrox à Corbas : comprendre le format, préparer la course",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · Hyrox", breadcrumbLabel: "Hyrox Corbas",
    excerpt: "Préparer Hyrox quand on habite Corbas : format, programme, coaching à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-corbas",
    title: "Préparation physique Corbas : sportifs amateurs — Chistera",
    description: "Préparation physique à Corbas : sportifs amateurs, performance et prévention. Guide CrossFit Chistera.",
    h1: "La préparation physique à Corbas : performance et prévention pour le sportif amateur",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · Préparation physique", breadcrumbLabel: "Préparation physique Corbas",
    excerpt: "Préparation physique à Corbas : sportifs amateurs, performance et prévention. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-corbas",
    title: "Remise en forme Corbas : reprendre durablement — Chistera",
    description: "Remise en forme à Corbas après une longue pause : méthode progressive, conseils pratiques. Guide CrossFit Chistera.",
    h1: "Remise en forme à Corbas : reprendre le sport sans se décourager",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · Remise en forme", breadcrumbLabel: "Remise en forme Corbas",
    excerpt: "Remise en forme à Corbas après une longue pause : méthode progressive, conseils pratiques. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-corbas",
    title: "Perte de poids Corbas : sport et méthode — Chistera",
    description: "Perte de poids durable à Corbas : rôle du sport, nutrition, méthode. Guide honnête CrossFit Chistera.",
    h1: "Perte de poids à Corbas : ce que le sport peut vraiment apporter",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Corbas · Perte de poids", breadcrumbLabel: "Perte de poids Corbas",
    excerpt: "Perte de poids durable à Corbas : rôle du sport, nutrition, méthode. Guide honnête CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },

  {
    slug: "salle-de-sport-saint-pierre-de-chandieu",
    title: "Salle de sport Saint-Pierre-de-Chandieu",
    description: "Salle de sport à Saint-Pierre-de-Chandieu : tissu local, box CrossFit Chistera, options en proximité. Guide pratique.",
    h1: "Salle de sport à Saint-Pierre-de-Chandieu : tissu local et choix selon votre profil",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · Salle de sport", breadcrumbLabel: "Salle de sport Saint-Pierre-de-Chandieu",
    excerpt: "Salle de sport à Saint-Pierre-de-Chandieu : tissu local, box CrossFit Chistera, options en proximité. Guide pratique.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-saint-pierre-de-chandieu",
    title: "Salle de fitness Saint-Pierre-de-Chandieu",
    description: "Salle de fitness à Saint-Pierre-de-Chandieu : pas d'enseigne intra-muros, alternatives en communes voisines. Guide pratique CrossFit Chistera.",
    h1: "Salle de fitness pour les habitants de Saint-Pierre-de-Chandieu : où aller",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · Salle de fitness", breadcrumbLabel: "Salle de fitness Saint-Pierre-de-Chandieu",
    excerpt: "Salle de fitness à Saint-Pierre-de-Chandieu : pas d'enseigne intra-muros, alternatives en communes voisines. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-saint-pierre-de-chandieu",
    title: "CrossFit débutant Saint-Pierre-de-Chandieu",
    description: "Démarrer le CrossFit à Saint-Pierre-de-Chandieu : box locale Chistera, accueil débutants, première séance. Guide pratique.",
    h1: "Démarrer le CrossFit à Saint-Pierre-de-Chandieu : guide pour les vrais débutants",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Saint-Pierre-de-Chandieu",
    excerpt: "Démarrer le CrossFit à Saint-Pierre-de-Chandieu : box locale Chistera, accueil débutants, première séance. Guide pratique.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-saint-pierre-de-chandieu",
    title: "CrossFit enfants Saint-Pierre-de-Chandieu",
    description: "CrossFit Kids à Saint-Pierre-de-Chandieu : programme adapté dans votre box locale Chistera. Philosophie, sécurité, bénéfices.",
    h1: "Le CrossFit pour enfants à Saint-Pierre-de-Chandieu : programme dans votre commune",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Saint-Pierre-de-Chandieu",
    excerpt: "CrossFit Kids à Saint-Pierre-de-Chandieu : programme adapté dans votre box locale Chistera. Philosophie, sécurité, bénéfices.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-saint-pierre-de-chandieu",
    title: "CrossFit senior Saint-Pierre-de-Chandieu",
    description: "CrossFit pour seniors à Saint-Pierre-de-Chandieu : adaptations, bénéfices santé, box locale Chistera. Guide pratique.",
    h1: "Le CrossFit après 60 ans à Saint-Pierre-de-Chandieu : préserver activement sa santé",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · CrossFit senior", breadcrumbLabel: "CrossFit senior Saint-Pierre-de-Chandieu",
    excerpt: "CrossFit pour seniors à Saint-Pierre-de-Chandieu : adaptations, bénéfices santé, box locale Chistera. Guide pratique.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-saint-pierre-de-chandieu",
    title: "Sport santé Saint-Pierre-de-Chandieu : APA — Chistera",
    description: "Sport santé à Saint-Pierre-de-Chandieu : activité physique adaptée, prescription, accompagnement. Box locale Chistera.",
    h1: "Le sport santé à Saint-Pierre-de-Chandieu : activité adaptée dans votre commune",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · Sport santé", breadcrumbLabel: "Sport santé Saint-Pierre-de-Chandieu",
    excerpt: "Sport santé à Saint-Pierre-de-Chandieu : activité physique adaptée, prescription, accompagnement. Box locale Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-saint-pierre-de-chandieu",
    title: "Callisthénie Saint-Pierre-de-Chandieu : pratique — Chistera",
    description: "La callisthénie à Saint-Pierre-de-Chandieu : pratique en extérieur, salle locale, progressions. Guide CrossFit Chistera.",
    h1: "La callisthénie à Saint-Pierre-de-Chandieu : pratique locale et progressions",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · Callisthénie", breadcrumbLabel: "Callisthénie Saint-Pierre-de-Chandieu",
    excerpt: "La callisthénie à Saint-Pierre-de-Chandieu : pratique en extérieur, salle locale, progressions. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-saint-pierre-de-chandieu",
    title: "Hybrid training Saint-Pierre-de-Chandieu",
    description: "L'hybrid training à Saint-Pierre-de-Chandieu : force et endurance, structure de semaine, coaching local. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Saint-Pierre-de-Chandieu : combiner force et endurance",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · Hybrid training", breadcrumbLabel: "Hybrid training Saint-Pierre-de-Chandieu",
    excerpt: "L'hybrid training à Saint-Pierre-de-Chandieu : force et endurance, structure de semaine, coaching local. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-saint-pierre-de-chandieu",
    title: "Pilates Saint-Pierre-de-Chandieu : pratique — Chistera",
    description: "Le pilates à Saint-Pierre-de-Chandieu : principes, bénéfices, options locales, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Saint-Pierre-de-Chandieu : panorama et complémentarité",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · Pilates", breadcrumbLabel: "Pilates Saint-Pierre-de-Chandieu",
    excerpt: "Le pilates à Saint-Pierre-de-Chandieu : principes, bénéfices, options locales, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-saint-pierre-de-chandieu",
    title: "Crosstraining Saint-Pierre-de-Chandieu : choisir — Chistera",
    description: "Crosstraining à Saint-Pierre-de-Chandieu : différences avec CrossFit, box affiliée locale. Guide CrossFit Chistera.",
    h1: "Crosstraining à Saint-Pierre-de-Chandieu : comprendre les différences",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · Crosstraining", breadcrumbLabel: "Crosstraining Saint-Pierre-de-Chandieu",
    excerpt: "Crosstraining à Saint-Pierre-de-Chandieu : différences avec CrossFit, box affiliée locale. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-saint-pierre-de-chandieu",
    title: "ATHX Saint-Pierre-de-Chandieu : conditioning — Chistera",
    description: "L'ATHX à Saint-Pierre-de-Chandieu : format de cours accessible, complémentarité CrossFit. Présentation CrossFit Chistera.",
    h1: "L'ATHX à Saint-Pierre-de-Chandieu : conditioning accessible",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · ATHX", breadcrumbLabel: "ATHX Saint-Pierre-de-Chandieu",
    excerpt: "L'ATHX à Saint-Pierre-de-Chandieu : format de cours accessible, complémentarité CrossFit. Présentation CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-saint-pierre-de-chandieu",
    title: "Hyrox Saint-Pierre-de-Chandieu : préparation — Chistera",
    description: "Préparer Hyrox à Saint-Pierre-de-Chandieu : format, programme, coaching dans votre box locale. Guide CrossFit Chistera.",
    h1: "Hyrox à Saint-Pierre-de-Chandieu : préparer la course localement",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · Hyrox", breadcrumbLabel: "Hyrox Saint-Pierre-de-Chandieu",
    excerpt: "Préparer Hyrox à Saint-Pierre-de-Chandieu : format, programme, coaching dans votre box locale. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-saint-pierre-de-chandieu",
    title: "Préparation physique Saint-Pierre-de-Chandieu",
    description: "Préparation physique à Saint-Pierre-de-Chandieu : sportifs amateurs, performance et prévention dans votre box locale. Guide CrossFit Chistera.",
    h1: "La préparation physique à Saint-Pierre-de-Chandieu : performance et prévention",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · Préparation physique", breadcrumbLabel: "Préparation physique Saint-Pierre-de-Chandieu",
    excerpt: "Préparation physique à Saint-Pierre-de-Chandieu : sportifs amateurs, performance et prévention dans votre box locale. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-saint-pierre-de-chandieu",
    title: "Remise en forme Saint-Pierre-de-Chandieu",
    description: "Remise en forme à Saint-Pierre-de-Chandieu : méthode progressive, box locale. Guide pratique CrossFit Chistera.",
    h1: "Remise en forme à Saint-Pierre-de-Chandieu : reprendre sans se décourager",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · Remise en forme", breadcrumbLabel: "Remise en forme Saint-Pierre-de-Chandieu",
    excerpt: "Remise en forme à Saint-Pierre-de-Chandieu : méthode progressive, box locale. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-saint-pierre-de-chandieu",
    title: "Perte de poids Saint-Pierre-de-Chandieu : méthode — Chistera",
    description: "Perte de poids durable à Saint-Pierre-de-Chandieu : rôle du sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à Saint-Pierre-de-Chandieu : ce que le sport apporte vraiment",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Pierre-de-Chandieu · Perte de poids", breadcrumbLabel: "Perte de poids Saint-Pierre-de-Chandieu",
    excerpt: "Perte de poids durable à Saint-Pierre-de-Chandieu : rôle du sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-saint-bonnet-de-mure",
    title: "Salle de sport Saint-Bonnet-de-Mure : choisir — Chistera",
    description: "Salle de sport à Saint-Bonnet-de-Mure : tissu local, alternatives voisines, critères de choix. Guide pratique CrossFit Chistera.",
    h1: "Salle de sport à Saint-Bonnet-de-Mure : tissu local et options de proximité",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · Salle de sport", breadcrumbLabel: "Salle de sport Saint-Bonnet-de-Mure",
    excerpt: "Salle de sport à Saint-Bonnet-de-Mure : tissu local, alternatives voisines, critères de choix. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-saint-bonnet-de-mure",
    title: "Salle de fitness Saint-Bonnet-de-Mure",
    description: "Salle de fitness à Saint-Bonnet-de-Mure : profils adaptés, alternatives. Guide pratique CrossFit Chistera.",
    h1: "Salle de fitness pour les Saint-Bonnetois : quand le modèle convient",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · Salle de fitness", breadcrumbLabel: "Salle de fitness Saint-Bonnet-de-Mure",
    excerpt: "Salle de fitness à Saint-Bonnet-de-Mure : profils adaptés, alternatives. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-saint-bonnet-de-mure",
    title: "CrossFit débutant Saint-Bonnet-de-Mure : démarrer — Chistera",
    description: "Démarrer le CrossFit à Saint-Bonnet-de-Mure : box voisine à 8 minutes, accueil débutants. Guide pratique CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Saint-Bonnetois : guide pour les vrais débutants",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Saint-Bonnet-de-Mure",
    excerpt: "Démarrer le CrossFit à Saint-Bonnet-de-Mure : box voisine à 8 minutes, accueil débutants. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-saint-bonnet-de-mure",
    title: "CrossFit enfants Saint-Bonnet-de-Mure",
    description: "CrossFit pour enfants à Saint-Bonnet-de-Mure : programme structuré à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Saint-Bonnet-de-Mure",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Saint-Bonnet-de-Mure",
    excerpt: "CrossFit pour enfants à Saint-Bonnet-de-Mure : programme structuré à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-saint-bonnet-de-mure",
    title: "CrossFit senior Saint-Bonnet-de-Mure : adapté — Chistera",
    description: "CrossFit pour seniors à Saint-Bonnet-de-Mure : adaptations, bénéfices santé, box voisine. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Saint-Bonnet-de-Mure : préserver sa santé",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · CrossFit senior", breadcrumbLabel: "CrossFit senior Saint-Bonnet-de-Mure",
    excerpt: "CrossFit pour seniors à Saint-Bonnet-de-Mure : adaptations, bénéfices santé, box voisine. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-saint-bonnet-de-mure",
    title: "Sport santé Saint-Bonnet-de-Mure : APA — Chistera",
    description: "Sport santé à Saint-Bonnet-de-Mure : activité adaptée, prescription, accompagnement local. Guide CrossFit Chistera.",
    h1: "Le sport santé à Saint-Bonnet-de-Mure : pratique adaptée et accompagnement",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · Sport santé", breadcrumbLabel: "Sport santé Saint-Bonnet-de-Mure",
    excerpt: "Sport santé à Saint-Bonnet-de-Mure : activité adaptée, prescription, accompagnement local. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-saint-bonnet-de-mure",
    title: "Callisthénie Saint-Bonnet-de-Mure : pratique — Chistera",
    description: "La callisthénie à Saint-Bonnet-de-Mure : extérieur, salle, progressions. Guide CrossFit Chistera.",
    h1: "La callisthénie pour les Saint-Bonnetois : pratique extérieure et progression encadrée",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · Callisthénie", breadcrumbLabel: "Callisthénie Saint-Bonnet-de-Mure",
    excerpt: "La callisthénie à Saint-Bonnet-de-Mure : extérieur, salle, progressions. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-saint-bonnet-de-mure",
    title: "Hybrid training Saint-Bonnet-de-Mure : combiner — Chistera",
    description: "L'hybrid training à Saint-Bonnet-de-Mure : force et endurance, structure de semaine. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Saint-Bonnet-de-Mure : équilibrer force et endurance",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · Hybrid training", breadcrumbLabel: "Hybrid training Saint-Bonnet-de-Mure",
    excerpt: "L'hybrid training à Saint-Bonnet-de-Mure : force et endurance, structure de semaine. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-saint-bonnet-de-mure",
    title: "Pilates Saint-Bonnet-de-Mure : pratique — Chistera",
    description: "Le pilates à Saint-Bonnet-de-Mure : principes, bénéfices, options locales, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Saint-Bonnet-de-Mure : renforcement profond",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · Pilates", breadcrumbLabel: "Pilates Saint-Bonnet-de-Mure",
    excerpt: "Le pilates à Saint-Bonnet-de-Mure : principes, bénéfices, options locales, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-saint-bonnet-de-mure",
    title: "Crosstraining Saint-Bonnet-de-Mure : choisir — Chistera",
    description: "Crosstraining à Saint-Bonnet-de-Mure : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    h1: "Crosstraining à Saint-Bonnet-de-Mure : comprendre les différences",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · Crosstraining", breadcrumbLabel: "Crosstraining Saint-Bonnet-de-Mure",
    excerpt: "Crosstraining à Saint-Bonnet-de-Mure : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-saint-bonnet-de-mure",
    title: "ATHX Saint-Bonnet-de-Mure : conditioning — Chistera",
    description: "L'ATHX à Saint-Bonnet-de-Mure : format de cours accessible, complémentarité CrossFit. Présentation CrossFit Chistera.",
    h1: "L'ATHX à Saint-Bonnet-de-Mure : conditioning accessible et exigeant",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · ATHX", breadcrumbLabel: "ATHX Saint-Bonnet-de-Mure",
    excerpt: "L'ATHX à Saint-Bonnet-de-Mure : format de cours accessible, complémentarité CrossFit. Présentation CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-saint-bonnet-de-mure",
    title: "Hyrox Saint-Bonnet-de-Mure : préparation — Chistera",
    description: "Préparer Hyrox quand on habite Saint-Bonnet-de-Mure : format, programme, coaching voisin. Guide CrossFit Chistera.",
    h1: "Hyrox quand on habite Saint-Bonnet-de-Mure : préparer la course",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · Hyrox", breadcrumbLabel: "Hyrox Saint-Bonnet-de-Mure",
    excerpt: "Préparer Hyrox quand on habite Saint-Bonnet-de-Mure : format, programme, coaching voisin. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-saint-bonnet-de-mure",
    title: "Préparation physique Saint-Bonnet-de-Mure",
    description: "Préparation physique à Saint-Bonnet-de-Mure : sportifs amateurs, performance et prévention. Guide CrossFit Chistera.",
    h1: "La préparation physique à Saint-Bonnet-de-Mure : performance et prévention",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · Préparation physique", breadcrumbLabel: "Préparation physique Saint-Bonnet-de-Mure",
    excerpt: "Préparation physique à Saint-Bonnet-de-Mure : sportifs amateurs, performance et prévention. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-saint-bonnet-de-mure",
    title: "Remise en forme Saint-Bonnet-de-Mure : reprendre — Chistera",
    description: "Remise en forme à Saint-Bonnet-de-Mure : méthode progressive, conseils pratiques. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Saint-Bonnetois : reprendre sans se décourager",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · Remise en forme", breadcrumbLabel: "Remise en forme Saint-Bonnet-de-Mure",
    excerpt: "Remise en forme à Saint-Bonnet-de-Mure : méthode progressive, conseils pratiques. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-saint-bonnet-de-mure",
    title: "Perte de poids Saint-Bonnet-de-Mure : méthode — Chistera",
    description: "Perte de poids durable à Saint-Bonnet-de-Mure : sport, nutrition. Guide honnête CrossFit Chistera.",
    h1: "Perte de poids à Saint-Bonnet-de-Mure : ce que le sport apporte vraiment",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Bonnet-de-Mure · Perte de poids", breadcrumbLabel: "Perte de poids Saint-Bonnet-de-Mure",
    excerpt: "Perte de poids durable à Saint-Bonnet-de-Mure : sport, nutrition. Guide honnête CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-saint-laurent-de-mure",
    title: "Salle de sport Saint-Laurent-de-Mure : choisir — Chistera",
    description: "Salle de sport à Saint-Laurent-de-Mure : tissu local, alternatives en communes voisines. Guide pratique CrossFit Chistera.",
    h1: "Salle de sport à Saint-Laurent-de-Mure : tissu local et options de proximité",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · Salle de sport", breadcrumbLabel: "Salle de sport Saint-Laurent-de-Mure",
    excerpt: "Salle de sport à Saint-Laurent-de-Mure : tissu local, alternatives en communes voisines. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-saint-laurent-de-mure",
    title: "Salle de fitness Saint-Laurent-de-Mure",
    description: "Salle de fitness pour les Saint-Laurentins : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    h1: "Salle de fitness pour les Saint-Laurentins : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · Salle de fitness", breadcrumbLabel: "Salle de fitness Saint-Laurent-de-Mure",
    excerpt: "Salle de fitness pour les Saint-Laurentins : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-saint-laurent-de-mure",
    title: "CrossFit débutant Saint-Laurent-de-Mure",
    description: "Démarrer le CrossFit à Saint-Laurent-de-Mure : box voisine, accueil débutants. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Saint-Laurentins : guide pour les vrais débutants",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Saint-Laurent-de-Mure",
    excerpt: "Démarrer le CrossFit à Saint-Laurent-de-Mure : box voisine, accueil débutants. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-saint-laurent-de-mure",
    title: "CrossFit enfants Saint-Laurent-de-Mure",
    description: "CrossFit pour enfants à Saint-Laurent-de-Mure : programme adapté à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Saint-Laurent-de-Mure",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Saint-Laurent-de-Mure",
    excerpt: "CrossFit pour enfants à Saint-Laurent-de-Mure : programme adapté à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-saint-laurent-de-mure",
    title: "CrossFit senior Saint-Laurent-de-Mure : adapté — Chistera",
    description: "CrossFit pour seniors à Saint-Laurent-de-Mure : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Saint-Laurent-de-Mure",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · CrossFit senior", breadcrumbLabel: "CrossFit senior Saint-Laurent-de-Mure",
    excerpt: "CrossFit pour seniors à Saint-Laurent-de-Mure : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-saint-laurent-de-mure",
    title: "Sport santé Saint-Laurent-de-Mure : APA — Chistera",
    description: "Sport santé à Saint-Laurent-de-Mure : activité adaptée, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Saint-Laurent-de-Mure : pratique adaptée",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · Sport santé", breadcrumbLabel: "Sport santé Saint-Laurent-de-Mure",
    excerpt: "Sport santé à Saint-Laurent-de-Mure : activité adaptée, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-saint-laurent-de-mure",
    title: "Callisthénie Saint-Laurent-de-Mure : pratique — Chistera",
    description: "La callisthénie à Saint-Laurent-de-Mure : extérieur, salle, progressions. Guide CrossFit Chistera.",
    h1: "La callisthénie à Saint-Laurent-de-Mure : pratique au poids du corps",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · Callisthénie", breadcrumbLabel: "Callisthénie Saint-Laurent-de-Mure",
    excerpt: "La callisthénie à Saint-Laurent-de-Mure : extérieur, salle, progressions. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-saint-laurent-de-mure",
    title: "Hybrid training Saint-Laurent-de-Mure : combiner — Chistera",
    description: "L'hybrid training à Saint-Laurent-de-Mure : force et endurance, structure de semaine. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Saint-Laurent-de-Mure : combiner force et endurance",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · Hybrid training", breadcrumbLabel: "Hybrid training Saint-Laurent-de-Mure",
    excerpt: "L'hybrid training à Saint-Laurent-de-Mure : force et endurance, structure de semaine. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-saint-laurent-de-mure",
    title: "Pilates Saint-Laurent-de-Mure : pratique — Chistera",
    description: "Le pilates à Saint-Laurent-de-Mure : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Saint-Laurent-de-Mure : panorama et complémentarité",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · Pilates", breadcrumbLabel: "Pilates Saint-Laurent-de-Mure",
    excerpt: "Le pilates à Saint-Laurent-de-Mure : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-saint-laurent-de-mure",
    title: "Crosstraining Saint-Laurent-de-Mure : choisir — Chistera",
    description: "Crosstraining à Saint-Laurent-de-Mure : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    h1: "Crosstraining à Saint-Laurent-de-Mure : comprendre les différences",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · Crosstraining", breadcrumbLabel: "Crosstraining Saint-Laurent-de-Mure",
    excerpt: "Crosstraining à Saint-Laurent-de-Mure : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-saint-laurent-de-mure",
    title: "ATHX Saint-Laurent-de-Mure : conditioning — Chistera",
    description: "L'ATHX à Saint-Laurent-de-Mure : format accessible, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "L'ATHX à Saint-Laurent-de-Mure : conditioning accessible",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · ATHX", breadcrumbLabel: "ATHX Saint-Laurent-de-Mure",
    excerpt: "L'ATHX à Saint-Laurent-de-Mure : format accessible, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-saint-laurent-de-mure",
    title: "Hyrox Saint-Laurent-de-Mure : préparation — Chistera",
    description: "Préparer Hyrox à Saint-Laurent-de-Mure : format, programme, coaching voisin. Guide CrossFit Chistera.",
    h1: "Hyrox quand on habite Saint-Laurent-de-Mure",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · Hyrox", breadcrumbLabel: "Hyrox Saint-Laurent-de-Mure",
    excerpt: "Préparer Hyrox à Saint-Laurent-de-Mure : format, programme, coaching voisin. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-saint-laurent-de-mure",
    title: "Préparation physique Saint-Laurent-de-Mure",
    description: "Préparation physique à Saint-Laurent-de-Mure : sportifs amateurs, performance, prévention. Guide CrossFit Chistera.",
    h1: "La préparation physique à Saint-Laurent-de-Mure : performance et prévention",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · Préparation physique", breadcrumbLabel: "Préparation physique Saint-Laurent-de-Mure",
    excerpt: "Préparation physique à Saint-Laurent-de-Mure : sportifs amateurs, performance, prévention. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-saint-laurent-de-mure",
    title: "Remise en forme Saint-Laurent-de-Mure : reprendre — Chistera",
    description: "Remise en forme à Saint-Laurent-de-Mure : méthode progressive, conseils pratiques. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Saint-Laurentins : reprendre sans se décourager",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · Remise en forme", breadcrumbLabel: "Remise en forme Saint-Laurent-de-Mure",
    excerpt: "Remise en forme à Saint-Laurent-de-Mure : méthode progressive, conseils pratiques. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-saint-laurent-de-mure",
    title: "Perte de poids Saint-Laurent-de-Mure : méthode — Chistera",
    description: "Perte de poids durable à Saint-Laurent-de-Mure : sport, nutrition. Guide honnête CrossFit Chistera.",
    h1: "Perte de poids à Saint-Laurent-de-Mure : ce que le sport apporte vraiment",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Laurent-de-Mure · Perte de poids", breadcrumbLabel: "Perte de poids Saint-Laurent-de-Mure",
    excerpt: "Perte de poids durable à Saint-Laurent-de-Mure : sport, nutrition. Guide honnête CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-marennes",
    title: "Salle de sport Marennes — Chistera",
    description: "Salle de sport à Marennes : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    h1: "Salle de sport pour les Marennais : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · Salle de sport", breadcrumbLabel: "Salle de sport Marennes",
    excerpt: "Salle de sport à Marennes : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-marennes",
    title: "Salle de fitness Marennes — Chistera",
    description: "Salle de fitness pour les Marennais : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    h1: "Salle de fitness pour les Marennais : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · Salle de fitness", breadcrumbLabel: "Salle de fitness Marennes",
    excerpt: "Salle de fitness pour les Marennais : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-marennes",
    title: "CrossFit débutant Marennes — Chistera",
    description: "Démarrer le CrossFit à Marennes : box voisine à 8 minutes. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Marennais",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Marennes",
    excerpt: "Démarrer le CrossFit à Marennes : box voisine à 8 minutes. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-marennes",
    title: "CrossFit enfants Marennes — Chistera",
    description: "CrossFit pour enfants à Marennes : programme adapté à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Marennes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Marennes",
    excerpt: "CrossFit pour enfants à Marennes : programme adapté à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-marennes",
    title: "CrossFit senior Marennes — Chistera",
    description: "CrossFit pour seniors à Marennes : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Marennes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · CrossFit senior", breadcrumbLabel: "CrossFit senior Marennes",
    excerpt: "CrossFit pour seniors à Marennes : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-marennes",
    title: "Sport santé Marennes — Chistera",
    description: "Sport santé à Marennes : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Marennes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · Sport santé", breadcrumbLabel: "Sport santé Marennes",
    excerpt: "Sport santé à Marennes : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-marennes",
    title: "Callisthénie Marennes — Chistera",
    description: "La callisthénie à Marennes : pratique extérieure, salle. Guide CrossFit Chistera.",
    h1: "La callisthénie à Marennes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · Callisthénie", breadcrumbLabel: "Callisthénie Marennes",
    excerpt: "La callisthénie à Marennes : pratique extérieure, salle. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-marennes",
    title: "Hybrid training Marennes — Chistera",
    description: "L'hybrid training à Marennes : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Marennes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · Hybrid Training", breadcrumbLabel: "Hybrid Training Marennes",
    excerpt: "L'hybrid training à Marennes : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-marennes",
    title: "Pilates Marennes — Chistera",
    description: "Le pilates à Marennes : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Marennes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · Pilates", breadcrumbLabel: "Pilates Marennes",
    excerpt: "Le pilates à Marennes : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-marennes",
    title: "Crosstraining Marennes — Chistera",
    description: "Crosstraining à Marennes : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    h1: "Crosstraining à Marennes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · Cross-training", breadcrumbLabel: "Cross-training Marennes",
    excerpt: "Crosstraining à Marennes : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-marennes",
    title: "ATHX Marennes — Chistera",
    description: "L'ATHX à Marennes : format accessible. Guide CrossFit Chistera.",
    h1: "L'ATHX à Marennes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · Athx", breadcrumbLabel: "Athx Marennes",
    excerpt: "L'ATHX à Marennes : format accessible. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-marennes",
    title: "Hyrox Marennes — Chistera",
    description: "Préparer Hyrox à Marennes : format, programme. Guide CrossFit Chistera.",
    h1: "Hyrox à Marennes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · Hyrox", breadcrumbLabel: "Hyrox Marennes",
    excerpt: "Préparer Hyrox à Marennes : format, programme. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-marennes",
    title: "Préparation physique Marennes — Chistera",
    description: "Préparation physique à Marennes : sportifs amateurs. Guide CrossFit Chistera.",
    h1: "La préparation physique à Marennes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · Préparation physique", breadcrumbLabel: "Préparation physique Marennes",
    excerpt: "Préparation physique à Marennes : sportifs amateurs. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-marennes",
    title: "Remise en forme Marennes — Chistera",
    description: "Remise en forme à Marennes : méthode progressive. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Marennais",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · Remise en forme", breadcrumbLabel: "Remise en forme Marennes",
    excerpt: "Remise en forme à Marennes : méthode progressive. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-marennes",
    title: "Perte de poids Marennes — Chistera",
    description: "Perte de poids durable à Marennes : sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à Marennes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Marennes · Perte de poids", breadcrumbLabel: "Perte de poids Marennes",
    excerpt: "Perte de poids durable à Marennes : sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-saint-just-chaleyssin",
    title: "Salle de sport Saint-Just-Chaleyssin — Chistera",
    description: "Salle de sport à Saint-Just-Chaleyssin : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    h1: "Salle de sport pour les Saint-Justois : alternatives en proximité",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · Salle de sport", breadcrumbLabel: "Salle de sport Saint-Just-Chaleyssin",
    excerpt: "Salle de sport à Saint-Just-Chaleyssin : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-saint-just-chaleyssin",
    title: "Salle de fitness Saint-Just-Chaleyssin — Chistera",
    description: "Salle de fitness pour les Saint-Justois : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    h1: "Salle de fitness pour les Saint-Justois : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · Salle de fitness", breadcrumbLabel: "Salle de fitness Saint-Just-Chaleyssin",
    excerpt: "Salle de fitness pour les Saint-Justois : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-saint-just-chaleyssin",
    title: "CrossFit débutant Saint-Just-Chaleyssin — Chistera",
    description: "Démarrer le CrossFit à Saint-Just-Chaleyssin : box voisine, accueil débutants. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Saint-Justois",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Saint-Just-Chaleyssin",
    excerpt: "Démarrer le CrossFit à Saint-Just-Chaleyssin : box voisine, accueil débutants. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-saint-just-chaleyssin",
    title: "CrossFit enfants Saint-Just-Chaleyssin — Chistera",
    description: "CrossFit pour enfants à Saint-Just-Chaleyssin : programme adapté à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Saint-Just-Chaleyssin",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Saint-Just-Chaleyssin",
    excerpt: "CrossFit pour enfants à Saint-Just-Chaleyssin : programme adapté à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-saint-just-chaleyssin",
    title: "CrossFit senior Saint-Just-Chaleyssin — Chistera",
    description: "CrossFit pour seniors à Saint-Just-Chaleyssin : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Saint-Just-Chaleyssin",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · CrossFit senior", breadcrumbLabel: "CrossFit senior Saint-Just-Chaleyssin",
    excerpt: "CrossFit pour seniors à Saint-Just-Chaleyssin : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-saint-just-chaleyssin",
    title: "Sport santé Saint-Just-Chaleyssin — Chistera",
    description: "Sport santé à Saint-Just-Chaleyssin : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Saint-Just-Chaleyssin",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · Sport santé", breadcrumbLabel: "Sport santé Saint-Just-Chaleyssin",
    excerpt: "Sport santé à Saint-Just-Chaleyssin : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-saint-just-chaleyssin",
    title: "Callisthénie Saint-Just-Chaleyssin — Chistera",
    description: "La callisthénie à Saint-Just-Chaleyssin : pratique extérieure, salle. Guide CrossFit Chistera.",
    h1: "La callisthénie à Saint-Just-Chaleyssin",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · Callisthénie", breadcrumbLabel: "Callisthénie Saint-Just-Chaleyssin",
    excerpt: "La callisthénie à Saint-Just-Chaleyssin : pratique extérieure, salle. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-saint-just-chaleyssin",
    title: "Hybrid training Saint-Just-Chaleyssin — Chistera",
    description: "L'hybrid training à Saint-Just-Chaleyssin : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Saint-Just-Chaleyssin",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · Hybrid Training", breadcrumbLabel: "Hybrid Training Saint-Just-Chaleyssin",
    excerpt: "L'hybrid training à Saint-Just-Chaleyssin : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-saint-just-chaleyssin",
    title: "Pilates Saint-Just-Chaleyssin — Chistera",
    description: "Le pilates à Saint-Just-Chaleyssin : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Saint-Just-Chaleyssin",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · Pilates", breadcrumbLabel: "Pilates Saint-Just-Chaleyssin",
    excerpt: "Le pilates à Saint-Just-Chaleyssin : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-saint-just-chaleyssin",
    title: "Crosstraining Saint-Just-Chaleyssin — Chistera",
    description: "Crosstraining à Saint-Just-Chaleyssin : différences avec CrossFit. Guide CrossFit Chistera.",
    h1: "Crosstraining à Saint-Just-Chaleyssin",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · Cross-training", breadcrumbLabel: "Cross-training Saint-Just-Chaleyssin",
    excerpt: "Crosstraining à Saint-Just-Chaleyssin : différences avec CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-saint-just-chaleyssin",
    title: "ATHX Saint-Just-Chaleyssin — Chistera",
    description: "L'ATHX à Saint-Just-Chaleyssin : format accessible. Guide CrossFit Chistera.",
    h1: "L'ATHX à Saint-Just-Chaleyssin",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · Athx", breadcrumbLabel: "Athx Saint-Just-Chaleyssin",
    excerpt: "L'ATHX à Saint-Just-Chaleyssin : format accessible. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-saint-just-chaleyssin",
    title: "Hyrox Saint-Just-Chaleyssin — Chistera",
    description: "Préparer Hyrox à Saint-Just-Chaleyssin : format, programme. Guide CrossFit Chistera.",
    h1: "Hyrox à Saint-Just-Chaleyssin",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · Hyrox", breadcrumbLabel: "Hyrox Saint-Just-Chaleyssin",
    excerpt: "Préparer Hyrox à Saint-Just-Chaleyssin : format, programme. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-saint-just-chaleyssin",
    title: "Préparation physique Saint-Just-Chaleyssin — Chistera",
    description: "Préparation physique à Saint-Just-Chaleyssin : sportifs amateurs. Guide CrossFit Chistera.",
    h1: "La préparation physique à Saint-Just-Chaleyssin",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · Préparation physique", breadcrumbLabel: "Préparation physique Saint-Just-Chaleyssin",
    excerpt: "Préparation physique à Saint-Just-Chaleyssin : sportifs amateurs. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-saint-just-chaleyssin",
    title: "Remise en forme Saint-Just-Chaleyssin — Chistera",
    description: "Remise en forme à Saint-Just-Chaleyssin : méthode progressive. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Saint-Justois",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · Remise en forme", breadcrumbLabel: "Remise en forme Saint-Just-Chaleyssin",
    excerpt: "Remise en forme à Saint-Just-Chaleyssin : méthode progressive. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-saint-just-chaleyssin",
    title: "Perte de poids Saint-Just-Chaleyssin — Chistera",
    description: "Perte de poids durable à Saint-Just-Chaleyssin : sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à Saint-Just-Chaleyssin",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Just-Chaleyssin · Perte de poids", breadcrumbLabel: "Perte de poids Saint-Just-Chaleyssin",
    excerpt: "Perte de poids durable à Saint-Just-Chaleyssin : sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-heyrieux",
    title: "Salle de sport Heyrieux : options — Chistera",
    description: "Salle de sport à Heyrieux : tissu local, alternatives en communes voisines. Guide pratique CrossFit Chistera.",
    h1: "Salle de sport à Heyrieux : tissu local et options de proximité",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · Salle de sport", breadcrumbLabel: "Salle de sport Heyrieux",
    excerpt: "Salle de sport à Heyrieux : tissu local, alternatives en communes voisines. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-heyrieux",
    title: "Salle de fitness Heyrieux : alternatives — Chistera",
    description: "Salle de fitness pour les Heyrieusins : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    h1: "Salle de fitness pour les Heyrieusins : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · Salle de fitness", breadcrumbLabel: "Salle de fitness Heyrieux",
    excerpt: "Salle de fitness pour les Heyrieusins : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-heyrieux",
    title: "CrossFit débutant Heyrieux — Chistera",
    description: "Démarrer le CrossFit à Heyrieux : box voisine, accueil débutants. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Heyrieusins : guide pour vrais débutants",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Heyrieux",
    excerpt: "Démarrer le CrossFit à Heyrieux : box voisine, accueil débutants. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-heyrieux",
    title: "CrossFit enfants Heyrieux — Chistera",
    description: "CrossFit pour enfants à Heyrieux : programme à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Heyrieux",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Heyrieux",
    excerpt: "CrossFit pour enfants à Heyrieux : programme à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-heyrieux",
    title: "CrossFit senior Heyrieux — Chistera",
    description: "CrossFit pour seniors à Heyrieux : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Heyrieux",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · CrossFit senior", breadcrumbLabel: "CrossFit senior Heyrieux",
    excerpt: "CrossFit pour seniors à Heyrieux : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-heyrieux",
    title: "Sport santé Heyrieux — Chistera",
    description: "Sport santé à Heyrieux : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Heyrieux : pratique adaptée",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · Sport santé", breadcrumbLabel: "Sport santé Heyrieux",
    excerpt: "Sport santé à Heyrieux : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-heyrieux",
    title: "Callisthénie Heyrieux — Chistera",
    description: "La callisthénie à Heyrieux : pratique en extérieur, salle, progressions. Guide CrossFit Chistera.",
    h1: "La callisthénie pour les Heyrieusins",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · Callisthénie", breadcrumbLabel: "Callisthénie Heyrieux",
    excerpt: "La callisthénie à Heyrieux : pratique en extérieur, salle, progressions. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-heyrieux",
    title: "Hybrid training Heyrieux — Chistera",
    description: "L'hybrid training à Heyrieux : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Heyrieux : combiner force et endurance",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · Hybrid Training", breadcrumbLabel: "Hybrid Training Heyrieux",
    excerpt: "L'hybrid training à Heyrieux : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-heyrieux",
    title: "Pilates Heyrieux — Chistera",
    description: "Le pilates à Heyrieux : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Heyrieux : panorama et complémentarité",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · Pilates", breadcrumbLabel: "Pilates Heyrieux",
    excerpt: "Le pilates à Heyrieux : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-heyrieux",
    title: "Crosstraining Heyrieux — Chistera",
    description: "Crosstraining à Heyrieux : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    h1: "Crosstraining à Heyrieux : comprendre les différences",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · Cross-training", breadcrumbLabel: "Cross-training Heyrieux",
    excerpt: "Crosstraining à Heyrieux : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-heyrieux",
    title: "ATHX Heyrieux — Chistera",
    description: "L'ATHX à Heyrieux : format accessible, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "L'ATHX à Heyrieux : conditioning accessible",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · Athx", breadcrumbLabel: "Athx Heyrieux",
    excerpt: "L'ATHX à Heyrieux : format accessible, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-heyrieux",
    title: "Hyrox Heyrieux — Chistera",
    description: "Préparer Hyrox à Heyrieux : format, programme, coaching. Guide CrossFit Chistera.",
    h1: "Hyrox à Heyrieux : préparer la course",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · Hyrox", breadcrumbLabel: "Hyrox Heyrieux",
    excerpt: "Préparer Hyrox à Heyrieux : format, programme, coaching. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-heyrieux",
    title: "Préparation physique Heyrieux — Chistera",
    description: "Préparation physique à Heyrieux : sportifs amateurs, performance. Guide CrossFit Chistera.",
    h1: "La préparation physique à Heyrieux",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · Préparation physique", breadcrumbLabel: "Préparation physique Heyrieux",
    excerpt: "Préparation physique à Heyrieux : sportifs amateurs, performance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-heyrieux",
    title: "Remise en forme Heyrieux — Chistera",
    description: "Remise en forme à Heyrieux : méthode progressive. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Heyrieusins",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · Remise en forme", breadcrumbLabel: "Remise en forme Heyrieux",
    excerpt: "Remise en forme à Heyrieux : méthode progressive. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-heyrieux",
    title: "Perte de poids Heyrieux — Chistera",
    description: "Perte de poids durable à Heyrieux : sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à Heyrieux",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Heyrieux · Perte de poids", breadcrumbLabel: "Perte de poids Heyrieux",
    excerpt: "Perte de poids durable à Heyrieux : sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-saint-quentin-fallavier",
    title: "Salle de sport Saint-Quentin-Fallavier : options — Chistera",
    description: "Salle de sport à Saint-Quentin-Fallavier : offre liée à la zone logistique, alternatives. Guide CrossFit Chistera.",
    h1: "Salle de sport à Saint-Quentin-Fallavier : offre liée à la zone logistique",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · Salle de sport", breadcrumbLabel: "Salle de sport Saint-Quentin-Fallavier",
    excerpt: "Salle de sport à Saint-Quentin-Fallavier : offre liée à la zone logistique, alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-saint-quentin-fallavier",
    title: "Salle de fitness Saint-Quentin-Fallavier",
    description: "Salle de fitness à Saint-Quentin-Fallavier : offre dense liée à la zone d'activité. Guide CrossFit Chistera.",
    h1: "Salle de fitness à Saint-Quentin-Fallavier : modèle adapté à la zone d'activité",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · Salle de fitness", breadcrumbLabel: "Salle de fitness Saint-Quentin-Fallavier",
    excerpt: "Salle de fitness à Saint-Quentin-Fallavier : offre dense liée à la zone d'activité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-saint-quentin-fallavier",
    title: "CrossFit débutant Saint-Quentin-Fallavier — Chistera",
    description: "Démarrer le CrossFit à Saint-Quentin-Fallavier : déroulé d'une première séance, accueil débutants. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Saint-Quentinois : guide pour vrais débutants",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Saint-Quentin-Fallavier",
    excerpt: "Démarrer le CrossFit à Saint-Quentin-Fallavier : déroulé d'une première séance, accueil débutants. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-saint-quentin-fallavier",
    title: "CrossFit enfants Saint-Quentin-Fallavier — Chistera",
    description: "CrossFit pour enfants à Saint-Quentin-Fallavier : programme adapté en proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Saint-Quentin-Fallavier",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Saint-Quentin-Fallavier",
    excerpt: "CrossFit pour enfants à Saint-Quentin-Fallavier : programme adapté en proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-saint-quentin-fallavier",
    title: "CrossFit senior Saint-Quentin-Fallavier — Chistera",
    description: "CrossFit pour seniors à Saint-Quentin-Fallavier : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Saint-Quentin-Fallavier",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · CrossFit senior", breadcrumbLabel: "CrossFit senior Saint-Quentin-Fallavier",
    excerpt: "CrossFit pour seniors à Saint-Quentin-Fallavier : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-saint-quentin-fallavier",
    title: "Sport santé Saint-Quentin-Fallavier : APA — Chistera",
    description: "Sport santé à Saint-Quentin-Fallavier : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Saint-Quentin-Fallavier : pratique adaptée",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · Sport santé", breadcrumbLabel: "Sport santé Saint-Quentin-Fallavier",
    excerpt: "Sport santé à Saint-Quentin-Fallavier : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-saint-quentin-fallavier",
    title: "Callisthénie Saint-Quentin-Fallavier — Chistera",
    description: "La callisthénie à Saint-Quentin-Fallavier : pratique extérieure, salle, progressions. Guide CrossFit Chistera.",
    h1: "La callisthénie à Saint-Quentin-Fallavier : pratique au poids du corps",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · Callisthénie", breadcrumbLabel: "Callisthénie Saint-Quentin-Fallavier",
    excerpt: "La callisthénie à Saint-Quentin-Fallavier : pratique extérieure, salle, progressions. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-saint-quentin-fallavier",
    title: "Hybrid training Saint-Quentin-Fallavier — Chistera",
    description: "L'hybrid training à Saint-Quentin-Fallavier : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Saint-Quentin-Fallavier : force et endurance",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · Hybrid Training", breadcrumbLabel: "Hybrid Training Saint-Quentin-Fallavier",
    excerpt: "L'hybrid training à Saint-Quentin-Fallavier : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-saint-quentin-fallavier",
    title: "Pilates Saint-Quentin-Fallavier — Chistera",
    description: "Le pilates à Saint-Quentin-Fallavier : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Saint-Quentin-Fallavier : panorama et complémentarité",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · Pilates", breadcrumbLabel: "Pilates Saint-Quentin-Fallavier",
    excerpt: "Le pilates à Saint-Quentin-Fallavier : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-saint-quentin-fallavier",
    title: "Crosstraining Saint-Quentin-Fallavier — Chistera",
    description: "Crosstraining à Saint-Quentin-Fallavier : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    h1: "Crosstraining à Saint-Quentin-Fallavier : comprendre les différences",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · Cross-training", breadcrumbLabel: "Cross-training Saint-Quentin-Fallavier",
    excerpt: "Crosstraining à Saint-Quentin-Fallavier : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-saint-quentin-fallavier",
    title: "ATHX Saint-Quentin-Fallavier — Chistera",
    description: "L'ATHX à Saint-Quentin-Fallavier : format accessible, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "L'ATHX à Saint-Quentin-Fallavier : conditioning accessible",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · Athx", breadcrumbLabel: "Athx Saint-Quentin-Fallavier",
    excerpt: "L'ATHX à Saint-Quentin-Fallavier : format accessible, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-saint-quentin-fallavier",
    title: "Hyrox Saint-Quentin-Fallavier — Chistera",
    description: "Préparer Hyrox à Saint-Quentin-Fallavier : format, programme, coaching. Guide CrossFit Chistera.",
    h1: "Hyrox à Saint-Quentin-Fallavier : préparer la course",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · Hyrox", breadcrumbLabel: "Hyrox Saint-Quentin-Fallavier",
    excerpt: "Préparer Hyrox à Saint-Quentin-Fallavier : format, programme, coaching. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-saint-quentin-fallavier",
    title: "Préparation physique Saint-Quentin-Fallavier — Chistera",
    description: "Préparation physique à Saint-Quentin-Fallavier : sportifs amateurs, performance. Guide CrossFit Chistera.",
    h1: "La préparation physique à Saint-Quentin-Fallavier",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · Préparation physique", breadcrumbLabel: "Préparation physique Saint-Quentin-Fallavier",
    excerpt: "Préparation physique à Saint-Quentin-Fallavier : sportifs amateurs, performance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-saint-quentin-fallavier",
    title: "Remise en forme Saint-Quentin-Fallavier — Chistera",
    description: "Remise en forme à Saint-Quentin-Fallavier : méthode progressive. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Saint-Quentinois",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · Remise en forme", breadcrumbLabel: "Remise en forme Saint-Quentin-Fallavier",
    excerpt: "Remise en forme à Saint-Quentin-Fallavier : méthode progressive. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-saint-quentin-fallavier",
    title: "Perte de poids Saint-Quentin-Fallavier — Chistera",
    description: "Perte de poids à Saint-Quentin-Fallavier : sport, nutrition. Guide honnête CrossFit Chistera.",
    h1: "Perte de poids à Saint-Quentin-Fallavier",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Quentin-Fallavier · Perte de poids", breadcrumbLabel: "Perte de poids Saint-Quentin-Fallavier",
    excerpt: "Perte de poids à Saint-Quentin-Fallavier : sport, nutrition. Guide honnête CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-venissieux",
    title: "Salle de sport Vénissieux : marché dense — Chistera",
    description: "Salle de sport à Vénissieux : offre locale dense, critères de choix, alternatives en proximité. Guide pratique CrossFit Chistera.",
    h1: "Salle de sport à Vénissieux : faire le bon choix dans un marché dense",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · Salle de sport", breadcrumbLabel: "Salle de sport Vénissieux",
    excerpt: "Salle de sport à Vénissieux : offre locale dense, critères de choix, alternatives en proximité. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-venissieux",
    title: "Salle de fitness Vénissieux : offre dense — Chistera",
    description: "Salle de fitness à Vénissieux : modèle, profils adaptés, choisir dans un marché dense. Guide pratique CrossFit Chistera.",
    h1: "Salle de fitness à Vénissieux : pour qui ce modèle convient vraiment",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · Salle de fitness", breadcrumbLabel: "Salle de fitness Vénissieux",
    excerpt: "Salle de fitness à Vénissieux : modèle, profils adaptés, choisir dans un marché dense. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-venissieux",
    title: "CrossFit débutant Vénissieux : commencer — Chistera",
    description: "Démarrer le CrossFit à Vénissieux : déroulé d'une première séance, accueil débutants. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Vénissians : guide pour les vrais débutants",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Vénissieux",
    excerpt: "Démarrer le CrossFit à Vénissieux : déroulé d'une première séance, accueil débutants. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-venissieux",
    title: "CrossFit enfants Vénissieux : programme Kids — Chistera",
    description: "CrossFit pour enfants à Vénissieux : philosophie, sécurité, options en proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants à Vénissieux : panorama et options",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Vénissieux",
    excerpt: "CrossFit pour enfants à Vénissieux : philosophie, sécurité, options en proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-venissieux",
    title: "CrossFit senior Vénissieux : pratique adaptée — Chistera",
    description: "CrossFit pour seniors à Vénissieux : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Vénissieux : préserver activement sa santé",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · CrossFit senior", breadcrumbLabel: "CrossFit senior Vénissieux",
    excerpt: "CrossFit pour seniors à Vénissieux : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-venissieux",
    title: "Sport santé Vénissieux : APA et accompagnement — Chistera",
    description: "Sport santé à Vénissieux : activité physique adaptée, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Vénissieux : pratique adaptée et accompagnement",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · Sport santé", breadcrumbLabel: "Sport santé Vénissieux",
    excerpt: "Sport santé à Vénissieux : activité physique adaptée, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-venissieux",
    title: "Callisthénie Vénissieux : pratique urbaine — Chistera",
    description: "La callisthénie à Vénissieux : aires de fitness urbain, salles, progressions. Guide CrossFit Chistera.",
    h1: "La callisthénie à Vénissieux : pratique urbaine très développée",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · Callisthénie", breadcrumbLabel: "Callisthénie Vénissieux",
    excerpt: "La callisthénie à Vénissieux : aires de fitness urbain, salles, progressions. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-venissieux",
    title: "Hybrid training Vénissieux : combiner — Chistera",
    description: "L'hybrid training à Vénissieux : force et endurance, structure de semaine. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Vénissieux : équilibrer force et endurance",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · Hybrid Training", breadcrumbLabel: "Hybrid Training Vénissieux",
    excerpt: "L'hybrid training à Vénissieux : force et endurance, structure de semaine. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-venissieux",
    title: "Pilates Vénissieux : offre dense — Chistera",
    description: "Le pilates à Vénissieux : offre locale dense, principes, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Vénissieux : panorama d'une offre dense",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · Pilates", breadcrumbLabel: "Pilates Vénissieux",
    excerpt: "Le pilates à Vénissieux : offre locale dense, principes, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-venissieux",
    title: "Crosstraining Vénissieux : CrossFit ou indépendant",
    description: "Crosstraining à Vénissieux : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    h1: "Crosstraining à Vénissieux : comprendre les différences",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · Cross-training", breadcrumbLabel: "Cross-training Vénissieux",
    excerpt: "Crosstraining à Vénissieux : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-venissieux",
    title: "ATHX Vénissieux : conditioning — Chistera",
    description: "L'ATHX à Vénissieux : format accessible, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "L'ATHX à Vénissieux : conditioning accessible",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · Athx", breadcrumbLabel: "Athx Vénissieux",
    excerpt: "L'ATHX à Vénissieux : format accessible, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-venissieux",
    title: "Hyrox Vénissieux : préparation — Chistera",
    description: "Préparer Hyrox à Vénissieux : format, programme, coaching. Guide CrossFit Chistera.",
    h1: "Hyrox à Vénissieux : préparer la course efficacement",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · Hyrox", breadcrumbLabel: "Hyrox Vénissieux",
    excerpt: "Préparer Hyrox à Vénissieux : format, programme, coaching. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-venissieux",
    title: "Préparation physique Vénissieux : sportifs — Chistera",
    description: "Préparation physique à Vénissieux : sportifs amateurs, performance, prévention. Guide CrossFit Chistera.",
    h1: "La préparation physique à Vénissieux : performance et prévention",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · Préparation physique", breadcrumbLabel: "Préparation physique Vénissieux",
    excerpt: "Préparation physique à Vénissieux : sportifs amateurs, performance, prévention. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-venissieux",
    title: "Remise en forme Vénissieux : reprendre — Chistera",
    description: "Remise en forme à Vénissieux : méthode progressive, conseils. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Vénissians : reprendre sans se décourager",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · Remise en forme", breadcrumbLabel: "Remise en forme Vénissieux",
    excerpt: "Remise en forme à Vénissieux : méthode progressive, conseils. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-venissieux",
    title: "Perte de poids Vénissieux : méthode — Chistera",
    description: "Perte de poids durable à Vénissieux : sport, nutrition. Guide honnête CrossFit Chistera.",
    h1: "Perte de poids à Vénissieux : ce que le sport apporte vraiment",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Vénissieux · Perte de poids", breadcrumbLabel: "Perte de poids Vénissieux",
    excerpt: "Perte de poids durable à Vénissieux : sport, nutrition. Guide honnête CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-sport-sante-est-lyonnais",
    title: "Sport santé Est lyonnais | CrossFit",
    description: "Découvrez l'approche sport santé de CrossFit Chistera : remise en forme, coaching adapté, nutrition, prévention des blessures, Hyrox et accompagnement personnalisé.",
    h1: "Sport santé dans l'Est lyonnais : pourquoi CrossFit Chistera développe une approche globale de la santé, du mouvement et de la nutrition",
    publishedAt: "2026-05-14", readingMinutes: 9,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Est lyonnais · Sport santé", breadcrumbLabel: "Sport santé CrossFit Chistera",
    excerpt: "Approche globale sport santé : mouvement, prévention, nutrition, séminaires et coaching adapté.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "drop-in-crossfit-est-lyonnais",
    title: "Drop-in CrossFit Est lyonnais — Chistera",
    description: "Drop in CrossFit proche de Lyon, Saint-Priest, Chaponnay ou Bourgoin : découvrez CrossFit Chistera et réservez votre séance directement en ligne.",
    h1: "Drop in CrossFit dans l'Est lyonnais : comment s'entraîner chez CrossFit Chistera pendant un déplacement ou des vacances",
    publishedAt: "2026-05-14", readingMinutes: 7,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Est lyonnais · Drop in", breadcrumbLabel: "Drop in CrossFit Chistera",
    excerpt: "Box accueillante pour drop-in : réservation en ligne, parking, accès facile depuis Lyon et l'aéroport.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-athx-hybrid-training-est-lyonnais",
    title: "Hyrox, ATHX et Hybrid Training Est lyonnais",
    description: "Découvrez les entraînements Hyrox, ATHX, CrossFit et Hybrid Training proposés chez CrossFit Chistera dans l'Est lyonnais.",
    h1: "Hyrox, ATHX, CrossFit et Hybrid Training dans l'Est lyonnais : pourquoi CrossFit Chistera développe une préparation physique moderne et complète",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Est lyonnais · Hyrox & Hybrid", breadcrumbLabel: "Hyrox ATHX Hybrid Training",
    excerpt: "Préparation physique moderne et complète : CrossFit, Hyrox, ATHX, Hybrid Training, séminaires spécialisés.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "formation-coachs-crossfit-est-lyonnais",
    title: "Formation coach CrossFit Est lyonnais — Chistera",
    description: "Découvrez la formation des coachs de CrossFit Chistera : certifications CrossFit Level 2, sport santé, mobilité, nutrition, Hyrox et formation continue dans l'Est lyonnais.",
    h1: "Pourquoi la formation des coachs fait toute la différence chez CrossFit Chistera",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "methode", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Est lyonnais · Formation coachs", breadcrumbLabel: "Formation des coachs",
    excerpt: "Certifications CrossFit Level 2, formation continue, sport santé, séminaires et interventions de pros de santé.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "affiliates-battle-preparation-concours-est-lyonnais",
    title: "Affiliates Battle, CrossFit et préparation concours",
    description: "Découvrez comment CrossFit Chistera prépare ses athlètes aux Affiliates Battle, aux compétitions CrossFit et aux concours armée, police, gendarmerie, pompier et STAPS.",
    h1: "Affiliates Battle, compétitions CrossFit et préparation aux concours : comment CrossFit Chistera développe la performance physique dans l'Est lyonnais",
    publishedAt: "2026-05-14", readingMinutes: 10,
    cluster: "competition", ctaVariant: "competition", needsHealthDisclaimer: false,
    category: "Est lyonnais · Compétition & concours", breadcrumbLabel: "Affiliates Battle & concours",
    excerpt: "Compétitions CrossFit, Affiliates Battle et préparation aux concours armée, police, gendarmerie, pompier, STAPS.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-villefontaine",
    title: "CrossFit Villefontaine : panorama et options — Chistera",
    description: "Pratiquer le CrossFit à Villefontaine : panorama des box locales et alternatives en proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour les habitants de Villefontaine : panorama et alternatives",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Villefontaine · CrossFit", breadcrumbLabel: "CrossFit Villefontaine",
    excerpt: "Pratiquer le CrossFit à Villefontaine : panorama des box locales et alternatives en proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-villefontaine",
    title: "Salle de sport Villefontaine : choisir — Chistera",
    description: "Salle de sport à Villefontaine : offre locale développée, critères de choix, alternatives. Guide pratique CrossFit Chistera.",
    h1: "Salle de sport à Villefontaine : tissu local et options",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Villefontaine · Salle de sport", breadcrumbLabel: "Salle de sport Villefontaine",
    excerpt: "Salle de sport à Villefontaine : offre locale développée, critères de choix, alternatives. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-villefontaine",
    title: "Salle de fitness Villefontaine : offre dense — Chistera",
    description: "Salle de fitness à Villefontaine : offre développée, profils adaptés. Guide pratique CrossFit Chistera.",
    h1: "Salle de fitness à Villefontaine : pour qui ce modèle convient",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Villefontaine · Salle de fitness", breadcrumbLabel: "Salle de fitness Villefontaine",
    excerpt: "Salle de fitness à Villefontaine : offre développée, profils adaptés. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-villefontaine",
    title: "CrossFit débutant Villefontaine : commencer — Chistera",
    description: "Démarrer le CrossFit à Villefontaine : déroulé d'une première séance, accueil débutants. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Villardins : guide pour vrais débutants",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Villefontaine · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Villefontaine",
    excerpt: "Démarrer le CrossFit à Villefontaine : déroulé d'une première séance, accueil débutants. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-villefontaine",
    title: "CrossFit enfants Villefontaine : programme Kids — Chistera",
    description: "CrossFit pour enfants à Villefontaine : philosophie, sécurité, options. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants à Villefontaine : panorama et options",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Villefontaine · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Villefontaine",
    excerpt: "CrossFit pour enfants à Villefontaine : philosophie, sécurité, options. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-villefontaine",
    title: "CrossFit senior Villefontaine : adapté — Chistera",
    description: "CrossFit pour seniors à Villefontaine : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Villefontaine",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Villefontaine · CrossFit senior", breadcrumbLabel: "CrossFit senior Villefontaine",
    excerpt: "CrossFit pour seniors à Villefontaine : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-villefontaine",
    title: "Sport santé Villefontaine : APA — Chistera",
    description: "Sport santé à Villefontaine : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Villefontaine : pratique adaptée et accompagnement",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Villefontaine · Sport santé", breadcrumbLabel: "Sport santé Villefontaine",
    excerpt: "Sport santé à Villefontaine : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-villefontaine",
    title: "Callisthénie Villefontaine : pratique — Chistera",
    description: "La callisthénie à Villefontaine : aires de fitness urbain, salle. Guide CrossFit Chistera.",
    h1: "La callisthénie à Villefontaine",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Villefontaine · Callisthénie", breadcrumbLabel: "Callisthénie Villefontaine",
    excerpt: "La callisthénie à Villefontaine : aires de fitness urbain, salle. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-villefontaine",
    title: "Hybrid training Villefontaine : combiner — Chistera",
    description: "L'hybrid training à Villefontaine : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Villefontaine : combiner force et endurance",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Villefontaine · Hybrid Training", breadcrumbLabel: "Hybrid Training Villefontaine",
    excerpt: "L'hybrid training à Villefontaine : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-villefontaine",
    title: "Pilates Villefontaine : offre locale — Chistera",
    description: "Le pilates à Villefontaine : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Villefontaine : panorama d'une offre développée",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Villefontaine · Pilates", breadcrumbLabel: "Pilates Villefontaine",
    excerpt: "Le pilates à Villefontaine : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-villefontaine",
    title: "Crosstraining Villefontaine : choisir — Chistera",
    description: "Crosstraining à Villefontaine : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    h1: "Crosstraining à Villefontaine : comprendre les différences",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Villefontaine · Cross-training", breadcrumbLabel: "Cross-training Villefontaine",
    excerpt: "Crosstraining à Villefontaine : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-villefontaine",
    title: "ATHX Villefontaine : conditioning — Chistera",
    description: "L'ATHX à Villefontaine : format accessible, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "L'ATHX à Villefontaine : conditioning accessible",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Villefontaine · Athx", breadcrumbLabel: "Athx Villefontaine",
    excerpt: "L'ATHX à Villefontaine : format accessible, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-villefontaine",
    title: "Hyrox Villefontaine : préparation — Chistera",
    description: "Préparer Hyrox à Villefontaine : format, programme, coaching. Guide CrossFit Chistera.",
    h1: "Hyrox à Villefontaine : préparer la course",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Villefontaine · Hyrox", breadcrumbLabel: "Hyrox Villefontaine",
    excerpt: "Préparer Hyrox à Villefontaine : format, programme, coaching. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-villefontaine",
    title: "Préparation physique Villefontaine — Chistera",
    description: "Préparation physique à Villefontaine : sportifs amateurs, performance, prévention. Guide CrossFit Chistera.",
    h1: "La préparation physique à Villefontaine",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Villefontaine · Préparation physique", breadcrumbLabel: "Préparation physique Villefontaine",
    excerpt: "Préparation physique à Villefontaine : sportifs amateurs, performance, prévention. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-villefontaine",
    title: "Remise en forme Villefontaine — Chistera",
    description: "Remise en forme à Villefontaine : méthode progressive, conseils. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Villardins",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Villefontaine · Remise en forme", breadcrumbLabel: "Remise en forme Villefontaine",
    excerpt: "Remise en forme à Villefontaine : méthode progressive, conseils. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-villefontaine",
    title: "Perte de poids Villefontaine — Chistera",
    description: "Perte de poids durable à Villefontaine : sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à Villefontaine",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Villefontaine · Perte de poids", breadcrumbLabel: "Perte de poids Villefontaine",
    excerpt: "Perte de poids durable à Villefontaine : sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-la-verpilliere",
    title: "CrossFit La Verpillière : box à 18 min — Chistera",
    description: "Pratiquer le CrossFit à La Verpillière : panorama local, box voisine. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour les habitants de La Verpillière : panorama et options",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "La Verpillière · CrossFit", breadcrumbLabel: "CrossFit La Verpillière",
    excerpt: "Pratiquer le CrossFit à La Verpillière : panorama local, box voisine. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-la-verpilliere",
    title: "Salle de sport La Verpillière : options — Chistera",
    description: "Salle de sport à La Verpillière : tissu local, alternatives en proximité. Guide pratique CrossFit Chistera.",
    h1: "Salle de sport à La Verpillière : tissu local et options de proximité",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "La Verpillière · Salle de sport", breadcrumbLabel: "Salle de sport La Verpillière",
    excerpt: "Salle de sport à La Verpillière : tissu local, alternatives en proximité. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-la-verpilliere",
    title: "Salle de fitness La Verpillière — Chistera",
    description: "Salle de fitness à La Verpillière : modèle, profils adaptés. Guide pratique CrossFit Chistera.",
    h1: "Salle de fitness pour les Verpilliérois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "La Verpillière · Salle de fitness", breadcrumbLabel: "Salle de fitness La Verpillière",
    excerpt: "Salle de fitness à La Verpillière : modèle, profils adaptés. Guide pratique CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-la-verpilliere",
    title: "CrossFit débutant La Verpillière — Chistera",
    description: "Démarrer le CrossFit à La Verpillière : déroulé d'une première séance. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Verpilliérois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "La Verpillière · CrossFit débutant", breadcrumbLabel: "CrossFit débutant La Verpillière",
    excerpt: "Démarrer le CrossFit à La Verpillière : déroulé d'une première séance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-la-verpilliere",
    title: "CrossFit enfants La Verpillière — Chistera",
    description: "CrossFit pour enfants à La Verpillière : programme adapté à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite La Verpillière",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "La Verpillière · CrossFit enfants", breadcrumbLabel: "CrossFit enfants La Verpillière",
    excerpt: "CrossFit pour enfants à La Verpillière : programme adapté à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-la-verpilliere",
    title: "CrossFit senior La Verpillière — Chistera",
    description: "CrossFit pour seniors à La Verpillière : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à La Verpillière",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "La Verpillière · CrossFit senior", breadcrumbLabel: "CrossFit senior La Verpillière",
    excerpt: "CrossFit pour seniors à La Verpillière : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-la-verpilliere",
    title: "Sport santé La Verpillière — Chistera",
    description: "Sport santé à La Verpillière : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à La Verpillière",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "La Verpillière · Sport santé", breadcrumbLabel: "Sport santé La Verpillière",
    excerpt: "Sport santé à La Verpillière : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-la-verpilliere",
    title: "Callisthénie La Verpillière — Chistera",
    description: "La callisthénie à La Verpillière : pratique extérieure, salle. Guide CrossFit Chistera.",
    h1: "La callisthénie à La Verpillière",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "La Verpillière · Callisthénie", breadcrumbLabel: "Callisthénie La Verpillière",
    excerpt: "La callisthénie à La Verpillière : pratique extérieure, salle. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-la-verpilliere",
    title: "Hybrid training La Verpillière — Chistera",
    description: "L'hybrid training à La Verpillière : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à La Verpillière",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "La Verpillière · Hybrid Training", breadcrumbLabel: "Hybrid Training La Verpillière",
    excerpt: "L'hybrid training à La Verpillière : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-la-verpilliere",
    title: "Pilates La Verpillière — Chistera",
    description: "Le pilates à La Verpillière : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à La Verpillière",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "La Verpillière · Pilates", breadcrumbLabel: "Pilates La Verpillière",
    excerpt: "Le pilates à La Verpillière : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-la-verpilliere",
    title: "Crosstraining La Verpillière — Chistera",
    description: "Crosstraining à La Verpillière : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    h1: "Crosstraining à La Verpillière",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "La Verpillière · Cross-training", breadcrumbLabel: "Cross-training La Verpillière",
    excerpt: "Crosstraining à La Verpillière : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-la-verpilliere",
    title: "ATHX La Verpillière — Chistera",
    description: "L'ATHX à La Verpillière : format accessible. Guide CrossFit Chistera.",
    h1: "L'ATHX à La Verpillière",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "La Verpillière · Athx", breadcrumbLabel: "Athx La Verpillière",
    excerpt: "L'ATHX à La Verpillière : format accessible. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-la-verpilliere",
    title: "Hyrox La Verpillière — Chistera",
    description: "Préparer Hyrox à La Verpillière : format, programme. Guide CrossFit Chistera.",
    h1: "Hyrox à La Verpillière",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "La Verpillière · Hyrox", breadcrumbLabel: "Hyrox La Verpillière",
    excerpt: "Préparer Hyrox à La Verpillière : format, programme. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-la-verpilliere",
    title: "Préparation physique La Verpillière — Chistera",
    description: "Préparation physique à La Verpillière : sportifs amateurs. Guide CrossFit Chistera.",
    h1: "La préparation physique à La Verpillière",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "La Verpillière · Préparation physique", breadcrumbLabel: "Préparation physique La Verpillière",
    excerpt: "Préparation physique à La Verpillière : sportifs amateurs. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-la-verpilliere",
    title: "Remise en forme La Verpillière — Chistera",
    description: "Remise en forme à La Verpillière : méthode progressive. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Verpilliérois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "La Verpillière · Remise en forme", breadcrumbLabel: "Remise en forme La Verpillière",
    excerpt: "Remise en forme à La Verpillière : méthode progressive. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-la-verpilliere",
    title: "Perte de poids La Verpillière — Chistera",
    description: "Perte de poids durable à La Verpillière : sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à La Verpillière",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "La Verpillière · Perte de poids", breadcrumbLabel: "Perte de poids La Verpillière",
    excerpt: "Perte de poids durable à La Verpillière : sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-diemoz",
    title: "CrossFit Diémoz : box à 10 min — Chistera",
    description: "Pratiquer le CrossFit à Diémoz : box voisine à Saint-Pierre-de-Chandieu. Coaching personnalisé. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour les habitants de Diémoz : box voisine en proximité",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Diémoz · CrossFit", breadcrumbLabel: "CrossFit Diémoz",
    excerpt: "Pratiquer le CrossFit à Diémoz : box voisine à Saint-Pierre-de-Chandieu. Coaching personnalisé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-diemoz",
    title: "Salle de sport Diémoz — Chistera",
    description: "Salle de sport à Diémoz : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    h1: "Salle de sport pour les Diémozans : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Diémoz · Salle de sport", breadcrumbLabel: "Salle de sport Diémoz",
    excerpt: "Salle de sport à Diémoz : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-diemoz",
    title: "Salle de fitness Diémoz — Chistera",
    description: "Salle de fitness pour les Diémozans : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    h1: "Salle de fitness pour les Diémozans : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Diémoz · Salle de fitness", breadcrumbLabel: "Salle de fitness Diémoz",
    excerpt: "Salle de fitness pour les Diémozans : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-diemoz",
    title: "CrossFit débutant Diémoz — Chistera",
    description: "Démarrer le CrossFit à Diémoz : box voisine, accueil débutants. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Diémozans",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Diémoz · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Diémoz",
    excerpt: "Démarrer le CrossFit à Diémoz : box voisine, accueil débutants. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-diemoz",
    title: "CrossFit enfants Diémoz — Chistera",
    description: "CrossFit pour enfants à Diémoz : programme adapté à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Diémoz",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Diémoz · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Diémoz",
    excerpt: "CrossFit pour enfants à Diémoz : programme adapté à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-diemoz",
    title: "CrossFit senior Diémoz — Chistera",
    description: "CrossFit pour seniors à Diémoz : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Diémoz",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Diémoz · CrossFit senior", breadcrumbLabel: "CrossFit senior Diémoz",
    excerpt: "CrossFit pour seniors à Diémoz : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-diemoz",
    title: "Sport santé Diémoz — Chistera",
    description: "Sport santé à Diémoz : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Diémoz",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Diémoz · Sport santé", breadcrumbLabel: "Sport santé Diémoz",
    excerpt: "Sport santé à Diémoz : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-diemoz",
    title: "Callisthénie Diémoz — Chistera",
    description: "La callisthénie à Diémoz : pratique extérieure, salle. Guide CrossFit Chistera.",
    h1: "La callisthénie à Diémoz",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Diémoz · Callisthénie", breadcrumbLabel: "Callisthénie Diémoz",
    excerpt: "La callisthénie à Diémoz : pratique extérieure, salle. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-diemoz",
    title: "Hybrid training Diémoz — Chistera",
    description: "L'hybrid training à Diémoz : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Diémoz",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Diémoz · Hybrid Training", breadcrumbLabel: "Hybrid Training Diémoz",
    excerpt: "L'hybrid training à Diémoz : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-diemoz",
    title: "Pilates Diémoz — Chistera",
    description: "Le pilates à Diémoz : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Diémoz",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Diémoz · Pilates", breadcrumbLabel: "Pilates Diémoz",
    excerpt: "Le pilates à Diémoz : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-diemoz",
    title: "Crosstraining Diémoz — Chistera",
    description: "Crosstraining à Diémoz : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    h1: "Crosstraining à Diémoz",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Diémoz · Cross-training", breadcrumbLabel: "Cross-training Diémoz",
    excerpt: "Crosstraining à Diémoz : différences avec CrossFit, choisir. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-diemoz",
    title: "ATHX Diémoz — Chistera",
    description: "L'ATHX à Diémoz : format accessible. Guide CrossFit Chistera.",
    h1: "L'ATHX à Diémoz",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Diémoz · Athx", breadcrumbLabel: "Athx Diémoz",
    excerpt: "L'ATHX à Diémoz : format accessible. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-diemoz",
    title: "Hyrox Diémoz — Chistera",
    description: "Préparer Hyrox à Diémoz : format, programme. Guide CrossFit Chistera.",
    h1: "Hyrox à Diémoz",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Diémoz · Hyrox", breadcrumbLabel: "Hyrox Diémoz",
    excerpt: "Préparer Hyrox à Diémoz : format, programme. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-diemoz",
    title: "Préparation physique Diémoz — Chistera",
    description: "Préparation physique à Diémoz : sportifs amateurs. Guide CrossFit Chistera.",
    h1: "La préparation physique à Diémoz",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Diémoz · Préparation physique", breadcrumbLabel: "Préparation physique Diémoz",
    excerpt: "Préparation physique à Diémoz : sportifs amateurs. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-diemoz",
    title: "Remise en forme Diémoz — Chistera",
    description: "Remise en forme à Diémoz : méthode progressive. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Diémozans",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Diémoz · Remise en forme", breadcrumbLabel: "Remise en forme Diémoz",
    excerpt: "Remise en forme à Diémoz : méthode progressive. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-diemoz",
    title: "Perte de poids Diémoz — Chistera",
    description: "Perte de poids durable à Diémoz : sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à Diémoz",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Diémoz · Perte de poids", breadcrumbLabel: "Perte de poids Diémoz",
    excerpt: "Perte de poids durable à Diémoz : sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-charantonnay",
    title: "CrossFit Charantonnay : box à 12 min — Chistera",
    description: "Pratiquer le CrossFit à Charantonnay : box voisine à Saint-Pierre-de-Chandieu. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour les habitants de Charantonnay : box voisine en proximité",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Charantonnay · CrossFit", breadcrumbLabel: "CrossFit Charantonnay",
    excerpt: "Pratiquer le CrossFit à Charantonnay : box voisine à Saint-Pierre-de-Chandieu. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-charantonnay",
    title: "Salle de sport Charantonnay — Chistera",
    description: "Salle de sport à Charantonnay : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    h1: "Salle de sport pour les Charantonnais : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Charantonnay · Salle de sport", breadcrumbLabel: "Salle de sport Charantonnay",
    excerpt: "Salle de sport à Charantonnay : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-charantonnay",
    title: "Salle de fitness Charantonnay — Chistera",
    description: "Salle de fitness pour les Charantonnais : alternatives. Guide CrossFit Chistera.",
    h1: "Salle de fitness pour les Charantonnais",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Charantonnay · Salle de fitness", breadcrumbLabel: "Salle de fitness Charantonnay",
    excerpt: "Salle de fitness pour les Charantonnais : alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-charantonnay",
    title: "CrossFit débutant Charantonnay — Chistera",
    description: "Démarrer le CrossFit à Charantonnay : box voisine. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Charantonnais",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Charantonnay · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Charantonnay",
    excerpt: "Démarrer le CrossFit à Charantonnay : box voisine. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-charantonnay",
    title: "CrossFit enfants Charantonnay — Chistera",
    description: "CrossFit pour enfants à Charantonnay : programme à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Charantonnay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Charantonnay · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Charantonnay",
    excerpt: "CrossFit pour enfants à Charantonnay : programme à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-charantonnay",
    title: "CrossFit senior Charantonnay — Chistera",
    description: "CrossFit pour seniors à Charantonnay : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Charantonnay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Charantonnay · CrossFit senior", breadcrumbLabel: "CrossFit senior Charantonnay",
    excerpt: "CrossFit pour seniors à Charantonnay : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-charantonnay",
    title: "Sport santé Charantonnay — Chistera",
    description: "Sport santé à Charantonnay : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Charantonnay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Charantonnay · Sport santé", breadcrumbLabel: "Sport santé Charantonnay",
    excerpt: "Sport santé à Charantonnay : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-charantonnay",
    title: "Callisthénie Charantonnay — Chistera",
    description: "La callisthénie à Charantonnay : pratique extérieure, salle. Guide CrossFit Chistera.",
    h1: "La callisthénie à Charantonnay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Charantonnay · Callisthénie", breadcrumbLabel: "Callisthénie Charantonnay",
    excerpt: "La callisthénie à Charantonnay : pratique extérieure, salle. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-charantonnay",
    title: "Hybrid training Charantonnay — Chistera",
    description: "L'hybrid training à Charantonnay : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Charantonnay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Charantonnay · Hybrid Training", breadcrumbLabel: "Hybrid Training Charantonnay",
    excerpt: "L'hybrid training à Charantonnay : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-charantonnay",
    title: "Pilates Charantonnay — Chistera",
    description: "Le pilates à Charantonnay : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Charantonnay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Charantonnay · Pilates", breadcrumbLabel: "Pilates Charantonnay",
    excerpt: "Le pilates à Charantonnay : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-charantonnay",
    title: "Crosstraining Charantonnay — Chistera",
    description: "Crosstraining à Charantonnay : différences avec CrossFit. Guide CrossFit Chistera.",
    h1: "Crosstraining à Charantonnay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Charantonnay · Cross-training", breadcrumbLabel: "Cross-training Charantonnay",
    excerpt: "Crosstraining à Charantonnay : différences avec CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-charantonnay",
    title: "ATHX Charantonnay — Chistera",
    description: "L'ATHX à Charantonnay : format accessible. Guide CrossFit Chistera.",
    h1: "L'ATHX à Charantonnay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Charantonnay · Athx", breadcrumbLabel: "Athx Charantonnay",
    excerpt: "L'ATHX à Charantonnay : format accessible. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-charantonnay",
    title: "Hyrox Charantonnay — Chistera",
    description: "Préparer Hyrox à Charantonnay : format, programme. Guide CrossFit Chistera.",
    h1: "Hyrox à Charantonnay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Charantonnay · Hyrox", breadcrumbLabel: "Hyrox Charantonnay",
    excerpt: "Préparer Hyrox à Charantonnay : format, programme. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-charantonnay",
    title: "Préparation physique Charantonnay — Chistera",
    description: "Préparation physique à Charantonnay : sportifs amateurs. Guide CrossFit Chistera.",
    h1: "La préparation physique à Charantonnay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Charantonnay · Préparation physique", breadcrumbLabel: "Préparation physique Charantonnay",
    excerpt: "Préparation physique à Charantonnay : sportifs amateurs. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-charantonnay",
    title: "Remise en forme Charantonnay — Chistera",
    description: "Remise en forme à Charantonnay : méthode progressive. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Charantonnais",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Charantonnay · Remise en forme", breadcrumbLabel: "Remise en forme Charantonnay",
    excerpt: "Remise en forme à Charantonnay : méthode progressive. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-charantonnay",
    title: "Perte de poids Charantonnay — Chistera",
    description: "Perte de poids durable à Charantonnay : sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à Charantonnay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Charantonnay · Perte de poids", breadcrumbLabel: "Perte de poids Charantonnay",
    excerpt: "Perte de poids durable à Charantonnay : sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-saint-georges-d-esperanche",
    title: "CrossFit Saint-Georges-d'Espéranche",
    description: "Pratiquer le CrossFit à Saint-Georges-d'Espéranche : box voisine à Saint-Pierre-de-Chandieu. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour les habitants de Saint-Georges-d'Espéranche",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Georges-d'Espéranche · CrossFit", breadcrumbLabel: "CrossFit Saint-Georges-d'Espéranche",
    excerpt: "Pratiquer le CrossFit à Saint-Georges-d'Espéranche : box voisine à Saint-Pierre-de-Chandieu. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-saint-georges-d-esperanche",
    title: "Salle de sport Saint-Georges-d'Espéranche — Chistera",
    description: "Salle de sport à Saint-Georges-d'Espéranche : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    h1: "Salle de sport pour les Saint-Georgeois : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Georges-d'Espéranche · Salle de sport", breadcrumbLabel: "Salle de sport Saint-Georges-d'Espéranche",
    excerpt: "Salle de sport à Saint-Georges-d'Espéranche : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-saint-georges-d-esperanche",
    title: "Salle de fitness Saint-Georges-d'Espéranche — Chistera",
    description: "Salle de fitness pour les Saint-Georgeois : alternatives. Guide CrossFit Chistera.",
    h1: "Salle de fitness pour les Saint-Georgeois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Georges-d'Espéranche · Salle de fitness", breadcrumbLabel: "Salle de fitness Saint-Georges-d'Espéranche",
    excerpt: "Salle de fitness pour les Saint-Georgeois : alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-saint-georges-d-esperanche",
    title: "CrossFit débutant Saint-Georges-d'Espéranche — Chistera",
    description: "Démarrer le CrossFit à Saint-Georges-d'Espéranche : box voisine. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Saint-Georgeois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Georges-d'Espéranche · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Saint-Georges-d'Espéranche",
    excerpt: "Démarrer le CrossFit à Saint-Georges-d'Espéranche : box voisine. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-saint-georges-d-esperanche",
    title: "CrossFit enfants Saint-Georges-d'Espéranche — Chistera",
    description: "CrossFit pour enfants à Saint-Georges-d'Espéranche : programme à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Saint-Georges-d'Espéranche",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Georges-d'Espéranche · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Saint-Georges-d'Espéranche",
    excerpt: "CrossFit pour enfants à Saint-Georges-d'Espéranche : programme à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-saint-georges-d-esperanche",
    title: "CrossFit senior Saint-Georges-d'Espéranche — Chistera",
    description: "CrossFit pour seniors à Saint-Georges-d'Espéranche : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Saint-Georges-d'Espéranche",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Saint-Georges-d'Espéranche · CrossFit senior", breadcrumbLabel: "CrossFit senior Saint-Georges-d'Espéranche",
    excerpt: "CrossFit pour seniors à Saint-Georges-d'Espéranche : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-saint-georges-d-esperanche",
    title: "Sport santé Saint-Georges-d'Espéranche — Chistera",
    description: "Sport santé à Saint-Georges-d'Espéranche : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Saint-Georges-d'Espéranche",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Saint-Georges-d'Espéranche · Sport santé", breadcrumbLabel: "Sport santé Saint-Georges-d'Espéranche",
    excerpt: "Sport santé à Saint-Georges-d'Espéranche : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-saint-georges-d-esperanche",
    title: "Callisthénie Saint-Georges-d'Espéranche — Chistera",
    description: "La callisthénie à Saint-Georges-d'Espéranche : pratique extérieure, salle. Guide CrossFit Chistera.",
    h1: "La callisthénie à Saint-Georges-d'Espéranche",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Georges-d'Espéranche · Callisthénie", breadcrumbLabel: "Callisthénie Saint-Georges-d'Espéranche",
    excerpt: "La callisthénie à Saint-Georges-d'Espéranche : pratique extérieure, salle. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-saint-georges-d-esperanche",
    title: "Hybrid training Saint-Georges-d'Espéranche — Chistera",
    description: "L'hybrid training à Saint-Georges-d'Espéranche : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Saint-Georges-d'Espéranche",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Georges-d'Espéranche · Hybrid Training", breadcrumbLabel: "Hybrid Training Saint-Georges-d'Espéranche",
    excerpt: "L'hybrid training à Saint-Georges-d'Espéranche : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-saint-georges-d-esperanche",
    title: "Pilates Saint-Georges-d'Espéranche — Chistera",
    description: "Le pilates à Saint-Georges-d'Espéranche : principes, bénéfices. Guide CrossFit Chistera.",
    h1: "Le pilates à Saint-Georges-d'Espéranche",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Georges-d'Espéranche · Pilates", breadcrumbLabel: "Pilates Saint-Georges-d'Espéranche",
    excerpt: "Le pilates à Saint-Georges-d'Espéranche : principes, bénéfices. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-saint-georges-d-esperanche",
    title: "Crosstraining Saint-Georges-d'Espéranche — Chistera",
    description: "Crosstraining à Saint-Georges-d'Espéranche : différences avec CrossFit. Guide CrossFit Chistera.",
    h1: "Crosstraining à Saint-Georges-d'Espéranche",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Georges-d'Espéranche · Cross-training", breadcrumbLabel: "Cross-training Saint-Georges-d'Espéranche",
    excerpt: "Crosstraining à Saint-Georges-d'Espéranche : différences avec CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-saint-georges-d-esperanche",
    title: "ATHX Saint-Georges-d'Espéranche — Chistera",
    description: "L'ATHX à Saint-Georges-d'Espéranche : format accessible. Guide CrossFit Chistera.",
    h1: "L'ATHX à Saint-Georges-d'Espéranche",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Georges-d'Espéranche · Athx", breadcrumbLabel: "Athx Saint-Georges-d'Espéranche",
    excerpt: "L'ATHX à Saint-Georges-d'Espéranche : format accessible. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-saint-georges-d-esperanche",
    title: "Hyrox Saint-Georges-d'Espéranche — Chistera",
    description: "Préparer Hyrox à Saint-Georges-d'Espéranche : format, programme. Guide CrossFit Chistera.",
    h1: "Hyrox à Saint-Georges-d'Espéranche",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Georges-d'Espéranche · Hyrox", breadcrumbLabel: "Hyrox Saint-Georges-d'Espéranche",
    excerpt: "Préparer Hyrox à Saint-Georges-d'Espéranche : format, programme. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-saint-georges-d-esperanche",
    title: "Préparation physique Saint-Georges-d'Espéranche — Chistera",
    description: "Préparation physique à Saint-Georges-d'Espéranche : sportifs amateurs. Guide CrossFit Chistera.",
    h1: "La préparation physique à Saint-Georges-d'Espéranche",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Saint-Georges-d'Espéranche · Préparation physique", breadcrumbLabel: "Préparation physique Saint-Georges-d'Espéranche",
    excerpt: "Préparation physique à Saint-Georges-d'Espéranche : sportifs amateurs. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-saint-georges-d-esperanche",
    title: "Remise en forme Saint-Georges-d'Espéranche — Chistera",
    description: "Remise en forme à Saint-Georges-d'Espéranche : méthode progressive. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Saint-Georgeois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Saint-Georges-d'Espéranche · Remise en forme", breadcrumbLabel: "Remise en forme Saint-Georges-d'Espéranche",
    excerpt: "Remise en forme à Saint-Georges-d'Espéranche : méthode progressive. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-saint-georges-d-esperanche",
    title: "Perte de poids Saint-Georges-d'Espéranche — Chistera",
    description: "Perte de poids durable à Saint-Georges-d'Espéranche : sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à Saint-Georges-d'Espéranche",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Saint-Georges-d'Espéranche · Perte de poids", breadcrumbLabel: "Perte de poids Saint-Georges-d'Espéranche",
    excerpt: "Perte de poids durable à Saint-Georges-d'Espéranche : sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-simandres",
    title: "CrossFit Simandres : box à 7 min — Chistera",
    description: "Pratiquer le CrossFit quand on habite Simandres : box voisine à Saint-Pierre-de-Chandieu. Coaching personnalisé. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour les habitants de Simandres : box voisine en proximité",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Simandres · CrossFit", breadcrumbLabel: "CrossFit Simandres",
    excerpt: "Pratiquer le CrossFit quand on habite Simandres : box voisine à Saint-Pierre-de-Chandieu. Coaching personnalisé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-simandres",
    title: "Salle de sport Simandres — Chistera",
    description: "Salle de sport à Simandres : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    h1: "Salle de sport pour les Simandrins : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Simandres · Salle de sport", breadcrumbLabel: "Salle de sport Simandres",
    excerpt: "Salle de sport à Simandres : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-simandres",
    title: "Salle de fitness Simandres — Chistera",
    description: "Salle de fitness pour les Simandrins : alternatives. Guide CrossFit Chistera.",
    h1: "Salle de fitness pour les Simandrins",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Simandres · Salle de fitness", breadcrumbLabel: "Salle de fitness Simandres",
    excerpt: "Salle de fitness pour les Simandrins : alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-simandres",
    title: "CrossFit débutant Simandres — Chistera",
    description: "Démarrer le CrossFit à Simandres : box voisine. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Simandrins",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Simandres · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Simandres",
    excerpt: "Démarrer le CrossFit à Simandres : box voisine. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-simandres",
    title: "CrossFit enfants Simandres — Chistera",
    description: "CrossFit pour enfants à Simandres : programme à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Simandres",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Simandres · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Simandres",
    excerpt: "CrossFit pour enfants à Simandres : programme à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-simandres",
    title: "CrossFit senior Simandres — Chistera",
    description: "CrossFit pour seniors à Simandres : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Simandres",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Simandres · CrossFit senior", breadcrumbLabel: "CrossFit senior Simandres",
    excerpt: "CrossFit pour seniors à Simandres : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-simandres",
    title: "Sport santé Simandres — Chistera",
    description: "Sport santé à Simandres : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Simandres",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Simandres · Sport santé", breadcrumbLabel: "Sport santé Simandres",
    excerpt: "Sport santé à Simandres : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-simandres",
    title: "Callisthénie Simandres — Chistera",
    description: "La callisthénie à Simandres : pratique extérieure, salle. Guide CrossFit Chistera.",
    h1: "La callisthénie à Simandres",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Simandres · Callisthénie", breadcrumbLabel: "Callisthénie Simandres",
    excerpt: "La callisthénie à Simandres : pratique extérieure, salle. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-simandres",
    title: "Hybrid training Simandres — Chistera",
    description: "L'hybrid training à Simandres : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Simandres",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Simandres · Hybrid Training", breadcrumbLabel: "Hybrid Training Simandres",
    excerpt: "L'hybrid training à Simandres : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-simandres",
    title: "Pilates Simandres — Chistera",
    description: "Le pilates à Simandres : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Simandres",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Simandres · Pilates", breadcrumbLabel: "Pilates Simandres",
    excerpt: "Le pilates à Simandres : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-simandres",
    title: "Crosstraining Simandres — Chistera",
    description: "Crosstraining à Simandres : différences avec CrossFit. Guide CrossFit Chistera.",
    h1: "Crosstraining à Simandres",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Simandres · Cross-training", breadcrumbLabel: "Cross-training Simandres",
    excerpt: "Crosstraining à Simandres : différences avec CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-simandres",
    title: "ATHX Simandres — Chistera",
    description: "L'ATHX à Simandres : format accessible. Guide CrossFit Chistera.",
    h1: "L'ATHX à Simandres",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Simandres · Athx", breadcrumbLabel: "Athx Simandres",
    excerpt: "L'ATHX à Simandres : format accessible. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-simandres",
    title: "Hyrox Simandres — Chistera",
    description: "Préparer Hyrox à Simandres : format, programme. Guide CrossFit Chistera.",
    h1: "Hyrox à Simandres",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Simandres · Hyrox", breadcrumbLabel: "Hyrox Simandres",
    excerpt: "Préparer Hyrox à Simandres : format, programme. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-simandres",
    title: "Préparation physique Simandres — Chistera",
    description: "Préparation physique à Simandres : sportifs amateurs. Guide CrossFit Chistera.",
    h1: "La préparation physique à Simandres",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Simandres · Préparation physique", breadcrumbLabel: "Préparation physique Simandres",
    excerpt: "Préparation physique à Simandres : sportifs amateurs. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-simandres",
    title: "Remise en forme Simandres — Chistera",
    description: "Remise en forme à Simandres : méthode progressive. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Simandrins",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Simandres · Remise en forme", breadcrumbLabel: "Remise en forme Simandres",
    excerpt: "Remise en forme à Simandres : méthode progressive. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-simandres",
    title: "Perte de poids Simandres — Chistera",
    description: "Perte de poids durable à Simandres : sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à Simandres",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Simandres · Perte de poids", breadcrumbLabel: "Perte de poids Simandres",
    excerpt: "Perte de poids durable à Simandres : sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-chuzelles",
    title: "Salle de sport Chuzelles — Chistera",
    description: "Salle de sport à Chuzelles : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    h1: "Salle de sport pour les Chuzellois : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chuzelles · Salle de sport", breadcrumbLabel: "Salle de sport Chuzelles",
    excerpt: "Salle de sport à Chuzelles : pas d'offre locale, alternatives en proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-chuzelles",
    title: "Salle de fitness Chuzelles — Chistera",
    description: "Salle de fitness pour les Chuzellois : alternatives. Guide CrossFit Chistera.",
    h1: "Salle de fitness pour les Chuzellois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chuzelles · Salle de fitness", breadcrumbLabel: "Salle de fitness Chuzelles",
    excerpt: "Salle de fitness pour les Chuzellois : alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-chuzelles",
    title: "CrossFit débutant Chuzelles — Chistera",
    description: "Démarrer le CrossFit à Chuzelles : déroulé d'une première séance. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Chuzellois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chuzelles · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Chuzelles",
    excerpt: "Démarrer le CrossFit à Chuzelles : déroulé d'une première séance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-chuzelles",
    title: "CrossFit enfants Chuzelles — Chistera",
    description: "CrossFit pour enfants à Chuzelles : programme à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Chuzelles",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chuzelles · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Chuzelles",
    excerpt: "CrossFit pour enfants à Chuzelles : programme à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-chuzelles",
    title: "CrossFit senior Chuzelles — Chistera",
    description: "CrossFit pour seniors à Chuzelles : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Chuzelles",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Chuzelles · CrossFit senior", breadcrumbLabel: "CrossFit senior Chuzelles",
    excerpt: "CrossFit pour seniors à Chuzelles : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-chuzelles",
    title: "Sport santé Chuzelles — Chistera",
    description: "Sport santé à Chuzelles : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Chuzelles",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Chuzelles · Sport santé", breadcrumbLabel: "Sport santé Chuzelles",
    excerpt: "Sport santé à Chuzelles : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-chuzelles",
    title: "Callisthénie Chuzelles — Chistera",
    description: "La callisthénie à Chuzelles : pratique extérieure, salle. Guide CrossFit Chistera.",
    h1: "La callisthénie à Chuzelles",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chuzelles · Callisthénie", breadcrumbLabel: "Callisthénie Chuzelles",
    excerpt: "La callisthénie à Chuzelles : pratique extérieure, salle. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-chuzelles",
    title: "Hybrid training Chuzelles — Chistera",
    description: "L'hybrid training à Chuzelles : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Chuzelles",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chuzelles · Hybrid Training", breadcrumbLabel: "Hybrid Training Chuzelles",
    excerpt: "L'hybrid training à Chuzelles : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-chuzelles",
    title: "Pilates Chuzelles — Chistera",
    description: "Le pilates à Chuzelles : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Chuzelles",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chuzelles · Pilates", breadcrumbLabel: "Pilates Chuzelles",
    excerpt: "Le pilates à Chuzelles : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-chuzelles",
    title: "Crosstraining Chuzelles — Chistera",
    description: "Crosstraining à Chuzelles : différences avec CrossFit. Guide CrossFit Chistera.",
    h1: "Crosstraining à Chuzelles",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chuzelles · Cross-training", breadcrumbLabel: "Cross-training Chuzelles",
    excerpt: "Crosstraining à Chuzelles : différences avec CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-chuzelles",
    title: "ATHX Chuzelles — Chistera",
    description: "L'ATHX à Chuzelles : format accessible. Guide CrossFit Chistera.",
    h1: "L'ATHX à Chuzelles",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chuzelles · Athx", breadcrumbLabel: "Athx Chuzelles",
    excerpt: "L'ATHX à Chuzelles : format accessible. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-chuzelles",
    title: "Hyrox Chuzelles — Chistera",
    description: "Préparer Hyrox à Chuzelles : format, programme. Guide CrossFit Chistera.",
    h1: "Hyrox à Chuzelles",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chuzelles · Hyrox", breadcrumbLabel: "Hyrox Chuzelles",
    excerpt: "Préparer Hyrox à Chuzelles : format, programme. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-chuzelles",
    title: "Préparation physique Chuzelles — Chistera",
    description: "Préparation physique à Chuzelles : sportifs amateurs. Guide CrossFit Chistera.",
    h1: "La préparation physique à Chuzelles",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Chuzelles · Préparation physique", breadcrumbLabel: "Préparation physique Chuzelles",
    excerpt: "Préparation physique à Chuzelles : sportifs amateurs. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-chuzelles",
    title: "Remise en forme Chuzelles — Chistera",
    description: "Remise en forme à Chuzelles : méthode progressive. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Chuzellois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Chuzelles · Remise en forme", breadcrumbLabel: "Remise en forme Chuzelles",
    excerpt: "Remise en forme à Chuzelles : méthode progressive. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-chuzelles",
    title: "Perte de poids Chuzelles — Chistera",
    description: "Perte de poids durable à Chuzelles : sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à Chuzelles",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Chuzelles · Perte de poids", breadcrumbLabel: "Perte de poids Chuzelles",
    excerpt: "Perte de poids durable à Chuzelles : sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-valencin",
    title: "Salle de sport Valencin — Chistera",
    description: "Salle de sport à Valencin : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    h1: "Salle de sport pour les Valencinois : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Valencin · Salle de sport", breadcrumbLabel: "Salle de sport Valencin",
    excerpt: "Salle de sport à Valencin : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-valencin",
    title: "Salle de fitness Valencin — Chistera",
    description: "Salle de fitness pour les Valencinois : alternatives. Guide CrossFit Chistera.",
    h1: "Salle de fitness pour les Valencinois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Valencin · Salle de fitness", breadcrumbLabel: "Salle de fitness Valencin",
    excerpt: "Salle de fitness pour les Valencinois : alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-valencin",
    title: "CrossFit débutant Valencin — Chistera",
    description: "Démarrer le CrossFit à Valencin : box voisine. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Valencinois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Valencin · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Valencin",
    excerpt: "Démarrer le CrossFit à Valencin : box voisine. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-valencin",
    title: "CrossFit enfants Valencin — Chistera",
    description: "CrossFit pour enfants à Valencin : programme à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Valencin",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Valencin · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Valencin",
    excerpt: "CrossFit pour enfants à Valencin : programme à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-valencin",
    title: "CrossFit senior Valencin — Chistera",
    description: "CrossFit pour seniors à Valencin : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Valencin",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Valencin · CrossFit senior", breadcrumbLabel: "CrossFit senior Valencin",
    excerpt: "CrossFit pour seniors à Valencin : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-valencin",
    title: "Sport santé Valencin — Chistera",
    description: "Sport santé à Valencin : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Valencin",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Valencin · Sport santé", breadcrumbLabel: "Sport santé Valencin",
    excerpt: "Sport santé à Valencin : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-valencin",
    title: "Callisthénie Valencin — Chistera",
    description: "La callisthénie à Valencin : pratique extérieure, salle. Guide CrossFit Chistera.",
    h1: "La callisthénie à Valencin",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Valencin · Callisthénie", breadcrumbLabel: "Callisthénie Valencin",
    excerpt: "La callisthénie à Valencin : pratique extérieure, salle. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-valencin",
    title: "Hybrid training Valencin — Chistera",
    description: "L'hybrid training à Valencin : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Valencin",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Valencin · Hybrid Training", breadcrumbLabel: "Hybrid Training Valencin",
    excerpt: "L'hybrid training à Valencin : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-valencin",
    title: "Pilates Valencin — Chistera",
    description: "Le pilates à Valencin : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Valencin",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Valencin · Pilates", breadcrumbLabel: "Pilates Valencin",
    excerpt: "Le pilates à Valencin : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-valencin",
    title: "Crosstraining Valencin — Chistera",
    description: "Crosstraining à Valencin : différences avec CrossFit. Guide CrossFit Chistera.",
    h1: "Crosstraining à Valencin",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Valencin · Cross-training", breadcrumbLabel: "Cross-training Valencin",
    excerpt: "Crosstraining à Valencin : différences avec CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-valencin",
    title: "ATHX Valencin — Chistera",
    description: "L'ATHX à Valencin : format accessible. Guide CrossFit Chistera.",
    h1: "L'ATHX à Valencin",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Valencin · Athx", breadcrumbLabel: "Athx Valencin",
    excerpt: "L'ATHX à Valencin : format accessible. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-valencin",
    title: "Hyrox Valencin — Chistera",
    description: "Préparer Hyrox à Valencin : format, programme. Guide CrossFit Chistera.",
    h1: "Hyrox à Valencin",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Valencin · Hyrox", breadcrumbLabel: "Hyrox Valencin",
    excerpt: "Préparer Hyrox à Valencin : format, programme. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-valencin",
    title: "Préparation physique Valencin — Chistera",
    description: "Préparation physique à Valencin : sportifs amateurs. Guide CrossFit Chistera.",
    h1: "La préparation physique à Valencin",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Valencin · Préparation physique", breadcrumbLabel: "Préparation physique Valencin",
    excerpt: "Préparation physique à Valencin : sportifs amateurs. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-valencin",
    title: "Remise en forme Valencin — Chistera",
    description: "Remise en forme à Valencin : méthode progressive. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Valencinois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Valencin · Remise en forme", breadcrumbLabel: "Remise en forme Valencin",
    excerpt: "Remise en forme à Valencin : méthode progressive. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-valencin",
    title: "Perte de poids Valencin — Chistera",
    description: "Perte de poids durable à Valencin : sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à Valencin",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Valencin · Perte de poids", breadcrumbLabel: "Perte de poids Valencin",
    excerpt: "Perte de poids durable à Valencin : sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-septeme",
    title: "Salle de sport Septème — Chistera",
    description: "Salle de sport à Septème : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    h1: "Salle de sport pour les Septémois : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Septème · Salle de sport", breadcrumbLabel: "Salle de sport Septème",
    excerpt: "Salle de sport à Septème : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-septeme",
    title: "Salle de fitness Septème — Chistera",
    description: "Salle de fitness pour les Septémois : alternatives. Guide CrossFit Chistera.",
    h1: "Salle de fitness pour les Septémois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Septème · Salle de fitness", breadcrumbLabel: "Salle de fitness Septème",
    excerpt: "Salle de fitness pour les Septémois : alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-septeme",
    title: "CrossFit débutant Septème — Chistera",
    description: "Démarrer le CrossFit à Septème : box voisine. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Septémois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Septème · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Septème",
    excerpt: "Démarrer le CrossFit à Septème : box voisine. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-septeme",
    title: "CrossFit enfants Septème — Chistera",
    description: "CrossFit pour enfants à Septème : programme à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Septème",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Septème · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Septème",
    excerpt: "CrossFit pour enfants à Septème : programme à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-septeme",
    title: "CrossFit senior Septème — Chistera",
    description: "CrossFit pour seniors à Septème : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Septème",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Septème · CrossFit senior", breadcrumbLabel: "CrossFit senior Septème",
    excerpt: "CrossFit pour seniors à Septème : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-septeme",
    title: "Sport santé Septème — Chistera",
    description: "Sport santé à Septème : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Septème",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Septème · Sport santé", breadcrumbLabel: "Sport santé Septème",
    excerpt: "Sport santé à Septème : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-septeme",
    title: "Callisthénie Septème — Chistera",
    description: "La callisthénie à Septème : pratique extérieure, salle. Guide CrossFit Chistera.",
    h1: "La callisthénie à Septème",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Septème · Callisthénie", breadcrumbLabel: "Callisthénie Septème",
    excerpt: "La callisthénie à Septème : pratique extérieure, salle. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-septeme",
    title: "Hybrid training Septème — Chistera",
    description: "L'hybrid training à Septème : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Septème",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Septème · Hybrid Training", breadcrumbLabel: "Hybrid Training Septème",
    excerpt: "L'hybrid training à Septème : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-septeme",
    title: "Pilates Septème — Chistera",
    description: "Le pilates à Septème : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Septème",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Septème · Pilates", breadcrumbLabel: "Pilates Septème",
    excerpt: "Le pilates à Septème : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-septeme",
    title: "Crosstraining Septème — Chistera",
    description: "Crosstraining à Septème : différences avec CrossFit. Guide CrossFit Chistera.",
    h1: "Crosstraining à Septème",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Septème · Cross-training", breadcrumbLabel: "Cross-training Septème",
    excerpt: "Crosstraining à Septème : différences avec CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-septeme",
    title: "ATHX Septème — Chistera",
    description: "L'ATHX à Septème : format accessible. Guide CrossFit Chistera.",
    h1: "L'ATHX à Septème",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Septème · Athx", breadcrumbLabel: "Athx Septème",
    excerpt: "L'ATHX à Septème : format accessible. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-septeme",
    title: "Hyrox Septème — Chistera",
    description: "Préparer Hyrox à Septème : format, programme. Guide CrossFit Chistera.",
    h1: "Hyrox à Septème",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Septème · Hyrox", breadcrumbLabel: "Hyrox Septème",
    excerpt: "Préparer Hyrox à Septème : format, programme. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-septeme",
    title: "Préparation physique Septème — Chistera",
    description: "Préparation physique à Septème : sportifs amateurs. Guide CrossFit Chistera.",
    h1: "La préparation physique à Septème",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Septème · Préparation physique", breadcrumbLabel: "Préparation physique Septème",
    excerpt: "Préparation physique à Septème : sportifs amateurs. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-septeme",
    title: "Remise en forme Septème — Chistera",
    description: "Remise en forme à Septème : méthode progressive. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Septémois",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Septème · Remise en forme", breadcrumbLabel: "Remise en forme Septème",
    excerpt: "Remise en forme à Septème : méthode progressive. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-septeme",
    title: "Perte de poids Septème — Chistera",
    description: "Perte de poids durable à Septème : sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à Septème",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Septème · Perte de poids", breadcrumbLabel: "Perte de poids Septème",
    excerpt: "Perte de poids durable à Septème : sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-sport-grenay",
    title: "Salle de sport Grenay — Chistera",
    description: "Salle de sport à Grenay : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    h1: "Salle de sport pour les Grenaysards : alternatives concrètes",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Grenay · Salle de sport", breadcrumbLabel: "Salle de sport Grenay",
    excerpt: "Salle de sport à Grenay : pas d'offre locale, alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "salle-de-fitness-grenay",
    title: "Salle de fitness Grenay — Chistera",
    description: "Salle de fitness pour les Grenaysards : alternatives. Guide CrossFit Chistera.",
    h1: "Salle de fitness pour les Grenaysards",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Grenay · Salle de fitness", breadcrumbLabel: "Salle de fitness Grenay",
    excerpt: "Salle de fitness pour les Grenaysards : alternatives. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-debutant-grenay",
    title: "CrossFit débutant Grenay — Chistera",
    description: "Démarrer le CrossFit à Grenay : box voisine. Guide CrossFit Chistera.",
    h1: "Démarrer le CrossFit pour les Grenaysards",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Grenay · CrossFit débutant", breadcrumbLabel: "CrossFit débutant Grenay",
    excerpt: "Démarrer le CrossFit à Grenay : box voisine. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-enfants-grenay",
    title: "CrossFit enfants Grenay — Chistera",
    description: "CrossFit pour enfants à Grenay : programme à proximité. Guide CrossFit Chistera.",
    h1: "Le CrossFit pour enfants quand on habite Grenay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Grenay · CrossFit enfants", breadcrumbLabel: "CrossFit enfants Grenay",
    excerpt: "CrossFit pour enfants à Grenay : programme à proximité. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crossfit-senior-grenay",
    title: "CrossFit senior Grenay — Chistera",
    description: "CrossFit pour seniors à Grenay : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    h1: "Le CrossFit après 60 ans à Grenay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Grenay · CrossFit senior", breadcrumbLabel: "CrossFit senior Grenay",
    excerpt: "CrossFit pour seniors à Grenay : adaptations, bénéfices santé. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "sport-sante-grenay",
    title: "Sport santé Grenay — Chistera",
    description: "Sport santé à Grenay : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    h1: "Le sport santé à Grenay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Grenay · Sport santé", breadcrumbLabel: "Sport santé Grenay",
    excerpt: "Sport santé à Grenay : APA, prescription, accompagnement. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "callisthenie-grenay",
    title: "Callisthénie Grenay — Chistera",
    description: "La callisthénie à Grenay : pratique extérieure, salle. Guide CrossFit Chistera.",
    h1: "La callisthénie à Grenay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Grenay · Callisthénie", breadcrumbLabel: "Callisthénie Grenay",
    excerpt: "La callisthénie à Grenay : pratique extérieure, salle. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hybrid-training-grenay",
    title: "Hybrid training Grenay — Chistera",
    description: "L'hybrid training à Grenay : force et endurance. Guide CrossFit Chistera.",
    h1: "L'hybrid training à Grenay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Grenay · Hybrid Training", breadcrumbLabel: "Hybrid Training Grenay",
    excerpt: "L'hybrid training à Grenay : force et endurance. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "pilates-grenay",
    title: "Pilates Grenay — Chistera",
    description: "Le pilates à Grenay : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    h1: "Le pilates à Grenay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Grenay · Pilates", breadcrumbLabel: "Pilates Grenay",
    excerpt: "Le pilates à Grenay : principes, bénéfices, complémentarité CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "crosstraining-grenay",
    title: "Crosstraining Grenay — Chistera",
    description: "Crosstraining à Grenay : différences avec CrossFit. Guide CrossFit Chistera.",
    h1: "Crosstraining à Grenay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Grenay · Cross-training", breadcrumbLabel: "Cross-training Grenay",
    excerpt: "Crosstraining à Grenay : différences avec CrossFit. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "athx-grenay",
    title: "ATHX Grenay — Chistera",
    description: "L'ATHX à Grenay : format accessible. Guide CrossFit Chistera.",
    h1: "L'ATHX à Grenay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Grenay · Athx", breadcrumbLabel: "Athx Grenay",
    excerpt: "L'ATHX à Grenay : format accessible. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "hyrox-grenay",
    title: "Hyrox Grenay — Chistera",
    description: "Préparer Hyrox à Grenay : format, programme. Guide CrossFit Chistera.",
    h1: "Hyrox à Grenay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Grenay · Hyrox", breadcrumbLabel: "Hyrox Grenay",
    excerpt: "Préparer Hyrox à Grenay : format, programme. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "preparation-physique-grenay",
    title: "Préparation physique Grenay — Chistera",
    description: "Préparation physique à Grenay : sportifs amateurs. Guide CrossFit Chistera.",
    h1: "La préparation physique à Grenay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Grenay · Préparation physique", breadcrumbLabel: "Préparation physique Grenay",
    excerpt: "Préparation physique à Grenay : sportifs amateurs. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "remise-en-forme-grenay",
    title: "Remise en forme Grenay — Chistera",
    description: "Remise en forme à Grenay : méthode progressive. Guide CrossFit Chistera.",
    h1: "Remise en forme pour les Grenaysards",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Grenay · Remise en forme", breadcrumbLabel: "Remise en forme Grenay",
    excerpt: "Remise en forme à Grenay : méthode progressive. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "perte-de-poids-grenay",
    title: "Perte de poids Grenay — Chistera",
    description: "Perte de poids durable à Grenay : sport, nutrition. Guide CrossFit Chistera.",
    h1: "Perte de poids à Grenay",
    publishedAt: "2026-05-14", readingMinutes: 8,
    cluster: "lieu", ctaVariant: "default", needsHealthDisclaimer: true,
    category: "Grenay · Perte de poids", breadcrumbLabel: "Perte de poids Grenay",
    excerpt: "Perte de poids durable à Grenay : sport, nutrition. Guide CrossFit Chistera.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
  {
    slug: "3-ans-crossfit-chistera",
    title: "3 ans CrossFit Chistera : journée WOD, Hyrox & BBQ Est lyonnais",
    description: "Reportage des 3 ans de CrossFit Chistera à Saint-Pierre-de-Chandieu : 80 adhérents, WODs Hyrox, recherche de max haltérophilie, BBQ et communauté.",
    h1: "Les 3 ans de CrossFit Chistera : une journée de sport, de partage et d'esprit de communauté dans l'Est lyonnais",
    publishedAt: "2026-05-15", readingMinutes: 13,
    cluster: "profils", ctaVariant: "default", needsHealthDisclaimer: false,
    category: "Communauté · Événement",
    breadcrumbLabel: "3 ans CrossFit Chistera",
    excerpt: "Reportage de la journée anniversaire du 25 avril 2026 : 80 adhérents, WODs Hyrox, snatchs, BBQ et esprit de famille dans l'Est lyonnais.",
    author: { name: "Loïc Galvier", role: "Co-fondateur CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, gérant" },
  },
  {
    slug: "hyrox-lyon-2026-crossfit-chistera-est-lyonnais",
    title: "HYROX Lyon 2026 : les athlètes CrossFit Chistera à Eurexpo | Box Est lyonnais",
    description: "Récit du HYROX Lyon 2026 à Eurexpo : les athlètes de CrossFit Chistera (Saint-Pierre-de-Chandieu, Est lyonnais) brillent en solo et en duo. Préparation hybrid training, cross-training et course à pied.",
    h1: "HYROX Lyon 2026 : les athlètes de CrossFit Chistera ont mis le feu à Eurexpo",
    publishedAt: "2026-05-23", readingMinutes: 11,
    cluster: "competition", ctaVariant: "competition", needsHealthDisclaimer: false,
    category: "Compétition · HYROX",
    breadcrumbLabel: "HYROX Lyon 2026",
    excerpt: "Récit du HYROX Lyon 2026 à Eurexpo : duos femmes en 1h18, premier solo, duo mixte coach Benbou. Préparation hybrid training à la box.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" },
    images: [
      { path: "/blog/hyrox-lyon-2026/hyrox-lyon-2026-finish-time-1h18-crossfit-chistera.jpg",
        alt: "Écran d'arrivée affichant le finish time 1:18:34 pour le duo femmes CrossFit Chistera au HYROX Lyon 2026 à Eurexpo",
        title: "Finish time 1:18:34 — CrossFit Chistera, HYROX Lyon 2026",
        caption: "FINISH TIME 1:18:34 — le duo femmes CrossFit Chistera explose son objectif au HYROX Lyon 2026 (Eurexpo, Chassieu).",
        width: 900, height: 1200 },
      { path: "/blog/hyrox-lyon-2026/hyrox-lyon-2026-sled-pull-duo-femmes-crossfit-chistera.jpg",
        alt: "F. et C., athlètes CrossFit Chistera, sur la station de sled pull lors du HYROX Lyon 2026 à Eurexpo Chassieu",
        title: "Sled pull — duo femmes CrossFit Chistera, HYROX Lyon 2026",
        caption: "Station sled pull : nos compétitrices F. et C. en plein effort au HYROX Lyon 2026 (Eurexpo, Chassieu).",
        width: 799, height: 1200 },
      { path: "/blog/hyrox-lyon-2026/hyrox-lyon-2026-course-duo-femmes-crossfit-chistera.jpg",
        alt: "Les deux athlètes femmes de CrossFit Chistera lancées sur le run final du HYROX Lyon 2026, arène Eurexpo",
        title: "Run final — duo femmes CrossFit Chistera, HYROX Lyon 2026",
        caption: "Le run final, fières et heureuses, après les 8 stations du HYROX Lyon 2026.",
        width: 1200, height: 799 },
      { path: "/blog/hyrox-lyon-2026/hyrox-lyon-2026-solo-open-judoka-crossfit-chistera.jpg",
        alt: "M., adhérent CrossFit Chistera et ancien judoka, après son premier HYROX en solo à Lyon 2026",
        title: "Premier HYROX solo — M., CrossFit Chistera",
        caption: "M., judoka de formation, valide son premier HYROX en solo au format Open.",
        width: 1200, height: 1600 },
      { path: "/blog/hyrox-lyon-2026/hyrox-lyon-2026-duo-mixte-coach-benbou-crossfit-chistera.jpg",
        alt: "L'adhérente J. et le coach Benbou de CrossFit Chistera en duo mixte au HYROX Lyon 2026, devant le mur HYROX",
        title: "Duo mixte J. et coach Benbou — CrossFit Chistera, HYROX Lyon 2026",
        caption: "Duo mixte : l'adhérente J. et le coach Anthony « Benbou » Bendahmane.",
        width: 1440, height: 1571 },
    ],
  },
  {
    slug: "murph-crossfit-est-lyonnais",
    title: "Murph CrossFit dans l'Est lyonnais | Histoire, préparation et événement CrossFit Chistera",
    description: "Découvrez l'histoire du Murph, le Hero WOD le plus célèbre du CrossFit. Retour sur le Murph 2026 organisé par CrossFit Chistera à Saint-Pierre-de-Chandieu avec plus de 30 athlètes.",
    h1: "Murph, Hero WOD, dépassement de soi et esprit de communauté : retour sur le Murph 2026 organisé par CrossFit Chistera",
    publishedAt: "2026-05-31", readingMinutes: 12,
    cluster: "competition", ctaVariant: "competition", needsHealthDisclaimer: false,
    category: "Communauté · Hero WOD",
    breadcrumbLabel: "Murph 2026",
    excerpt: "Retour sur le Murph 2026 chez CrossFit Chistera (Saint-Pierre-de-Chandieu) : histoire du Hero WOD, hommage au lieutenant Michael Murphy, plus de 30 athlètes engagés dans l'Est lyonnais.",
    author: { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" },
    reviewer: { name: "Cédric Bastien", role: "BPJEPS AGFF, CrossFit Level 1, coach gérant" },
  },
];


export const EXTRA_BY_SLUG: Record<string, ExtraArticle> = Object.fromEntries(
  EXTRA_ARTICLES.map((a) => [a.slug, a])
);
